import React, { useState, startTransition, useEffect, useRef, useMemo } from 'react';
import { Calendar, CalendarDays, DollarSign, Home, MapPin, Settings, Users, ChevronRight, Plus, Download, Clock, CheckCircle, AlertCircle, Edit, Trash2, X, Menu, ChevronLeft, BarChart3, Star, TrendingUp, TrendingDown, MoreVertical, Save, Copy, Link, Palette, RotateCcw } from 'lucide-react';
import { toast } from 'sonner@2.0.3';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CourseProvider, useCourse } from './contexts/CourseContext';
import { CourseSelector } from './components/CourseSelector';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './components/ui/alert-dialog';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Switch } from './components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './components/ui/dropdown-menu';
import { Separator } from './components/ui/separator';
import { Toaster } from './components/ui/sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { AddressSearch } from './components/AddressSearch';
import { InteractiveMap } from './components/InteractiveMap';
import { DiscgoLogo } from './components/DiscgoLogo';
import basketIcon from 'figma:asset/f4b6087bb5a2b853ba4fdd33278c203eb98d743e.png';
import { api } from "./lib/api";
import { supabase } from "./lib/supabase";
import { themes } from "./lib/themes";
import { useTheme } from "./contexts/ThemeContext";
import { ThemeModal } from "./components/ThemeModal";

const getPhotoUrl = (path: string) =>
  supabase.storage.from('course-photos').getPublicUrl(path).data.publicUrl;

const COURSE_ID = import.meta.env.VITE_COURSE_ID;
const OWNER_ID = import.meta.env.VITE_OWNER_ID;
const DISCGO_SITE_URL = import.meta.env.VITE_DISCGO_SITE_URL || 'https://discgo-site.vercel.app';



// Custom styles for the cream and green brand colors
const brandStyles = {
 primary: 'bg-[#2B8B3B] text-white hover:bg-[#236B2F]', // Green from logo
 secondary: 'bg-[#f5f5dc] text-[#2B8B3B] hover:bg-[#f0ebe1] border-[#2B8B3B]', // New cream background
 accent: 'bg-[#f5f5dc]', // New cream accent
 text: 'text-[#2B8B3B]' // Green text
};

const pieChartColors = ['#2B8B3B', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107'];

// Empty data structures - to be populated from backend
const performanceData: any[] = [];

const reviewData = {
 totalReviews: 0,
 averageRating: 0,
 ratingTrend: {
 direction: 'stable' as const,
 percentage: 0,
 previousRating: 0
 },
 ratingDistribution: [
 { rating: 5, count: 0, percentage: 0 },
 { rating: 4, count: 0, percentage: 0 },
 { rating: 3, count: 0, percentage: 0 },
 { rating: 2, count: 0, percentage: 0 },
 { rating: 1, count: 0, percentage: 0 },
 ]
};

const getDynamicRatingData = (period: 'today' | 'week' | 'month') => {
 return {
 averageRating: 0,
 totalReviews: 0,
 ratingTrend: { direction: 'stable' as const, percentage: 0 },
 timePeriodLabel: period === 'today' ? 'today' : period === 'week' ? 'this week' : 'this month'
 };
};

const structuredFeedback = {
 areasForImprovement: [] as { text: string; count: number; percentage: number }[],
 whatWentWell: [] as { text: string; count: number; percentage: number }[]
};

const samplePayouts: any[] = [];

// Tee Time Configuration Interface
interface TeeTimeConfig {
 id: string;
 name: string;
 maxPlayers: number;
 teeTimeInterval: number; // Time between tee times in minutes
 bookingType: 'auto' | 'request';
 startTime: string;
 endTime: string;
 daysOfWeek: string[];
 pricingType: 'per-player' | 'per-round';
 price: number;
 status?: 'active' | 'suspended';
}

const OWNER_SECTION_TO_PATH: Record<string, string> = {
 dashboard: '/dashboard',
 course: '/profile',
 availability: '/setup',
 bookings: '/bookings',
 insights: '/insights',
 payments: '/payments',
};
const OWNER_PATH_TO_SECTION: Record<string, string> = Object.fromEntries(
 Object.entries(OWNER_SECTION_TO_PATH).map(([k, v]) => [v, k])
);
function ownerSectionFromPath(pathname: string): string {
 const seg = '/' + pathname.replace(/^\//, '').split('/')[0];
 return OWNER_PATH_TO_SECTION[seg] || 'dashboard';
}

export default function App() {
 const [activeSection, setActiveSection] = useState(() => ownerSectionFromPath(window.location.pathname));
 const [selectedDate, setSelectedDate] = useState(new Date());
 const [hoveredSlot, setHoveredSlot] = useState<string | null>(null);
 const [editBookingId, setEditBookingId] = useState<number | null>(null);
 
 // Selected course ID from CourseContext (updated by AppContent)
 const [activeCourseId, setActiveCourseId] = useState<string | null>(COURSE_ID || null);
 
 // Tee Time Configuration State
 const [teeTimeConfigs, setTeeTimeConfigs] = useState<TeeTimeConfig[]>([]);
 const [editingConfig, setEditingConfig] = useState<TeeTimeConfig | null>(null);
 const [showConfigDialog, setShowConfigDialog] = useState(false);
 const [configErrors, setConfigErrors] = useState<string[]>([]);
 const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
 const [configToDelete, setConfigToDelete] = useState<TeeTimeConfig | null>(null);
 const [blockedDates, setBlockedDates] = useState<string[]>([]);
 const [bookings, setBookings] = useState([]);
 const [currentWeekStart, setCurrentWeekStart] = useState(() => {
 const today = new Date();
 const dayOfWeek = today.getDay();
 const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust for Sunday = 0
 const monday = new Date(today);
 monday.setDate(today.getDate() + mondayOffset);
 return monday;
 });
 // Course state
 const [course, setCourse] = useState(null);
 const [ownerProfile, setOwnerProfile] = useState(null);
 const [loading, setLoading] = useState(false);
 const [loadErr, setLoadErr] = useState("");
 const [selectedHoles, setSelectedHoles] = useState("18");
 const [refundPolicies, setRefundPolicies] = useState([]);
 const [insightsData, setInsightsData] = useState<{
  kpis: {
   currentMonthBookings: number;
   prevMonthBookings: number;
   currentMonthRevenue: number;
   prevMonthRevenue: number;
   totalPlayers: number;
   prevMonthPlayers: number;
  };
  chartData: { month: string; bookings: number; revenue: number; players: number }[];
  reviewData: {
   totalReviews: number;
   averageRating: number;
   ratingTrend: { direction: 'up' | 'down' | 'stable'; percentage: number; previousRating: number };
   ratingDistribution: { rating: number; count: number; percentage: number }[];
  };
  structuredFeedback: {
   whatWentWell: { text: string; count: number; percentage: number }[];
   areasForImprovement: { text: string; count: number; percentage: number }[];
  };
 } | null>(null);
 const [insightsLoading, setInsightsLoading] = useState(false);

 // Course Settings fields
 const [contactEmail, setContactEmail] = useState('');
 const [contactPhone, setContactPhone] = useState('');
 const [courseWebsite, setCourseWebsite] = useState('');
 const [notifEmailBookings, setNotifEmailBookings] = useState(true);
 const [notifEmailCancellations, setNotifEmailCancellations] = useState(true);
 const savedContactEmail = useRef('');
 const savedContactPhone = useRef('');
 const savedCourseWebsite = useRef('');

 // Basic info edit mode
 const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
 const [courseName, setCourseName] = useState('');
 const [courseDescription, setCourseDescription] = useState('');
 
 // Location edit mode
 const [isEditingLocation, setIsEditingLocation] = useState(false);
 
 // Booking link copy state
 const [bookingLinkCopied, setBookingLinkCopied] = useState(false);

 // Earnings & Payouts state
 const [earningsData, setEarningsData] = useState<{
   pendingEarnings: number;
   transferredThisMonth: number;
   lastTransferDate: string | null;
   history: { id: string; slotStart: string; playerName: string; ownerAmount: number; transferStatus: string | null; transferredAt: string | null; bookingStatus: string; refundedAt: string | null }[];
   paidOutThisMonth: number;
   lastPayoutDate: string | null;
   payoutHistory: { id: string; stripePayoutId: string; amount: number; status: string; failureMessage: string | null; arrivalDate: string | null }[];
   payoutFailed: boolean;
 } | null>(null);
 const [earningsLoading, setEarningsLoading] = useState(false);
 const [distroSummary, setDistroSummary] = useState<{
   total_fund_balance: number;
   participating_course_count: number;
   anticipated_dividend_per_course: number;
 } | null>(null);
 const [earningsPage, setEarningsPage] = useState(0);
 const EARNINGS_PAGE_SIZE = 20;
 const [connectStatus, setConnectStatus] = useState<'loading' | 'not_started' | 'incomplete' | 'complete'>('loading');
 const [connectLoading, setConnectLoading] = useState(false);

 // Payment preferences edit mode
 const [isEditingPaymentPrefs, setIsEditingPaymentPrefs] = useState(false);
 const [paymentPrefs, setPaymentPrefs] = useState(() => ({
 paymentMethod: 'bank',
 accountHolder: 'Pine Valley Disc Golf LLC',
 bankName: 'First National Bank',
 routingNumber: '123456789',
 accountNumber: '•••••••4321',
 accountType: 'checking',
 paypalEmail: '',
 businessName: ''
 }));
 const [originalPaymentPrefs, setOriginalPaymentPrefs] = useState(() => ({
 paymentMethod: 'bank',
 accountHolder: 'Pine Valley Disc Golf LLC',
 bankName: 'First National Bank',
 routingNumber: '123456789',
 accountNumber: '•••••••4321',
 accountType: 'checking',
 paypalEmail: '',
 businessName: ''
 }));
 
 // Amenities state
 const [amenities, setAmenities] = useState({
 restrooms: false,
 proShop: false,
 foodDrinks: false,
 equipmentRental: false,
 practiceArea: false,
 waterStations: false,
 picnicArea: false,
 dogFriendly: false,
 garbageCans: false
 });
 
 // Location state
 const [courseAddress, setCourseAddress] = useState('');
 const [courseLatitude, setCourseLatitude] = useState<number | null>(null);
 const [courseLongitude, setCourseLongitude] = useState<number | null>(null);
 const [courseTimezone, setCourseTimezone] = useState('America/New_York');
 
 // Photo upload state - stores paths for saving to DB
 const [photoPaths, setPhotoPaths] = useState<{
 mainPath: string | null;
 galleryPaths: string[];
 mapPath: string | null;
 }>({
 mainPath: null,
 galleryPaths: [],
 mapPath: null
 });
 
 // Original values for Course Profile unsaved changes tracking
 const [originalCourseData, setOriginalCourseData] = useState<{
 name: string;
 description: string;
 address: string;
 latitude: number | null;
 longitude: number | null;
 timezone: string;
 holes: string;
 amenities: typeof amenities;
 photoPaths: typeof photoPaths;
 } | null>(null);
 
 // Check if Course Profile has unsaved changes
 const hasCourseProfileChanges = useMemo(() => {
 if (!originalCourseData || !course) return false;
 
 return (
 course.name !== originalCourseData.name ||
 course.description !== originalCourseData.description ||
 courseAddress !== originalCourseData.address ||
 courseLatitude !== originalCourseData.latitude ||
 courseLongitude !== originalCourseData.longitude ||
 courseTimezone !== originalCourseData.timezone ||
 selectedHoles !== originalCourseData.holes ||
 JSON.stringify(amenities) !== JSON.stringify(originalCourseData.amenities) ||
 JSON.stringify(photoPaths) !== JSON.stringify(originalCourseData.photoPaths)
 );
 }, [course, courseAddress, courseLatitude, courseLongitude, courseTimezone, selectedHoles, amenities, photoPaths, originalCourseData]);
 
 const handleNavigation = (targetSection: string) => {
  const path = OWNER_SECTION_TO_PATH[targetSection] || '/dashboard';
  window.history.pushState({ section: targetSection }, '', path);
  setActiveSection(targetSection);
 };

 useEffect(() => {
  const onPopState = () => setActiveSection(ownerSectionFromPath(window.location.pathname));
  window.addEventListener('popstate', onPopState);
  return () => window.removeEventListener('popstate', onPopState);
 }, []);
 
 // Upload photo to backend
 const uploadPhoto = async (file: File, photoType: 'main' | 'gallery' | 'map') => {
 if (!activeCourseId) throw new Error('No course selected');

 const supabaseAuthKey = Object.keys(localStorage).find(key =>
   key.startsWith('sb-') && key.endsWith('-auth-token')
 );
 let accessToken: string | null = null;
 if (supabaseAuthKey) {
   const sessionData = localStorage.getItem(supabaseAuthKey);
   if (sessionData) {
     const parsed = JSON.parse(sessionData);
     accessToken = parsed?.access_token || null;
   }
 }

 const formData = new FormData();
 formData.append('photo', file);
 formData.append('photoType', photoType);

 const headers: Record<string, string> = {};
 if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

 const response = await fetch(`/api/courses/${activeCourseId}/photos`, {
   method: 'POST',
   headers,
   body: formData
 });

 if (!response.ok) {
   throw new Error('Upload failed');
 }

 const data = await response.json();
 return data.path;
 };
 
 // Delete photo from backend
 const deletePhoto = async (photoPath: string) => {
 if (!activeCourseId) throw new Error('No course selected');

 const supabaseAuthKey = Object.keys(localStorage).find(key =>
   key.startsWith('sb-') && key.endsWith('-auth-token')
 );
 let accessToken: string | null = null;
 if (supabaseAuthKey) {
   const sessionData = localStorage.getItem(supabaseAuthKey);
   if (sessionData) {
     const parsed = JSON.parse(sessionData);
     accessToken = parsed?.access_token || null;
   }
 }

 const headers: Record<string, string> = { 'Content-Type': 'application/json' };
 if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

 const response = await fetch(`/api/courses/${activeCourseId}/photos`, {
   method: 'DELETE',
   headers,
   body: JSON.stringify({ photoPath })
 });

 if (!response.ok) {
   throw new Error('Delete failed');
 }
 };
 
 // Refs for file inputs
 const mainPhotoInputRef = useRef<HTMLInputElement>(null);
 const galleryPhotosInputRef = useRef<HTMLInputElement>(null);
 const mapPhotoInputRef = useRef<HTMLInputElement>(null);
 
 // New state for configuration management and ad-hoc blocking
 // NOTE: Configs are mutually exclusive - no overlapping days/times allowed
 const [suspendedConfigs, setSuspendedConfigs] = useState<string[]>([]);
 const [blockedTimeSlots, setBlockedTimeSlots] = useState<Record<string, string[]>>({}); // date to array of time slots
 const [blockedDays, setBlockedDays] = useState<string[]>([]);
 const [blockedWeeks, setBlockedWeeks] = useState<string[]>([]); // array of week start dates (YYYY-MM-DD format)
 
 // Pending changes for ad-hoc blocking (before save/publish)
 const [pendingBlockedTimeSlots, setPendingBlockedTimeSlots] = useState<Record<string, string[]>>({});
 const [pendingBlockedDays, setPendingBlockedDays] = useState<string[]>([]);
 const [pendingBlockedWeeks, setPendingBlockedWeeks] = useState<string[]>([]); // array of week start dates (YYYY-MM-DD format)
 const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
 const [blockBookingConflicts, setBlockBookingConflicts] = useState<{ date: string; time: string; playerName: string }[]>([]);
 const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
 const [previewWeekStart, setPreviewWeekStart] = useState(() => {
 const today = new Date();
 const dayOfWeek = today.getDay();
 const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
 const monday = new Date(today);
 monday.setDate(today.getDate() + mondayOffset);
 return monday;
 });
 
 // Sidebar collapse state
 const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
 
 // Dashboard metrics toggle state
 const [metricsView, setMetricsView] = useState<'today' | 'week' | 'month'>('today');
 
 // Bookings tab state
 const [bookingsActiveTab, setBookingsActiveTab] = useState<'table' | 'calendar'>('table');
 
 // Bookings search and filter state
 const [bookingsSearchTerm, setBookingsSearchTerm] = useState('');
 const [bookingsDateFilter, setBookingsDateFilter] = useState('all');
 const [bookingsDateRangeStart, setBookingsDateRangeStart] = useState('');
 const [bookingsDateRangeEnd, setBookingsDateRangeEnd] = useState('');
 const [bookingsStatusFilter, setBookingsStatusFilter] = useState('all'); // Single status filter
 const [bookingsSortKey, setBookingsSortKey] = useState<'slotStart' | 'createdAt' | 'playerName' | 'amount' | 'status'>('slotStart');
 const [bookingsSortDir, setBookingsSortDir] = useState<'asc' | 'desc'>('desc');
 
 // Edit booking dialog state
 const [editDialogOpen, setEditDialogOpen] = useState(false);
 const [selectedBookingForEdit, setSelectedBookingForEdit] = useState<typeof bookings[0] | null>(null);

 // Manual booking modal state
 const [manualBookingOpen, setManualBookingOpen] = useState(false);
 const [manualBookingSubmitting, setManualBookingSubmitting] = useState(false);
 const [manualBookingError, setManualBookingError] = useState('');
 const [mbFirstName, setMbFirstName] = useState('');
 const [mbLastName, setMbLastName] = useState('');
 const [mbEmail, setMbEmail] = useState('');
 const [mbPhone, setMbPhone] = useState('');
 const [mbDate, setMbDate] = useState('');
 const [mbTime, setMbTime] = useState('');
 const [mbCustomTime, setMbCustomTime] = useState('');
 const [mbConfigId, setMbConfigId] = useState('');
 const [mbQty, setMbQty] = useState('1');

 // Payment method state

 // Booking fee preference state
 const [bookingFeePayer, setBookingFeePayer] = useState('player');

 const navigationItems = [
 { id: 'dashboard', name: 'Dashboard', icon: Home },
 { id: 'course', name: 'Course Profile', icon: MapPin },
 { id: 'availability', name: 'Tee Time Setup', icon: CalendarDays },
 { id: 'bookings', name: 'Bookings', icon: Users },
 { id: 'insights', name: 'Insights', icon: BarChart3 },
 { id: 'payments', name: 'Payments & Settings', icon: Settings },
 ];

 const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

 // Convert numeric day (0=Sunday, 1=Monday, etc.) to day name
 const numericDayToName = (numDay: number): string => {
 const dayMap = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
 return dayMap[numDay] || '';
 };

 // Get initials: prefer DB profile names (from AuthContext or course API), fallback to auth metadata
 const getOwnerInitials = (authUser?: any, authFirstName?: string, authLastName?: string): string => {
 const firstName = ownerProfile?.first_name || authFirstName || authUser?.user_metadata?.first_name || '';
 const lastName = ownerProfile?.last_name || authLastName || authUser?.user_metadata?.last_name || '';
 const first = firstName.charAt(0).toUpperCase();
 const last = lastName.charAt(0).toUpperCase();
 return first && last ? `${first}${last}` : first || '?';
 };

 // Convert day name to numeric day (for backend)
 const dayNameToNumeric = (dayName: string): number => {
 const dayMap: { [key: string]: number } = {
 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6
 };
 return dayMap[dayName] ?? -1;
 };

 // Helper functions for tee time configurations
 const generateTimeSlots = (startTime: string, endTime: string, teeTimeInterval: number): string[] => {
 const slots: string[] = [];
 // Handle both "08:00" and "08:00:00" formats from backend
 const normalizeTime = (time: string) => {
 // If time already has seconds (HH:MM:SS), use as is
 // If not (HH:MM), add seconds
 return time.includes(':') && time.split(':').length === 3 ? time : `${time}:00`;
 };
 
 const start = new Date(`2000-01-01T${normalizeTime(startTime)}`);
 const end = new Date(`2000-01-01T${normalizeTime(endTime)}`);
 const intervalMs = teeTimeInterval * 60 * 1000; // Convert minutes to milliseconds
 
 let current = start;
 while (current < end) {
 const timeStr = current.toLocaleTimeString('en-US', { 
 hour: 'numeric', 
 minute: '2-digit',
 hour12: true 
 });
 slots.push(timeStr);
 current = new Date(current.getTime() + intervalMs);
 }
 
 return slots;
 };

 const checkConfigConflict = (newConfig: TeeTimeConfig, existingConfigs: TeeTimeConfig[]): string[] => {
 const errors: string[] = [];
 const newStart = new Date(`2000-01-01T${newConfig.startTime}:00`);
 const newEnd = new Date(`2000-01-01T${newConfig.endTime}:00`);
 
 if (newStart >= newEnd) {
 errors.push('End time must be after start time');
 }
 
 existingConfigs.forEach(config => {
 if (config.id === newConfig.id) return; // Skip self when editing
 
 const existingStart = new Date(`2000-01-01T${config.startTime}:00`);
 const existingEnd = new Date(`2000-01-01T${config.endTime}:00`);
 
 // Check for overlapping days
 const overlappingDays = newConfig.daysOfWeek.filter(day => config.daysOfWeek.includes(day));
 
 if (overlappingDays.length > 0) {
 // Check for overlapping times
 const timeOverlaps = (newStart < existingEnd && newEnd > existingStart);
 
 if (timeOverlaps) {
 errors.push(`Conflicts with "${config.name}" on ${overlappingDays.join(', ')}`);
 }
 }
 });
 
 return errors;
 };

 const addOrUpdateTeeTimeConfig = async (config: TeeTimeConfig) => {
 const errors = checkConfigConflict(config, teeTimeConfigs);
 if (errors.length > 0) {
 setConfigErrors(errors);
 return false;
 }

 // map UI -> backend shape
 const body = {
 name: config.name,
 start_time: config.startTime,
 end_time: config.endTime,
 interval_minutes: config.teeTimeInterval,
 max_players_per_slot: config.maxPlayers,
 pricing_type: config.pricingType, // "per-player" | "per-round"
 price_cents: Math.round((config.price || 0) * 100),
 booking_type: config.bookingType, // "auto" | "request"
 days_of_week: config.daysOfWeek, // ["Mon","Tue",...]
 };

 if (!activeCourseId) {
 toast.error('No course selected');
 return false;
 }

 try {
 let saved;
 if (config.id) {
 // update
 saved = await api.put(`/api/courses/${activeCourseId}/configs/${config.id}`, body);
 } else {
 // create
 saved = await api.post(`/api/courses/${activeCourseId}/configs`, body);
 }

 // server should return the row; map back to UI
 const toUI = (c) => ({
 id: c.id,
 name: c.name,
 maxPlayers: c.max_players_per_slot,
 teeTimeInterval: c.interval_minutes,
 bookingType: (c.booking_type === "request" ? "request" : "auto"),
 startTime: c.start_time,
 endTime: c.end_time,
 daysOfWeek: (c.days_of_week || []).map((numDay: number) => numericDayToName(numDay)),
 pricingType: (c.pricing_type === "per-round" ? "per-round" : "per-player"),
 price: (c.price_cents ?? 0) / 100,
 });

 if (config.id) {
 setTeeTimeConfigs(prev => prev.map(c => c.id === config.id ? toUI(saved) : c));
 toast.success("Tee time configuration updated successfully!");
 } else {
 setTeeTimeConfigs(prev => [...prev, toUI(saved)]);
 toast.success("Tee time configuration created successfully!");
 }

 setConfigErrors([]);
 setEditingConfig(null);
 setShowConfigDialog(false);
 return true;
 } catch (e) {
 setConfigErrors([e.message || "Save failed"]);
 toast.error(`Failed to save configuration: ${e.message || "Unknown error"}`);
 return false;
 }
 };


 // Open delete confirmation dialog
 const deleteTeeTimeConfig = (config: TeeTimeConfig) => {
 setConfigToDelete(config);
 setDeleteConfirmOpen(true);
 };
 
 // Actually delete the config (soft delete with deleted_at)
 const confirmDeleteConfig = async () => {
 if (!configToDelete) return;
 if (!activeCourseId) {
 toast.error('No course selected');
 return;
 }
 
 try {
 // Call backend to soft delete (set deleted_at timestamp)
 await api.del(`/api/courses/${activeCourseId}/configs/${configToDelete.id}`);
 
 // Remove from local state
 setTeeTimeConfigs(prev => prev.filter(c => c.id !== configToDelete.id));
 
 toast.success(`Configuration "${configToDelete.name}" deleted`);
 setDeleteConfirmOpen(false);
 setConfigToDelete(null);
 } catch (error) {
 toast.error(`Failed to delete configuration: ${error.message}`);
 }
 };

 // Handle booking requests
 const handleBookingRequest = async (bookingId: string, action: 'accept' | 'reject') => {
 const newStatus = action === 'accept' ? 'confirmed' : 'cancelled';
 try {
 await api.put(`/api/courses/${activeCourseId}/bookings`, { bookingId, status: newStatus });
 setBookings(prev => prev.map(booking =>
 booking.id === bookingId
 ? { ...booking, status: newStatus }
 : booking
 ));
 if (action === 'accept') {
 toast.success('Booking accepted — payment captured');
 } else {
 toast.success('Booking rejected — hold released');
 }
 } catch (err: any) {
 console.error('Failed to update booking status:', err);
 toast.error(err?.message || `Failed to ${action} booking`);
 }
 };



 // Cancel a booking
 const cancelBooking = (bookingId: number) => {
 setBookings(prev => prev.map(booking => 
 booking.id === bookingId 
 ? { ...booking, status: 'cancelled' }
 : booking
 ));
 };

 // Filter bookings based on search and filter criteria
 const getFilteredBookings = () => {
 return bookings.filter(booking => {
 // Search filter (name and email)
 const searchMatch = !bookingsSearchTerm || 
 booking.playerName.toLowerCase().includes(bookingsSearchTerm.toLowerCase()) ||
 booking.email.toLowerCase().includes(bookingsSearchTerm.toLowerCase());

 // Date filter
 let dateMatch = true;
 if (bookingsDateFilter && bookingsDateFilter !== 'all') {
 const bookingDate = new Date(booking.slotStart);
 const today = new Date();
 const tomorrow = new Date(today);
 tomorrow.setDate(today.getDate() + 1);

 if (bookingsDateFilter === 'range') {
 const startDate = bookingsDateRangeStart ? new Date(bookingsDateRangeStart) : null;
 const endDate = bookingsDateRangeEnd ? new Date(bookingsDateRangeEnd) : null;
 if (startDate && endDate) {
 dateMatch = bookingDate >= startDate && bookingDate <= endDate;
 } else if (startDate) {
 dateMatch = bookingDate >= startDate;
 } else if (endDate) {
 dateMatch = bookingDate <= endDate;
 }
 } else if (bookingsDateFilter === 'today') {
 dateMatch = bookingDate.toDateString() === today.toDateString();
 } else if (bookingsDateFilter === 'tomorrow') {
 dateMatch = bookingDate.toDateString() === tomorrow.toDateString();
 } else if (bookingsDateFilter === 'this-week') {
 const startOfWeek = new Date(today);
 startOfWeek.setDate(today.getDate() - today.getDay());
 const endOfWeek = new Date(startOfWeek);
 endOfWeek.setDate(startOfWeek.getDate() + 6);
 dateMatch = bookingDate >= startOfWeek && bookingDate <= endOfWeek;
 } else if (bookingsDateFilter === 'next-week') {
 const startOfNextWeek = new Date(today);
 startOfNextWeek.setDate(today.getDate() - today.getDay() + 7);
 const endOfNextWeek = new Date(startOfNextWeek);
 endOfNextWeek.setDate(startOfNextWeek.getDate() + 6);
 dateMatch = bookingDate >= startOfNextWeek && bookingDate <= endOfNextWeek;
 } else {
 dateMatch = bookingDate.toDateString() === new Date(bookingsDateFilter).toDateString();
 }
 }

 // Status filter
 const statusMatch = !bookingsStatusFilter || bookingsStatusFilter === 'all' || booking.status === bookingsStatusFilter;

 return searchMatch && dateMatch && statusMatch;
 }).sort((a, b) => {
 const dir = bookingsSortDir === 'asc' ? 1 : -1;
 if (bookingsSortKey === 'slotStart') return dir * (new Date(a.slotStart).getTime() - new Date(b.slotStart).getTime());
 if (bookingsSortKey === 'createdAt') return dir * (new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
 if (bookingsSortKey === 'playerName') return dir * (a.playerName || '').localeCompare(b.playerName || '');
 if (bookingsSortKey === 'amount') return dir * ((parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0));
 if (bookingsSortKey === 'status') return dir * (a.status || '').localeCompare(b.status || '');
 return 0;
 });
 };

 const handleBookingsSort = (key: typeof bookingsSortKey) => {
 if (bookingsSortKey === key) {
 setBookingsSortDir(d => d === 'asc' ? 'desc' : 'asc');
 } else {
 setBookingsSortKey(key);
 setBookingsSortDir('desc');
 }
 };

 // Block/unblock specific dates
 const toggleDateBlock = (date: string) => {
 setBlockedDates(prev => 
 prev.includes(date) 
 ? prev.filter(d => d !== date)
 : [...prev, date]
 );
 };

 // Calculate total tee times for a configuration
 const calculateTeeTimesCount = (config: TeeTimeConfig): number => {
 const startTime = new Date(`2000-01-01T${config.startTime}:00`);
 const endTime = new Date(`2000-01-01T${config.endTime}:00`);
 const intervalMs = config.teeTimeInterval * 60 * 1000;
 const totalTimeMs = endTime.getTime() - startTime.getTime();
 return Math.floor(totalTimeMs / intervalMs);
 };

 // Week navigation functions
 const navigateWeek = (direction: 'prev' | 'next') => {
 setCurrentWeekStart(prev => {
 const newDate = new Date(prev);
 newDate.setDate(prev.getDate() + (direction === 'prev' ? -7 : 7));
 return newDate;
 });
 };

 // Preview week navigation functions
 const navigatePreviewWeek = (direction: 'prev' | 'next') => {
 setPreviewWeekStart(prev => {
 const newDate = new Date(prev);
 newDate.setDate(prev.getDate() + (direction === 'prev' ? -7 : 7));
 return newDate;
 });
 };

 // Get dates for the preview week
 const getPreviewWeekDates = () => {
 const dates = [];
 for (let i = 0; i < 7; i++) {
 const date = new Date(previewWeekStart);
 date.setDate(previewWeekStart.getDate() + i);
 dates.push(date);
 }
 return dates;
 };

 // Suspend/activate configuration
 const toggleConfigSuspension = async (configId: string) => {
 const config = teeTimeConfigs.find(c => c.id === configId);
 if (!config || !activeCourseId) return;

 const currentStatus = config.status || 'active';
 const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';

 // Optimistically update UI immediately
 setTeeTimeConfigs(prev =>
 prev.map(c => c.id === configId ? { ...c, status: newStatus } : c)
 );

 try {
 await api.patch(`/api/courses/${activeCourseId}/configs/${configId}`, { status: newStatus });
 toast.success(`Configuration ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
 } catch (error) {
 // Revert on failure
 setTeeTimeConfigs(prev =>
 prev.map(c => c.id === configId ? { ...c, status: currentStatus } : c)
 );
 console.error('Error toggling config status:', error);
 toast.error('Failed to update configuration status');
 }
 };

 // Block/unblock individual time slot (pending changes)
 const toggleTimeSlotBlock = (date: string, timeSlot: string) => {
 setPendingBlockedTimeSlots(prev => {
 const dateSlots = prev[date] || [];
 const isBlocked = dateSlots.includes(timeSlot);
 
 if (isBlocked) {
 // Unblock the time slot
 const newSlots = dateSlots.filter(slot => slot !== timeSlot);
 if (newSlots.length === 0) {
 const { [date]: removed, ...rest } = prev;
 return rest;
 }
 return { ...prev, [date]: newSlots };
 } else {
 // Block the time slot
 return { ...prev, [date]: [...dateSlots, timeSlot] };
 }
 });
 setHasUnsavedChanges(true);
 };

 // Block/unblock entire day (pending changes)
 const toggleDayBlock = (date: string) => {
 setPendingBlockedDays(prev => 
 prev.includes(date) 
 ? prev.filter(d => d !== date)
 : [...prev, date]
 );
 
 // Also remove any individual time slot blocks for this date
 setPendingBlockedTimeSlots(prev => {
 const { [date]: removed, ...rest } = prev;
 return rest;
 });
 setHasUnsavedChanges(true);
 };

 // Block/unblock entire week (all configs) - consistent with day blocking behavior
 const toggleWeekBlock = (date: string) => {
 // Parse date string (YYYY-MM-DD) to create a proper Date at midnight UTC
 const [year, month, day] = date.split('-').map(Number);
 const parsedDate = new Date(Date.UTC(year, month - 1, day));
 const weekStart = getWeekStart(parsedDate);
 const weekKey = formatUTCDateKey(weekStart);
 
 setPendingBlockedWeeks(prev => 
 prev.includes(weekKey) 
 ? prev.filter(w => w !== weekKey)
 : [...prev, weekKey]
 );
 
 // Also remove any day blocks for dates in this week
 const weekDates = [];
 for (let i = 0; i < 7; i++) {
 const dayDate = new Date(weekStart);
 dayDate.setUTCDate(weekStart.getUTCDate() + i);
 weekDates.push(formatUTCDateKey(dayDate));
 }
 setPendingBlockedDays(prev => prev.filter(d => !weekDates.includes(d)));
 
 setHasUnsavedChanges(true);
 };

 // Get week start date (Monday) - ensure we work with dates at midnight to avoid timezone issues
 const getWeekStart = (date: Date) => {
 // IMPORTANT: Use UTC methods throughout to avoid timezone conversion issues
 // Create a new date at midnight UTC to avoid timezone offset issues
 const normalized = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
 const dayOfWeek = normalized.getUTCDay();
 const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
 const monday = new Date(normalized);
 monday.setUTCDate(normalized.getUTCDate() + mondayOffset);
 return monday;
 };

 // Format Date to YYYY-MM-DD string without timezone conversion (for local time dates)
 const formatDateKey = (date: Date): string => {
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 return `${year}-${month}-${day}`;
 };

 // Format Date to YYYY-MM-DD string using UTC components (for UTC-normalized dates)
 const formatUTCDateKey = (date: Date): string => {
 const year = date.getUTCFullYear();
 const month = String(date.getUTCMonth() + 1).padStart(2, '0');
 const day = String(date.getUTCDate()).padStart(2, '0');
 return `${year}-${month}-${day}`;
 };

 // Parse date key (YYYY-MM-DD) to Date object
 const dateFromKey = (dateKey: string): Date => {
 const [year, month, day] = dateKey.split('-').map(Number);
 return new Date(Date.UTC(year, month - 1, day));
 };

 // Helper function: Convert local time in course timezone to UTC
 const localTimeToUTC = (year: number, month: number, day: number, hour: number, minute: number, timezone: string): Date => {
 // Start with a UTC guess
 let utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
 
 // Create a formatter for the target timezone
 const formatter = new Intl.DateTimeFormat('en-US', {
 timeZone: timezone,
 year: 'numeric',
 month: '2-digit',
 day: '2-digit',
 hour: '2-digit',
 minute: '2-digit',
 hour12: false
 });
 
 // Format our guess to see what it looks like in the target timezone
 const parts = formatter.formatToParts(new Date(utcGuess));
 const guessYear = parseInt(parts.find(p => p.type === 'year')?.value || '0');
 const guessMonth = parseInt(parts.find(p => p.type === 'month')?.value || '0');
 const guessDay = parseInt(parts.find(p => p.type === 'day')?.value || '0');
 const guessHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
 const guessMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
 
 // Calculate the millisecond difference between target and guess using Date.UTC
 const targetUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
 const guessAsUtc = Date.UTC(guessYear, guessMonth - 1, guessDay, guessHour, guessMinute, 0);
 const diffMs = targetUtc - guessAsUtc;
 
 // Adjust our UTC guess by the difference
 const correctedUtc = utcGuess + diffMs;
 
 return new Date(correctedUtc);
 };

 // Transform frontend state to database blocks format
 const transformToDatabaseBlocks = () => {
 const blocks: any[] = [];

 // Transform slot blocks
 Object.entries(pendingBlockedTimeSlots).forEach(([dateKey, timeSlots]) => {
 const date = dateFromKey(dateKey);
 const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
 
 timeSlots.forEach((timeSlot: string) => {
 const applicableConfig = teeTimeConfigs.find(config => 
 config.daysOfWeek.includes(dayName) &&
 generateTimeSlots(config.startTime, config.endTime, config.teeTimeInterval).includes(timeSlot) &&
 config.status !== 'suspended'
 );
 
 if (!applicableConfig) return;
 
 const [year, month, day] = dateKey.split('-').map(Number);
 const [hour, minutePart] = timeSlot.split(':');
 const isPM = timeSlot.includes('PM');
 const is12Hour = timeSlot.includes('12:');
 let hour24 = parseInt(hour);
 
 if (isPM && !is12Hour) hour24 += 12;
 if (!isPM && is12Hour) hour24 = 0;
 
 const minute = parseInt(minutePart);
 
 // Convert from course timezone to UTC
 const slotStart = localTimeToUTC(year, month, day, hour24, minute, courseTimezone);
 const slotEnd = new Date(slotStart.getTime() + (applicableConfig.teeTimeInterval * 60 * 1000));

 blocks.push({
 tee_time_config_id: applicableConfig.id,
 scope: 'slot',
 slot_start: slotStart.toISOString(),
 slot_end: slotEnd.toISOString()
 });
 });
 });

 // Transform day blocks
 pendingBlockedDays.forEach(dateKey => {
 const date = dateFromKey(dateKey);
 const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
 
 teeTimeConfigs.forEach(config => {
 if (config.status !== 'suspended' && config.daysOfWeek.includes(dayName)) {
 blocks.push({
 tee_time_config_id: config.id,
 scope: 'day',
 day: dateKey
 });
 }
 });
 });

 // Transform week blocks (blocks entire week for all configs)
 pendingBlockedWeeks.forEach(weekKey => {
 // Create a block for each tee time configuration (like day blocks)
 teeTimeConfigs.forEach(config => {
 if (config.status !== 'suspended') {
 blocks.push({
 tee_time_config_id: config.id,
 scope: 'week',
 week_monday: weekKey
 });
 }
 });
 });

 return blocks;
 };

 // Load blocks from database and transform to frontend state
 const loadAdHocBlocks = async () => {
 if (!activeCourseId) return;
 
 try {
 const data = await api.get(`/api/courses/${activeCourseId}/tee-time-blocks`);
 const blocks = data.blocks || [];

 const newBlockedTimeSlots: Record<string, string[]> = {};
 const newBlockedDays: string[] = [];
 const newBlockedWeeks: string[] = [];

 blocks.forEach((block: any) => {
 if (block.scope === 'slot') {
 // Convert UTC time to course timezone for display
 const utcDate = new Date(block.slot_start);
 
 // Use Intl.DateTimeFormat to extract date/time components in the course timezone
 const formatter = new Intl.DateTimeFormat('en-US', {
 timeZone: courseTimezone,
 year: 'numeric',
 month: '2-digit',
 day: '2-digit',
 hour: 'numeric',
 minute: '2-digit',
 hour12: true
 });
 
 const parts = formatter.formatToParts(utcDate);
 const year = parts.find(p => p.type === 'year')?.value;
 const month = parts.find(p => p.type === 'month')?.value;
 const day = parts.find(p => p.type === 'day')?.value;
 const hour = parts.find(p => p.type === 'hour')?.value;
 const minute = parts.find(p => p.type === 'minute')?.value;
 const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value;
 
 const dateKey = `${year}-${month}-${day}`;
 const timeSlot = `${hour}:${minute} ${dayPeriod}`;

 if (!newBlockedTimeSlots[dateKey]) {
 newBlockedTimeSlots[dateKey] = [];
 }
 if (!newBlockedTimeSlots[dateKey].includes(timeSlot)) {
 newBlockedTimeSlots[dateKey].push(timeSlot);
 }
 } else if (block.scope === 'day') {
 // Day blocks: only add unique dateKeys (blocks entire day for all configs)
 if (!newBlockedDays.includes(block.day)) {
 newBlockedDays.push(block.day);
 }
 } else if (block.scope === 'week') {
 // Week blocks: only add unique week keys (blocks entire week for all configs)
 if (!newBlockedWeeks.includes(block.week_monday)) {
 newBlockedWeeks.push(block.week_monday);
 }
 }
 });

 setBlockedTimeSlots(newBlockedTimeSlots);
 setBlockedDays(newBlockedDays);
 setBlockedWeeks(newBlockedWeeks);
 setPendingBlockedTimeSlots(newBlockedTimeSlots);
 setPendingBlockedDays(newBlockedDays);
 setPendingBlockedWeeks(newBlockedWeeks);
 } catch (error) {
 console.error('Failed to load ad-hoc blocks:', error);
 }
 };

 // Collect all bookings that fall within pending blocked ranges
 const getBlockedBookingConflicts = (): { date: string; time: string; playerName: string }[] => {
 const conflicts: { date: string; time: string; playerName: string }[] = [];
 const seen = new Set<string>();

 const addConflict = (dateKey: string, time: string) => {
 const key = `${dateKey}|${time}`;
 if (seen.has(key)) return;
 const booking = bookings.find(b => {
 if (!b.slotStart) return false;
 const [y, m, d] = dateKey.split('-').map(Number);
 const targetDate = new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
 const slotDate = new Date(b.slotStart);
 const bookingDate = slotDate.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
 const bookingTime = slotDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
 return bookingDate === targetDate && bookingTime === time && ['confirmed', 'requested', 'pending'].includes(b.status);
 });
 if (booking) {
 seen.add(key);
 conflicts.push({ date: dateKey, time, playerName: booking.playerName });
 }
 };

 // Slot-level blocks
 Object.entries(pendingBlockedTimeSlots).forEach(([dateKey, times]) => {
 times.forEach(time => addConflict(dateKey, time));
 });

 // Day-level blocks — check every booking against each blocked day
 pendingBlockedDays.forEach(dateKey => {
 bookings.forEach(b => {
 if (!b.slotStart) return;
 const [y, m, d] = dateKey.split('-').map(Number);
 const targetDate = new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
 const slotDate = new Date(b.slotStart);
 const bookingDate = slotDate.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
 if (bookingDate === targetDate && ['confirmed', 'requested', 'pending'].includes(b.status)) {
 const time = slotDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
 addConflict(dateKey, time);
 }
 });
 });

 // Week-level blocks — check every booking against each blocked week
 pendingBlockedWeeks.forEach(weekKey => {
 const [wy, wm, wd] = weekKey.split('-').map(Number);
 const weekStartDate = new Date(Date.UTC(wy, wm - 1, wd));
 const weekEndDate = new Date(weekStartDate.getTime() + 7 * 24 * 60 * 60 * 1000);
 bookings.forEach(b => {
 if (!b.slotStart) return;
 const slotDate = new Date(b.slotStart);
 if (slotDate >= weekStartDate && slotDate < weekEndDate && ['confirmed', 'requested', 'pending'].includes(b.status)) {
 const [sy, sm, sd] = [slotDate.getUTCFullYear(), slotDate.getUTCMonth() + 1, slotDate.getUTCDate()];
 const dateKey = `${sy}-${String(sm).padStart(2, '0')}-${String(sd).padStart(2, '0')}`;
 const time = slotDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
 addConflict(dateKey, time);
 }
 });
 });

 return conflicts;
 };

 // Save/publish pending ad-hoc blocks to database
 const saveAdHocBlocks = () => {
 if (!activeCourseId) {
 toast.error('No course selected');
 return;
 }
 const conflicts = getBlockedBookingConflicts();
 if (conflicts.length > 0) {
 setBlockBookingConflicts(conflicts);
 setBlockConfirmOpen(true);
 return;
 }
 confirmSaveAdHocBlocks();
 };

 const confirmSaveAdHocBlocks = async () => {
 if (!activeCourseId) return;
 setBlockConfirmOpen(false);
 try {
 const blocks = transformToDatabaseBlocks();
 await api.del(`/api/courses/${activeCourseId}/tee-time-blocks`);
 if (blocks.length > 0) {
 await api.post(`/api/courses/${activeCourseId}/tee-time-blocks`, { blocks });
 }
 setBlockedTimeSlots(pendingBlockedTimeSlots);
 setBlockedDays(pendingBlockedDays);
 setBlockedWeeks(pendingBlockedWeeks);
 setHasUnsavedChanges(false);
 toast.success('Availability blocks saved successfully!');
 } catch (error) {
 console.error('Failed to save ad-hoc blocks:', error);
 toast.error('Failed to save blocks. Please try again.');
 }
 };

 // Discard pending changes
 const discardAdHocChanges = () => {
 setPendingBlockedTimeSlots(blockedTimeSlots);
 setPendingBlockedDays(blockedDays);
 setPendingBlockedWeeks(blockedWeeks);
 setHasUnsavedChanges(false);
 };

 // Initialize pending state with current state when component mounts or when published state changes
 const initializePendingState = () => {
 if (!hasUnsavedChanges) {
 setPendingBlockedTimeSlots(blockedTimeSlots);
 setPendingBlockedDays(blockedDays);
 setPendingBlockedWeeks(blockedWeeks);
 }
 };

 // Initialize pending state on mount
 useEffect(() => {
 initializePendingState();
 }, [blockedTimeSlots, blockedDays, blockedWeeks]);

 useEffect(() => {
  if (activeSection === 'insights' && activeCourseId) {
   fetchInsights();
  }
 }, [activeSection, activeCourseId]);

 useEffect(() => {
  if (activeSection === 'payments' && activeCourseId) {
   fetchEarnings(activeCourseId);
   fetchConnectStatus(activeCourseId);
  }
 }, [activeSection, activeCourseId]);

 // Handle Stripe Connect return redirects (?stripe_return=connected|refresh)
 useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const stripeReturn = params.get('stripe_return');
  const courseParam = params.get('course');
  if (!stripeReturn) return;

  // Clean the params from the URL
  window.history.replaceState({}, '', window.location.pathname);

  // Navigate to payments section
  handleNavigation('payments');

  if (stripeReturn === 'refresh' && courseParam) {
   // Account link expired — auto-restart onboarding
   setConnectLoading(true);
   api.post(`/api/courses/${courseParam}/stripe/connect`)
    .then((data: any) => { if (data.url) window.location.href = data.url; })
    .catch((err: any) => toast.error(err.message || 'Failed to resume payout setup.'))
    .finally(() => setConnectLoading(false));
  }
  // For stripe_return=connected, the payments useEffect will fetch status when
  // the section activates, so no extra action needed here.
 }, []);

 // Check if a time slot should be blocked (individual, day, or week level) - for live/published state
 const isTimeSlotBlocked = (date: string, timeSlot: string) => {
 // Check individual time slot blocks
 if ((blockedTimeSlots[date] || []).includes(timeSlot)) return true;
 
 // Check day blocks
 if (blockedDays.includes(date)) return true;
 
 // Check week blocks (applies to entire week, all configs)
 const [year, month, dayNum] = date.split('-').map(Number);
 const parsedDate = new Date(Date.UTC(year, month - 1, dayNum));
 const weekStart = getWeekStart(parsedDate);
 const weekKey = formatUTCDateKey(weekStart);
 if (blockedWeeks.includes(weekKey)) return true;
 
 return false;
 };

 // Check if a time slot should be blocked (pending changes for preview)
 const isPendingTimeSlotBlocked = (date: string, timeSlot: string) => {
 // Check individual time slot blocks
 if ((pendingBlockedTimeSlots[date] || []).includes(timeSlot)) return true;
 
 // Check day blocks
 if (pendingBlockedDays.includes(date)) return true;
 
 // Check week blocks (applies to entire week, all configs)
 const [year, month, dayNum] = date.split('-').map(Number);
 const parsedDate = new Date(Date.UTC(year, month - 1, dayNum));
 const weekStart = getWeekStart(parsedDate);
 const weekKey = formatUTCDateKey(weekStart);
 if (pendingBlockedWeeks.includes(weekKey)) return true;
 
 return false;
 };

 // Check if there's a booking for a specific date and time slot
 const hasBookingAtSlot = (date: string, timeSlot: string): boolean => {
 // Parse YYYY-MM-DD as local midnight to avoid off-by-one in timezones west of UTC
 const [y, m, d] = date.split('-').map(Number);
 const targetDate = new Date(y, m - 1, d).toLocaleDateString('en-US', {
 year: 'numeric',
 month: 'numeric',
 day: 'numeric'
 });

 // timeSlot is already "8:00 AM" format from generateTimeSlots — use directly
 const formattedTime = timeSlot;

 return bookings.some(booking => {
 if (!booking.slotStart) return false;
 const slotDate = new Date(booking.slotStart);
 const bookingDate = slotDate.toLocaleDateString('en-US', {
 year: 'numeric',
 month: 'numeric',
 day: 'numeric'
 });
 const bookingTime = slotDate.toLocaleTimeString('en-US', {
 hour: 'numeric',
 minute: '2-digit',
 hour12: true
 });
 return bookingDate === targetDate &&
 bookingTime === formattedTime &&
 ['confirmed', 'requested', 'pending'].includes(booking.status);
 });
 };

 // Handle click on time slot - first click blocks slot, second click blocks day (all configs), third click blocks week (all configs)
 const handleTimeSlotClick = (date: string, timeSlot: string) => {
 const dateKey = date;

 // Calculate week key for this date
 const [year, month, dayNum] = dateKey.split('-').map(Number);
 const parsedDate = new Date(Date.UTC(year, month - 1, dayNum));
 const weekStart = getWeekStart(parsedDate);
 const weekKey = formatUTCDateKey(weekStart);

 // Check current pending blocking states (simplified - no config-specific logic)
 const isSlotBlocked = (pendingBlockedTimeSlots[dateKey] || []).includes(timeSlot);
 const isDayBlocked = pendingBlockedDays.includes(dateKey);
 const isWeekBlocked = pendingBlockedWeeks.includes(weekKey);


 
 // Determine current blocking level and advance to next level
 if (isWeekBlocked) {
 // Fourth click: Week is blocked, unblock completely
 toggleWeekBlock(dateKey);
 } else if (isDayBlocked) {
 // Third click: Day is blocked, block entire week (all configs)
 toggleWeekBlock(dateKey);
 } else if (isSlotBlocked) {
 // Second click: Slot is blocked, block entire day (all configs)
 setPendingBlockedTimeSlots(prev => {
 const { [dateKey]: removed, ...rest } = prev;
 return rest;
 }); // Remove slot block first
 toggleDayBlock(dateKey);
 } else {
 // First click: Slot is not blocked, block just this slot
 toggleTimeSlotBlock(dateKey, timeSlot);
 }
 };

 // Get dates for the current week
 const getCurrentWeekDates = () => {
 const dates = [];
 for (let i = 0; i < 7; i++) {
 const date = new Date(currentWeekStart);
 date.setDate(currentWeekStart.getDate() + i);
 dates.push(date);
 }
 return dates;
 };

 // Get next 7 days starting from today
 const getNext7Days = () => {
 const dates = [];
 const today = new Date();
 today.setHours(12, 0, 0, 0); // noon local — avoids timezone boundary when converting to course tz
 for (let i = 0; i < 7; i++) {
 const date = new Date(today);
 date.setDate(today.getDate() + i);
 dates.push(date);
 }
 return dates;
 };

 // Format date for display
 const formatDate = (date: Date) => {
 return date.toLocaleDateString('en-US', { 
 month: 'short', 
 day: 'numeric',
 year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
 });
 };

 // Generate all time slots from configurations for calendar view
 const getAllTimeSlots = (): string[] => {
 const allSlots = new Set<string>();
 teeTimeConfigs.forEach(config => {
 // Skip suspended configs
 if (config.status === 'suspended') {
 return;
 }
 const slots = generateTimeSlots(config.startTime, config.endTime, config.teeTimeInterval);
 slots.forEach(slot => allSlots.add(slot));
 });
 return Array.from(allSlots).sort((a, b) => {
 const timeA = new Date(`2000-01-01 ${a}`);
 const timeB = new Date(`2000-01-01 ${b}`);
 return timeA.getTime() - timeB.getTime();
 });
 };

 // Get tee time configuration data for a specific day/time slot (for Preview view)
 const getConfigSlotData = (day: string, timeSlot: string, date?: string) => {
 const applicableConfig = teeTimeConfigs.find(config => 
 config.daysOfWeek.includes(day) &&
 generateTimeSlots(config.startTime, config.endTime, config.teeTimeInterval).includes(timeSlot) &&
 config.status !== 'suspended' // Don't show suspended configurations
 );
 
 if (!applicableConfig) {
 return { available: false, configName: '', configAbbrev: '', maxPlayers: 0, teeTimesCount: 0, bookingType: 'auto' as const, blocked: false, suspended: false, configId: '' };
 }

 const isBlocked = date && isTimeSlotBlocked(date, timeSlot, applicableConfig.id);
 const teeTimesCount = calculateTeeTimesCount(applicableConfig);
 
 // Create abbreviation from config name
 const configAbbrev = applicableConfig.name
 .split(' ')
 .map(word => word.charAt(0).toUpperCase())
 .join('');
 
 return {
 available: !isBlocked,
 configName: applicableConfig.name,
 configAbbrev,
 maxPlayers: applicableConfig.maxPlayers,
 teeTimesCount,
 bookingType: applicableConfig.bookingType,
 blocked: isBlocked || false,
 suspended: suspendedConfigs.includes(applicableConfig.id),
 configId: applicableConfig.id
 };
 };

 // Get booking data for a specific day/time slot (for Bookings calendar view)
 const getBookingSlotData = (day: string, timeSlot: string, date: Date) => {
 const applicableConfig = teeTimeConfigs.find(config =>
 config.daysOfWeek.includes(day) &&
 generateTimeSlots(config.startTime, config.endTime, config.teeTimeInterval).includes(timeSlot)
 );

 if (!applicableConfig) {
 return { available: false, maxPlayers: 0, isBooked: false, playerName: null, players: 0, bookingType: 'auto' as const, configName: '' };
 }

 // Match bookings to this calendar cell by comparing date and approximate time
 const dateStr = date.toLocaleDateString('en-CA', { timeZone: courseTimezone }); // YYYY-MM-DD in course tz
 const activeBooking = bookings.find(b => {
 if (!b.slotStart || !['confirmed', 'requested', 'reserved', 'paid'].includes(b.status)) return false;
 const slotDate = new Date(b.slotStart).toLocaleDateString('en-CA', { timeZone: courseTimezone });
 const slotTime = new Date(b.slotStart).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: courseTimezone });
 return slotDate === dateStr && slotTime === timeSlot;
 });

 return {
 available: true,
 maxPlayers: applicableConfig.maxPlayers,
 isBooked: !!activeBooking,
 playerName: activeBooking?.playerName || null,
 players: activeBooking?.players || 0,
 bookingType: applicableConfig.bookingType,
 configName: applicableConfig.name,
 };
 };

 // Helper to format booking slotStart in course timezone
 const getBookingDateTime = (booking: any) => {
 if (!booking.slotStart) return { date: '', time: '' };
 
 const slotDate = new Date(booking.slotStart);
 const formatter = new Intl.DateTimeFormat('en-US', {
 timeZone: courseTimezone,
 year: 'numeric',
 month: '2-digit',
 day: '2-digit',
 hour: '2-digit',
 minute: '2-digit',
 hour12: false
 });
 
 const parts = formatter.formatToParts(slotDate);
 const month = parts.find(p => p.type === 'month')?.value || '';
 const day = parts.find(p => p.type === 'day')?.value || '';
 const year = parts.find(p => p.type === 'year')?.value || '';
 const hour = parts.find(p => p.type === 'hour')?.value || '';
 const minute = parts.find(p => p.type === 'minute')?.value || '';
 
 return {
 date: `${year}-${month}-${day}`,
 time: `${hour}:${minute}`
 };
 };

 // Get available time slots for a given YYYY-MM-DD date based on active tee time configs
 const getTimeSlotsForDate = (dateStr: string): { time: string; configId: string; configName: string; price: number; pricingType: string }[] => {
 if (!dateStr) return [];
 const [y, m, d] = dateStr.split('-').map(Number);
 const dayIndex = new Date(y, m - 1, d).getDay(); // 0=Sun..6=Sat
 const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
 const dayName = dayNames[dayIndex];
 const slots: { time: string; configId: string; configName: string; price: number; pricingType: string }[] = [];
 teeTimeConfigs
 .filter(c => c.status !== 'suspended' && c.daysOfWeek.includes(dayName))
 .forEach(c => {
 generateTimeSlots(c.startTime, c.endTime, c.teeTimeInterval).forEach(time => {
 slots.push({ time, configId: c.id, configName: c.name, price: c.price, pricingType: c.pricingType });
 });
 });
 return slots;
 };

 const resetManualBookingForm = () => {
 setMbFirstName(''); setMbLastName(''); setMbEmail(''); setMbPhone('');
 setMbDate(''); setMbTime(''); setMbCustomTime(''); setMbConfigId('');
 setMbQty('1');
 setManualBookingError('');
 };

 const handleManualBookingSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setManualBookingError('');

 const resolvedTime = mbTime === '__custom__' ? mbCustomTime : mbTime;

 if (!mbFirstName.trim() || !mbLastName.trim() || !mbEmail.trim() || !mbDate || !resolvedTime) {
 setManualBookingError('First name, last name, email, date, and time are required.');
 return;
 }

 // Convert local date+time to UTC
 const [y, m, d] = mbDate.split('-').map(Number);
 const [hourStr, minuteStr] = resolvedTime.replace(' AM', '').replace(' PM', '').split(':');
 let hour = parseInt(hourStr, 10);
 const minute = parseInt(minuteStr || '0', 10);
 if (resolvedTime.includes('PM') && hour !== 12) hour += 12;
 if (resolvedTime.includes('AM') && hour === 12) hour = 0;
 const slotStartUTC = localTimeToUTC(y, m, d, hour, minute, courseTimezone);

 // Derive pricing from selected tee time slot
 const slots = getTimeSlotsForDate(mbDate);
 const matchedSlot = slots.find(s => s.time === mbTime);
 const qty = parseInt(mbQty, 10) || 1;
 const total = matchedSlot && matchedSlot.price > 0
 ? (matchedSlot.pricingType === 'per-player' ? matchedSlot.price * qty : matchedSlot.price)
 : 0;
 const ownerAmt = total;
 const fee = 0;

 setManualBookingSubmitting(true);
 try {
 await api.post(`/api/courses/${activeCourseId}/bookings`, {
 playerFirstName: mbFirstName.trim(),
 playerLastName: mbLastName.trim(),
 playerEmail: mbEmail.trim(),
 playerPhone: mbPhone.trim(),
 slotStart: slotStartUTC.toISOString(),
 teeTimeConfigId: mbConfigId || null,
 qty,
 totalAmount: total,
 ownerAmount: ownerAmt,
 bookingFeeAmount: fee,
 });
 toast.success('Booking added successfully');
 resetManualBookingForm();
 setManualBookingOpen(false);
 // Refresh bookings list
 const bookingsData = await api.get(`/api/courses/${activeCourseId}/bookings`);
 setBookings(bookingsData.bookings || []);
 } catch (err: any) {
 setManualBookingError(err.message || 'Failed to create booking. Please try again.');
 } finally {
 setManualBookingSubmitting(false);
 }
 };

 // Calculate daily booking summary
 // Calculate dashboard metrics from bookings
 const getDashboardMetrics = (view: 'today' | 'week' | 'month') => {
 const filteredBookings = bookings.filter(booking => {
 // Exclude refunded bookings and pending requests; include confirmed + cancelled-without-refund
 if (booking.status === 'refunded') return false;
 if (booking.status === 'requested') return false;
 if (booking.status === 'cancelled' && (booking as any).refundedAt) return false;
 if (!['confirmed', 'paid', 'reserved', 'cancelled'].includes(booking.status)) return false;
 if (!booking.slotStart) return false; // Skip bookings without timestamp
 
 // Convert slot_start to course timezone for accurate date comparison
 const bookingDate = new Date(booking.slotStart);
 const formatter = new Intl.DateTimeFormat('en-US', {
 timeZone: courseTimezone,
 year: 'numeric',
 month: 'numeric',
 day: 'numeric'
 });
 const bookingLocalDate = new Date(formatter.format(bookingDate));
 
 // Get current date in course timezone
 const nowInTimezone = new Date(new Intl.DateTimeFormat('en-US', {
 timeZone: courseTimezone,
 year: 'numeric',
 month: 'numeric',
 day: 'numeric'
 }).format(new Date()));
 
 if (view === 'today') {
 return bookingLocalDate.toDateString() === nowInTimezone.toDateString();
 } else if (view === 'week') {
 const weekStart = new Date(nowInTimezone);
 weekStart.setDate(nowInTimezone.getDate() - nowInTimezone.getDay()); // Start of week (Sunday)
 const weekEnd = new Date(weekStart);
 weekEnd.setDate(weekStart.getDate() + 6); // End of week (Saturday)
 return bookingLocalDate >= weekStart && bookingLocalDate <= weekEnd;
 } else { // month
 return bookingLocalDate.getMonth() === nowInTimezone.getMonth() && 
 bookingLocalDate.getFullYear() === nowInTimezone.getFullYear();
 }
 });
 
 const totalBookings = filteredBookings.length;
 const totalPlayers = filteredBookings.reduce((sum, b) => sum + (b.players || 0), 0);
 const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.amount || 0), 0);
 
 return {
 bookingsCount: totalBookings,
 totalPlayers,
 revenue: totalRevenue
 };
 };

 const getDailySummary = (date: Date) => {
 const day = weekDays[date.getDay() === 0 ? 6 : date.getDay() - 1]; // Convert Sunday=0 to Sunday=6
 const allTimeSlots = getAllTimeSlots();

 let bookedSlots = 0;
 let totalSlots = 0;
 let totalPlayers = 0;

 allTimeSlots.forEach(timeSlot => {
 const slotData = getBookingSlotData(day, timeSlot, date);
 if (slotData.available) {
 totalSlots++;
 if (slotData.isBooked) {
 bookedSlots++;
 totalPlayers += slotData.players;
 }
 }
 });

 const utilizationPercent = totalSlots > 0 ? Math.round((bookedSlots / totalSlots) * 100) : 0;

 return {
 bookedSlots,
 totalSlots,
 totalPlayers,
 utilizationPercent
 };
 };
const fetchConnectStatus = async (courseId: string) => {
  setConnectStatus('loading');
  try {
    const data = await api.get(`/api/courses/${courseId}/stripe/status`);
    setConnectStatus(data.status);
  } catch {
    setConnectStatus('not_started');
  }
};

const startConnectFlow = async () => {
  if (!activeCourseId) return;
  setConnectLoading(true);
  try {
    const data = await api.post(`/api/courses/${activeCourseId}/stripe/connect`);
    if (data.url) window.location.href = data.url;
  } catch (err: any) {
    toast.error(err.message || 'Failed to start payout setup. Please try again.');
    setConnectLoading(false);
  }
};

const fetchEarnings = async (courseId: string) => {
  setEarningsLoading(true);
  try {
    const { data, error } = await supabase
      .from('bookings')
      .select('id, slot_start, player_id, owner_amount, transfer_status, transferred_at, status, refunded_at')
      .eq('course_id', courseId)
      .in('status', ['confirmed', 'paid', 'reserved', 'cancelled', 'refunded'])
      .order('slot_start', { ascending: false });
    if (error) throw error;
    const rows = data || [];

    // Fetch player names
    const playerIds = [...new Set(rows.map((r: any) => r.player_id).filter(Boolean))];
    let nameMap: Record<string, string> = {};
    if (playerIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', playerIds);
      for (const p of profiles || []) {
        nameMap[p.id] = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Player';
      }
    }

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    let pendingEarnings = 0;
    let transferredThisMonth = 0;
    let lastTransferDate: string | null = null;

    for (const r of rows) {
      const amount = parseFloat(r.owner_amount) || 0;
      // Exclude fully refunded bookings and cancelled bookings where a refund was issued
      const wasRefunded = r.status === 'refunded' || (r.status === 'cancelled' && r.refunded_at);
      if (!wasRefunded && (!r.transfer_status || r.transfer_status === 'pending')) {
        pendingEarnings += amount;
      }
      if (r.transfer_status === 'transferred' && r.transferred_at) {
        const t = new Date(r.transferred_at);
        if (t >= monthStart) transferredThisMonth += amount;
        if (!lastTransferDate || t > new Date(lastTransferDate)) lastTransferDate = r.transferred_at;
      }
    }

    // Fetch course's stripe_account_id and owner payout_failed flag in parallel
    const [courseRes, profileRes] = await Promise.all([
      supabase.from('courses').select('stripe_account_id, owner_id').eq('id', courseId).maybeSingle(),
      user ? supabase.from('profiles').select('payout_failed').eq('id', user.id).maybeSingle() : Promise.resolve({ data: null }),
    ]);

    const stripeAccountId = courseRes.data?.stripe_account_id ?? null;
    const payoutFailed = profileRes.data?.payout_failed ?? false;

    // Fetch payout history for this connected account
    let payoutHistory: { id: string; stripePayoutId: string; amount: number; status: string; failureMessage: string | null; arrivalDate: string | null }[] = [];
    let paidOutThisMonth = 0;
    let lastPayoutDate: string | null = null;

    if (stripeAccountId) {
      const { data: payouts } = await supabase
        .from('payouts')
        .select('id, stripe_payout_id, amount, status, failure_message, arrival_date, created_at')
        .eq('stripe_account_id', stripeAccountId)
        .order('created_at', { ascending: false });

      for (const p of payouts || []) {
        if (p.status === 'paid' && p.arrival_date) {
          const t = new Date(p.arrival_date);
          if (t >= monthStart) paidOutThisMonth += p.amount / 100;
          if (!lastPayoutDate || t > new Date(lastPayoutDate)) lastPayoutDate = p.arrival_date;
        }
      }

      payoutHistory = (payouts || []).map((p: any) => ({
        id: p.id,
        stripePayoutId: p.stripe_payout_id,
        amount: p.amount,
        status: p.status,
        failureMessage: p.failure_message,
        arrivalDate: p.arrival_date,
      }));
    }

    setEarningsData({
      pendingEarnings,
      transferredThisMonth,
      lastTransferDate,
      history: rows.map((r: any) => ({
        id: r.id,
        slotStart: r.slot_start,
        playerName: nameMap[r.player_id] || 'Player',
        ownerAmount: parseFloat(r.owner_amount) || 0,
        transferStatus: r.transfer_status,
        transferredAt: r.transferred_at,
        bookingStatus: r.status,
        refundedAt: r.refunded_at || null,
      })),
      paidOutThisMonth,
      lastPayoutDate,
      payoutHistory,
      payoutFailed,
    });
    setEarningsPage(0);

    // Fetch distro fund summary (not course-specific — platform-wide)
    try {
      const distro = await api.get('/api/distro-fund/summary');
      setDistroSummary(distro);
    } catch (err) {
      console.error('Failed to fetch distro summary:', err);
    }
  } catch (err) {
    console.error('Failed to fetch earnings:', err);
  } finally {
    setEarningsLoading(false);
  }
};

const fetchInsights = async () => {
  if (!activeCourseId) return;
  setInsightsLoading(true);
  try {
   const data = await api.get(`/api/courses/${activeCourseId}/insights`);
   setInsightsData(data);
  } catch (err) {
   console.error('Failed to fetch insights:', err);
  } finally {
   setInsightsLoading(false);
  }
 };

// Function to load course data given a courseId
 const loadCourseData = async (courseId: string) => {
 if (!courseId) return;
 try {
 setLoading(true);
 setLoadErr("");
 // owner-protected, uses x-user-id header
 const data = await api.get(`/api/owner/courses/${courseId}`);
 // data shape: { course, configs, tee_time_count }
 setCourse(data.course || null);
 setSelectedHoles(data.course?.holes?.toString() || "18");
 setCourseName(data.course?.name || '');
 setCourseDescription(data.course?.description || '');
 setContactEmail(data.course?.contact_email || '');
 setContactPhone(data.course?.contact_phone || '');
 setCourseWebsite(data.course?.website_url || '');
 savedContactEmail.current = data.course?.contact_email || '';
 savedContactPhone.current = data.course?.contact_phone || '';
 savedCourseWebsite.current = data.course?.website_url || '';
 setNotifEmailBookings(data.course?.notif_email_bookings !== false);
 setNotifEmailCancellations(data.course?.notif_email_cancellations !== false);
 
 // Initialize amenities and photos from course data
 if (data.course) {
 // Create initial amenities object
 const initialAmenities = {
 restrooms: data.course.amenity_restrooms || false,
 proShop: data.course.amenity_pro_shop || false,
 foodDrinks: data.course.amenity_food_drinks || false,
 equipmentRental: data.course.amenity_equipment_rental || false,
 practiceArea: data.course.amenity_practice_area || false,
 waterStations: data.course.amenity_water_stations || false,
 picnicArea: data.course.amenity_picnic_area || false,
 dogFriendly: data.course.amenity_dog_friendly || false,
 garbageCans: data.course.amenity_garbage_cans || false
 };
 setAmenities(initialAmenities);

 // Initialize location state
 setCourseAddress(data.course.address || '');
 setCourseLatitude(data.course.latitude || null);
 setCourseLongitude(data.course.longitude || null);
 setCourseTimezone(data.course.timezone || 'America/New_York');
 
 // Initialize booking fee payer
 setBookingFeePayer(data.course.booking_fee_payer || 'player');
 
 // Create initial photo paths object
 const initialPhotoPaths = {
 mainPath: data.course.photo_main_path || null,
 galleryPaths: Array.isArray(data.course.photo_gallery_paths) ? data.course.photo_gallery_paths : [],
 mapPath: data.course.photo_map_path || null
 };
 setPhotoPaths(initialPhotoPaths);
 
 // Capture original data for change tracking (deep copy to avoid reference issues)
 setOriginalCourseData({
 name: data.course.name || '',
 description: data.course.description || '',
 address: data.course.address || '',
 latitude: data.course.latitude || null,
 longitude: data.course.longitude || null,
 timezone: data.course.timezone || 'America/New_York',
 holes: data.course.holes?.toString() || '18',
 amenities: { ...initialAmenities },
 photoPaths: { ...initialPhotoPaths, galleryPaths: [...initialPhotoPaths.galleryPaths] }
 });
 }
 
 // Fetch owner profile if we have owner_id
 if (data.course?.owner_id) {
 try {
 const profileData = await api.get(`/api/owner/profile/${data.course.owner_id}`);
 setOwnerProfile(profileData.profile || null);
 } catch (profileError) {
 console.error('Failed to load owner profile:', profileError);
 }
 }
 
 // map backend fields -> your UI model
 const mapped = (data.configs || []).map(c => ({
 id: c.id,
 name: c.name,
 maxPlayers: c.max_players_per_slot,
 teeTimeInterval: c.interval_minutes,
 bookingType: (c.booking_type === "request" ? "request" : "auto"),
 startTime: c.start_time, // "08:00"
 endTime: c.end_time, // "12:00"
 daysOfWeek: (c.days_of_week || []).map((numDay: number) => numericDayToName(numDay)),
 pricingType: (c.pricing_type === "per-round" ? "per-round" : "per-player"),
 price: (c.price_cents ?? 0) / 100,
 status: c.status || 'active', // Default to 'active' if not set
 }));
 setTeeTimeConfigs(mapped);
 
 // Load ad-hoc tee time blocks from database
 // TODO: Implement block loading when needed
 // try {
 // const blocksData = await api.get(`/api/courses/${courseId}/blocks`);
 // // Set block state here
 // } catch (blocksError) {
 // console.error('Failed to load ad-hoc blocks:', blocksError);
 // }
 
 // Load bookings
 try {
 const bookingsData = await api.get(`/api/courses/${courseId}/bookings`);
 setBookings(bookingsData.bookings || []);
 } catch (bookingsError) {
 console.error('Failed to load bookings:', bookingsError);
 }
 
 // Load refund policies
 try {
 const policiesData = await api.get('/api/refund-policies');
 setRefundPolicies(policiesData || []);
 } catch (policyError) {
 console.error('Failed to load refund policies:', policyError);
 }

 // Load insights (reviews + KPIs) so dashboard rating tile is populated on arrival
 try {
 const insightsRes = await api.get(`/api/courses/${courseId}/insights`);
 setInsightsData(insightsRes);
 } catch (insightsError) {
 console.error('Failed to load insights:', insightsError);
 }
 } catch (e: any) {
 console.error(e);
 setLoadErr(e.message || "Failed to load");
 } finally {
 setLoading(false);
 }
 };


 const renderDashboard = () => (
 <div className="space-y-6">

 {/* Key Metrics with Toggle */}
 <div className="space-y-4">
 <div className="flex justify-center">
 <div className="inline-flex items-center rounded-lg p-1" style={{ backgroundColor: '#F5F5DC' }}>
 <button
 onClick={() => setMetricsView('today')}
 className={`
 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200
 ${metricsView === 'today'
 ? 'bg-[#2B8B3B] text-white shadow-sm'
 : 'text-[#2B8B3B] hover:bg-white'
 }
 `}
 >
 Today
 </button>
 <button
 onClick={() => setMetricsView('week')}
 className={`
 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200
 ${metricsView === 'week'
 ? 'bg-[#2B8B3B] text-white shadow-sm'
 : 'text-[#2B8B3B] hover:bg-white'
 }
 `}
 >
 This Week
 </button>
 <button
 onClick={() => setMetricsView('month')}
 className={`
 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200
 ${metricsView === 'month'
 ? 'bg-[#2B8B3B] text-white shadow-sm'
 : 'text-[#2B8B3B] hover:bg-white'
 }
 `}
 >
 This Month
 </button>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
 {(() => {
 const avgRating = insightsData?.reviewData?.averageRating ?? 0;
 const totalReviews = insightsData?.reviewData?.totalReviews ?? 0;
 return (
 <Card>
 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
 <CardTitle className="text-sm font-medium">Course Rating</CardTitle>
 <Star className="h-4 w-4 text-muted-foreground" />
 </CardHeader>
 <CardContent>
 <div className="flex items-baseline gap-3 flex-wrap">
 <div className="flex items-center gap-2 flex-shrink-0">
 <div className="text-2xl font-bold text-[#2B8B3B]">{avgRating > 0 ? avgRating.toFixed(1) : '—'}</div>
 <div className="flex">
 {[1, 2, 3, 4, 5].map((star) => (
 <Star
 key={star}
 className={`h-3 w-3 ${star <= Math.round(avgRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
 />
 ))}
 </div>
 </div>
 </div>
 <p className="text-xs text-muted-foreground mt-1">
 {totalReviews === 0 ? 'No reviews yet' : `Based on ${totalReviews} ${totalReviews === 1 ? 'review' : 'reviews'}`}
 </p>
 </CardContent>
 </Card>
 );
 })()}
 {(() => {
 const metrics = getDashboardMetrics(metricsView);
 return (
 <>
 <Card>
 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
 <CardTitle className="text-sm font-medium">
 {metricsView === 'today' ? "Today's Bookings" : 
 metricsView === 'week' ? "Week's Bookings" : "Month's Bookings"}
 </CardTitle>
 <Calendar className="h-4 w-4 text-muted-foreground" />
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-bold">{metrics.bookingsCount}</div>
 <p className="text-xs text-muted-foreground">
 {metrics.bookingsCount === 0 ? 'No bookings yet' : 
 metricsView === 'today' ? 'confirmed bookings today' :
 metricsView === 'week' ? 'confirmed bookings this week' :
 'confirmed bookings this month'}
 </p>
 </CardContent>
 </Card>
 <Card>
 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
 <CardTitle className="text-sm font-medium">
 Total Players
 </CardTitle>
 <Users className="h-4 w-4 text-muted-foreground" />
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-bold">{metrics.totalPlayers}</div>
 <p className="text-xs text-muted-foreground">
 {metrics.totalPlayers === 0 ? 'No players yet' :
 metricsView === 'today' ? 'players booked today' :
 metricsView === 'week' ? 'players booked this week' :
 'players booked this month'}
 </p>
 </CardContent>
 </Card>
 <Card>
 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
 <CardTitle className="text-sm font-medium">
 {metricsView === 'today' ? "Today's Revenue" : 
 metricsView === 'week' ? "Week's Revenue" : "Month's Revenue"}
 </CardTitle>
 <DollarSign className="h-4 w-4 text-muted-foreground" />
 </CardHeader>
 <CardContent>
 <div className="text-2xl font-bold">${metrics.revenue.toFixed(2)}</div>
 <p className="text-xs text-muted-foreground">
 {metrics.revenue === 0 ? 'No revenue yet' :
 metricsView === 'today' ? 'earned today' :
 metricsView === 'week' ? 'earned this week' :
 'earned this month'}
 </p>
 </CardContent>
 </Card>
 </>
 );
 })()}
 </div>

 {/* View Detailed Insights Button */}
 <div className="flex justify-center">
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => handleNavigation('insights')}
 >
 View Detailed Insights
 </Button>
 </div>
 </div>

 {/* Pending Requests - Priority Section */}
 {bookings.filter(booking => booking.status === 'requested').length > 0 && (
 <Card className="border-orange-200 bg-orange-50">
 <CardHeader>
 <div className="flex items-center gap-2">
 <AlertCircle className="h-5 w-5 text-orange-600" />
 <CardTitle className="text-orange-900">Pending Requests</CardTitle>
 </div>
 <CardDescription className="text-orange-700">
 These requests need your immediate attention
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {bookings.filter(booking => booking.status === 'requested').map((booking) => (
 <div key={booking.id} className="flex items-center justify-between p-4 bg-white border border-orange-200 rounded-lg">
 <div className="flex items-center space-x-4">
 <div>
 <p className="font-medium">{booking.playerName}</p>
 <p className="text-sm text-muted-foreground">{booking.email}</p>
 </div>
 </div>
 <div className="text-center">
 <p className="font-medium">{getBookingDateTime(booking).date} at {getBookingDateTime(booking).time}</p>
 <p className="text-sm text-muted-foreground">{booking.players} players • ${booking.amount?.toFixed(2)}</p>
 </div>
 <div className="flex gap-2">
 <Button 
 size="sm" 
 className={brandStyles.primary}
 onClick={() => handleBookingRequest(booking.id, 'accept')}
 >
 <CheckCircle className="h-3 w-3 mr-1" />
 Accept
 </Button>
 <Button 
 size="sm" 
 variant="outline"
 onClick={() => handleBookingRequest(booking.id, 'reject')}
 >
 <X className="h-3 w-3 mr-1" />
 Reject
 </Button>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Weekly Bookings Overview */}
 <Card>
 <CardHeader>
 <CardTitle>Next 7 Days Overview</CardTitle>
 <CardDescription>Daily booking summaries and capacity overview</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
 {getNext7Days().map((date, index) => {
 const dailySummary = getDailySummary(date);
 const isToday = date.toDateString() === new Date().toDateString();
 
 // Get actual day of week for this date
 const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
 const dayName = dayOfWeek === 0 ? 'Sun' : weekDays[dayOfWeek - 1]; // Convert Sunday=0 to Sun, others to weekDays
 
 // Determine color based on utilization
 let bgColor = '#F5F5DC';
 let bgColorClass = '';
 let borderColor = 'border-[#2B8B3B]';
 let textColor = 'text-[#2B8B3B]';
 
 if (dailySummary.utilizationPercent >= 90) {
 bgColor = null;
 bgColorClass = 'bg-red-50';
 borderColor = 'border-red-400';
 textColor = 'text-red-700';
 } else if (dailySummary.utilizationPercent >= 70) {
 bgColor = null;
 bgColorClass = 'bg-orange-50';
 borderColor = 'border-orange-400';
 textColor = 'text-orange-700';
 } else if (dailySummary.utilizationPercent >= 40) {
 bgColor = null;
 bgColorClass = 'bg-yellow-50';
 borderColor = 'border-yellow-400';
 textColor = 'text-yellow-700';
 }
 
 return (
 <div
 key={index}
 className={`
 p-4 border-2 rounded-lg ${bgColorClass} ${borderColor}
 ${isToday ? 'ring-2 ring-[#2B8B3B] ring-opacity-50' : ''}
 `}
 style={bgColor ? { backgroundColor: bgColor } : undefined}
 >
 <div className="text-center">
 <div className={`font-medium text-sm ${textColor}`}>
 {dayName}
 </div>
 <div className="text-xs text-muted-foreground mb-3">
 {formatDate(date)}
 </div>
 
 <div className="space-y-2 text-center">
 <div className={`text-xl font-bold ${textColor}`}>
 {dailySummary.bookedSlots}/{dailySummary.totalSlots}
 </div>
 
 <div className={`text-sm ${textColor}`}>
 {dailySummary.totalPlayers} players
 </div>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 
 <div className="mt-4 text-center">
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => {
 setBookingsActiveTab('calendar');
 handleNavigation('bookings');
 }}
 >
 View Detailed Calendar
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Recent Bookings */}
 <Card>
 <CardHeader>
 <CardTitle>Recent Bookings</CardTitle>
 <CardDescription>Latest confirmed reservations and check-ins</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {bookings.filter(booking => ['confirmed', 'requested'].includes(booking.status)).slice(0, 4).map((booking) => (
 <div key={booking.id} className="flex items-center justify-between p-4 border rounded-lg">
 <div className="flex items-center space-x-4">
 <div>
 <p className="font-medium">{booking.playerName}</p>
 <p className="text-sm text-muted-foreground">{booking.email}</p>
 </div>
 </div>
 <div className="text-center">
 <p className="font-medium">{getBookingDateTime(booking).date} at {getBookingDateTime(booking).time}</p>
 <p className="text-sm text-muted-foreground">{booking.players} players • ${booking.amount?.toFixed(2)}</p>
 </div>
 <div className="flex items-center gap-3">
 <Badge variant="outline" className={booking.status === 'confirmed' ? 'bg-green-100 text-green-800 border-green-300' : 'bg-orange-50 text-orange-700 border-orange-300'}>
 {booking.status === 'confirmed' && <CheckCircle className="h-3 w-3 mr-1" />}
 {booking.status === 'requested' && <Clock className="h-3 w-3 mr-1" />}
 {booking.status === 'confirmed' ? 'Confirmed' : 'Pending Request'}
 </Badge>
 </div>
 </div>
 ))}
 </div>
 
 <div className="mt-4 text-center">
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => {
 setBookingsActiveTab('table');
 handleNavigation('bookings');
 }}
 >
 View All Bookings
 </Button>
 </div>
 </CardContent>
 </Card>
 </div>
 );

 const renderCourseProfile = () => (
 <div className="space-y-6">
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* Basic Information */}
 <Card>
 <CardHeader>
 <div className="flex items-start justify-between">
 <div>
 <CardTitle>Basic Information</CardTitle>
 <CardDescription>Essential details about your disc golf course</CardDescription>
 </div>
 {!isEditingBasicInfo ? (
 <Button 
 onClick={() => setIsEditingBasicInfo(true)} 
 size="sm"
 variant="outline"
 >
 <Edit className="h-4 w-4 mr-1" />
 Edit
 </Button>
 ) : (
 <div className="flex gap-2">
 <Button 
 onClick={handleCancelBasicInfo} 
 size="sm"
 variant="outline"
 >
 Cancel
 </Button>
 <Button 
 onClick={handleSaveBasicInfo} 
 size="sm"
 className={brandStyles.primary}
 >
 <Save className="h-4 w-4 mr-1" />
 Save
 </Button>
 </div>
 )}
 </div>
 </CardHeader>
 <CardContent className="space-y-6">
 <div className="space-y-2">
 <Label htmlFor="courseName">Course Name</Label>
 <Input
 id="courseName"
 placeholder="Enter course name"
 value={courseName || ''}
 onChange={(e) => setCourseName(e.target.value)}
 disabled={!isEditingBasicInfo}
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="description">Course Description</Label>
 <Textarea
 id="description"
 placeholder="Describe your disc golf course..."
 rows={4}
 value={courseDescription || ''}
 onChange={(e) => setCourseDescription(e.target.value)}
 disabled={!isEditingBasicInfo}
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="numberOfHoles">Number of Holes</Label>
 <Select 
 value={selectedHoles} 
 onValueChange={setSelectedHoles}
 disabled={!isEditingBasicInfo}
 >
 <SelectTrigger className={!isEditingBasicInfo ? 'bg-gray-50 cursor-not-allowed' : ''}>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="9">9 Holes</SelectItem>
 <SelectItem value="12">12 Holes</SelectItem>
 <SelectItem value="15">15 Holes</SelectItem>
 <SelectItem value="18">18 Holes</SelectItem>
 <SelectItem value="21">21 Holes</SelectItem>
 <SelectItem value="24">24 Holes</SelectItem>
 <SelectItem value="27">27 Holes</SelectItem>
 <SelectItem value="30">30 Holes</SelectItem>
 <SelectItem value="33">33 Holes</SelectItem>
 <SelectItem value="36">36 Holes</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </CardContent>
 </Card>

 {/* Location */}
 <Card>
 <CardHeader>
 <div className="flex items-start justify-between">
 <div>
 <CardTitle>Location</CardTitle>
 <CardDescription>Set your course location and address</CardDescription>
 </div>
 {!isEditingLocation ? (
 <Button 
 onClick={() => setIsEditingLocation(true)} 
 size="sm"
 variant="outline"
 >
 <Edit className="h-4 w-4 mr-1" />
 Edit
 </Button>
 ) : (
 <div className="flex gap-2">
 <Button 
 onClick={handleCancelLocation} 
 size="sm"
 variant="outline"
 >
 Cancel
 </Button>
 <Button 
 onClick={handleSaveLocation} 
 size="sm"
 className={brandStyles.primary}
 >
 <Save className="h-4 w-4 mr-1" />
 Save
 </Button>
 </div>
 )}
 </div>
 </CardHeader>
 <CardContent className="space-y-6">
 <div className="space-y-2">
 <Label htmlFor="location">Address</Label>
 <AddressSearch
 value={courseAddress}
 onChange={setCourseAddress}
 onLocationSelect={(lat, lng, address) => {
 if (isEditingLocation) {
 setCourseLatitude(lat);
 setCourseLongitude(lng);
 setCourseAddress(address);
 }
 }}
 disabled={!isEditingLocation}
 />
 <p className="text-xs text-gray-500 mt-1">
 {isEditingLocation ? 'Search for an address to set the course location' : 'Course address'}
 </p>
 </div>

 <div className="space-y-2">
 <Label htmlFor="timezone">Timezone</Label>
 <Select 
 value={courseTimezone} 
 onValueChange={setCourseTimezone}
 disabled={!isEditingLocation}
 >
 <SelectTrigger className={!isEditingLocation ? 'bg-gray-50 cursor-not-allowed' : ''}>
 <SelectValue />
 </SelectTrigger>
 <SelectContent style={{ zIndex: 9999 }}>
 <SelectItem value="America/New_York">Eastern (America/New_York)</SelectItem>
 <SelectItem value="America/Chicago">Central (America/Chicago)</SelectItem>
 <SelectItem value="America/Denver">Mountain (America/Denver)</SelectItem>
 <SelectItem value="America/Phoenix">Mountain - No DST (America/Phoenix)</SelectItem>
 <SelectItem value="America/Los_Angeles">Pacific (America/Los_Angeles)</SelectItem>
 <SelectItem value="America/Anchorage">Alaska (America/Anchorage)</SelectItem>
 <SelectItem value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</SelectItem>
 </SelectContent>
 </Select>
 <p className="text-xs text-gray-500 mt-1">
 Select the timezone for your course location
 </p>
 </div>

 <div className="space-y-2">
 <Label>Map Location</Label>
 <InteractiveMap
 latitude={courseLatitude}
 longitude={courseLongitude}
 onLocationChange={(lat, lng) => {
 if (isEditingLocation) {
 setCourseLatitude(lat);
 setCourseLongitude(lng);
 }
 }}
 draggable={isEditingLocation}
 />
 {courseLatitude !== null && courseLongitude !== null && (
 <p className="text-xs text-gray-500">
 Coordinates: {courseLatitude.toFixed(6)}, {courseLongitude.toFixed(6)}
 </p>
 )}
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Amenities */}
 <Card>
 <CardHeader>
 <CardTitle>Course Amenities</CardTitle>
 <CardDescription>Select the amenities available at your course</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 {[
 { label: 'Restrooms', key: 'restrooms' },
 { label: 'Pro Shop', key: 'proShop' },
 { label: 'Food & Drinks', key: 'foodDrinks' },
 { label: 'Equipment Rental', key: 'equipmentRental' },
 { label: 'Practice Area', key: 'practiceArea' },
 { label: 'Water Stations', key: 'waterStations' },
 { label: 'Picnic Area', key: 'picnicArea' },
 { label: 'Dog Friendly', key: 'dogFriendly' },
 { label: 'Garbage Cans', key: 'garbageCans' }
 ].map((amenity) => (
 <div key={amenity.key} className="flex items-center space-x-2">
 <input
 type="checkbox"
 id={amenity.key}
 className="rounded"
 checked={amenities[amenity.key]}
 onChange={async (e) => {
   const updated = { ...amenities, [amenity.key]: e.target.checked };
   setAmenities(updated);
   setOriginalCourseData(prev => prev ? { ...prev, amenities: updated } : prev);
   if (activeCourseId) {
     try {
       await api.post(`/api/owner/courses/${activeCourseId}`, {
         amenity_restrooms: updated.restrooms,
         amenity_pro_shop: updated.proShop,
         amenity_food_drinks: updated.foodDrinks,
         amenity_equipment_rental: updated.equipmentRental,
         amenity_practice_area: updated.practiceArea,
         amenity_water_stations: updated.waterStations,
         amenity_picnic_area: updated.picnicArea,
         amenity_dog_friendly: updated.dogFriendly,
         amenity_garbage_cans: updated.garbageCans,
       });
     } catch (err) {
       console.error('Failed to save amenity:', err);
       toast.error('Failed to save amenity');
     }
   }
 }}
 />
 <Label htmlFor={amenity.key} className="text-sm font-normal">
 {amenity.label}
 </Label>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Course Photos */}
 <Card>
 <CardHeader>
 <CardTitle>Course Photos</CardTitle>
 <CardDescription>Upload photos to showcase your disc golf course</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 {/* Main Photo */}
 <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
 <div className="space-y-2">
 <div className="mx-auto h-12 w-12 text-gray-400">
 <Plus className="h-full w-full" />
 </div>
 <div className="text-sm text-gray-600">
 <input
 type="file"
 ref={mainPhotoInputRef}
 accept="image/*"
 className="hidden"
 style={{ display: 'none' }}
 onChange={async (e) => {
 const file = e.target.files?.[0];
 if (file) {
 try {
 toast.success(`Uploading ${file.name}...`);
 const path = await uploadPhoto(file, 'main');
 setPhotoPaths(prev => ({ ...prev, mainPath: path }));
 toast.success(`Uploaded: ${file.name}`);
 } catch (error) {
 toast.error(`Upload failed: ${error.message}`);
 }
 }
 }}
 />
 <Button 
 variant="outline" 
 size="sm" 
 onClick={() => mainPhotoInputRef.current?.click()}
 >
 Upload Main Photo
 </Button>
 <p className="mt-1">Primary course image</p>
 {photoPaths.mainPath && (
 <div className="mt-2 space-y-1">
 <img
   src={getPhotoUrl(photoPaths.mainPath)}
   alt="Main photo preview"
   className="w-full h-32 object-cover rounded-md"
 />
 <div className="flex items-center justify-center gap-2">
 <p className="text-xs text-green-600">✓ Uploaded</p>
 <Button
 variant="ghost"
 size="sm"
 className="h-5 px-2 text-xs text-red-600 hover:text-red-700"
 onClick={async () => {
 try {
 await deletePhoto(photoPaths.mainPath!);
 setPhotoPaths(prev => ({ ...prev, mainPath: null }));
 toast.success('Photo removed');
 } catch (error) {
 toast.error('Failed to remove photo');
 }
 }}
 >
 Remove
 </Button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 
 {/* Gallery Photos */}
 <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
 <div className="space-y-2">
 <div className="mx-auto h-12 w-12 text-gray-400">
 <Plus className="h-full w-full" />
 </div>
 <div className="text-sm text-gray-600">
 <input
 type="file"
 ref={galleryPhotosInputRef}
 accept="image/*"
 multiple
 className="hidden"
 style={{ display: 'none' }}
 onChange={async (e) => {
 const files = Array.from(e.target.files || []);
 if (files.length > 0) {
 try {
 toast.success(`Uploading ${files.length} photo(s)...`);
 const uploadPromises = files.map(file => uploadPhoto(file, 'gallery'));
 const paths = await Promise.all(uploadPromises);
 setPhotoPaths(prev => ({ 
 ...prev, 
 galleryPaths: [...prev.galleryPaths, ...paths] 
 }));
 toast.success(`Uploaded ${files.length} photo(s)`);
 } catch (error) {
 toast.error(`Upload failed: ${error.message}`);
 }
 }
 }}
 />
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => galleryPhotosInputRef.current?.click()}
 >
 Upload Gallery
 </Button>
 <p className="mt-1">Additional photos</p>
 {photoPaths.galleryPaths.length > 0 && (
 <div className="mt-2 space-y-1">
 <div className="flex flex-wrap gap-1 justify-center">
   {photoPaths.galleryPaths.map((path, i) => (
     <img key={i} src={getPhotoUrl(path)} alt={`Gallery ${i + 1}`} className="h-20 w-20 object-cover rounded" />
   ))}
 </div>
 <div className="flex items-center justify-center gap-2">
 <p className="text-xs text-green-600">✓ {photoPaths.galleryPaths.length} photo(s)</p>
 <Button
 variant="ghost"
 size="sm"
 className="h-5 px-2 text-xs text-red-600 hover:text-red-700"
 onClick={async () => {
 try {
 await Promise.all(photoPaths.galleryPaths.map(path => deletePhoto(path)));
 setPhotoPaths(prev => ({ ...prev, galleryPaths: [] }));
 toast.success('Gallery photos removed');
 } catch (error) {
 toast.error('Failed to remove photos');
 }
 }}
 >
 Remove All
 </Button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 
 {/* Map Photo */}
 <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
 <div className="space-y-2">
 <div className="mx-auto h-12 w-12 text-gray-400">
 <Plus className="h-full w-full" />
 </div>
 <div className="text-sm text-gray-600">
 <input
 type="file"
 ref={mapPhotoInputRef}
 accept="image/*"
 className="hidden"
 style={{ display: 'none' }}
 onChange={async (e) => {
 const file = e.target.files?.[0];
 if (file) {
 try {
 toast.success(`Uploading ${file.name}...`);
 const path = await uploadPhoto(file, 'map');
 setPhotoPaths(prev => ({ ...prev, mapPath: path }));
 toast.success(`Uploaded: ${file.name}`);
 } catch (error) {
 toast.error(`Upload failed: ${error.message}`);
 }
 }
 }}
 />
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => mapPhotoInputRef.current?.click()}
 >
 Upload Map
 </Button>
 <p className="mt-1">Course layout map</p>
 {photoPaths.mapPath && (
 <div className="mt-2 space-y-1">
 <img
   src={getPhotoUrl(photoPaths.mapPath)}
   alt="Map photo preview"
   className="w-full h-32 object-cover rounded-md"
 />
 <div className="flex items-center justify-center gap-2">
 <p className="text-xs text-green-600">✓ Uploaded</p>
 <Button
 variant="ghost"
 size="sm"
 className="h-5 px-2 text-xs text-red-600 hover:text-red-700"
 onClick={async () => {
 try {
 await deletePhoto(photoPaths.mapPath!);
 setPhotoPaths(prev => ({ ...prev, mapPath: null }));
 toast.success('Photo removed');
 } catch (error) {
 toast.error('Failed to remove photo');
 }
 }}
 >
 Remove
 </Button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 );

 const renderTeeTimeSetup = () => {
 const allTimeSlots = getAllTimeSlots();
 
 return (
 <div className="space-y-6">
 {/* Summary Stats */}
 <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Active Configs</p>
 <p className="text-2xl font-bold">{teeTimeConfigs.filter(c => c.status === 'active').length}</p>
 </div>
 <Settings className="h-8 w-8 text-muted-foreground" />
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Suspended Configs</p>
 <p className="text-2xl font-bold">{teeTimeConfigs.filter(c => c.status === 'suspended').length}</p>
 </div>
 <AlertCircle className="h-8 w-8 text-muted-foreground" />
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Blocked Time Slots</p>
 <div className="flex items-center gap-2">
 <p className="text-2xl font-bold">
 {Object.values(pendingBlockedTimeSlots).reduce((total, slots) => total + slots.length, 0)}
 </p>
 {hasUnsavedChanges && (
 <div className="w-2 h-2 bg-orange-500 rounded-full" title="Unsaved changes" />
 )}
 </div>
 </div>
 <Clock className="h-8 w-8 text-muted-foreground" />
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Blocked Days</p>
 <div className="flex items-center gap-2">
 <p className="text-2xl font-bold">{pendingBlockedDays.length}</p>
 {hasUnsavedChanges && (
 <div className="w-2 h-2 bg-orange-500 rounded-full" title="Unsaved changes" />
 )}
 </div>
 </div>
 <Calendar className="h-8 w-8 text-muted-foreground" />
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Blocked Weeks</p>
 <div className="flex items-center gap-2">
 <p className="text-2xl font-bold">
 {Object.values(pendingBlockedWeeks).reduce((total, configs) => total + configs.length, 0)}
 </p>
 {hasUnsavedChanges && (
 <div className="w-2 h-2 bg-orange-500 rounded-full" title="Unsaved changes" />
 )}
 </div>
 </div>
 <CalendarDays className="h-8 w-8 text-muted-foreground" />
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Tee Time Configurations Management */}
 <Card>
 <CardHeader className="flex items-start justify-between">
 <div>
 <CardTitle>Tee Time Configurations</CardTitle>
 <CardDescription>Create and manage your tee time availability schedules. Click on configuration to suspend it.</CardDescription>
 </div>
 <Button
 variant="outline"
 size="sm"
 onClick={() => {
 setEditingConfig(null);
 setConfigErrors([]);
 setShowConfigDialog(true);
 }}
 className="border-[#2B8B3B] text-[#2B8B3B] hover:!bg-gray-100"
 aria-label="Add Configuration"
 >
 <Plus className="h-4 w-4" />
 </Button>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {teeTimeConfigs.map((config) => {
 const isSuspended = config.status === 'suspended';
 return (
 <Card 
 key={config.id} 
 className={`border-2 cursor-pointer transition-all ${
 isSuspended ? 'bg-gray-50 border-gray-300 opacity-75' : 'hover:shadow-md'
 }`}
 onClick={() => toggleConfigSuspension(config.id)}
 >
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-4 mb-2">
 <h4 className={`font-medium ${isSuspended ? 'text-gray-600' : ''}`}>
 {config.name}
 </h4>
 <Badge variant={config.bookingType === 'auto' ? 'default' : 'secondary'}>
 {config.bookingType === 'auto' ? 'Auto Booking' : 'Request to Book'}
 </Badge>
 {isSuspended && (
 <Badge variant="destructive">Suspended</Badge>
 )}
 {!isSuspended && (
 <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
 Active
 </Badge>
 )}
 </div>
 <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
 <div>
 <span className="text-muted-foreground">Time: </span>
 <span className={isSuspended ? 'text-gray-600' : ''}>
 {config.startTime} - {config.endTime}
 </span>
 </div>
 <div>
 <span className="text-muted-foreground">Max Players: </span>
 <span className={isSuspended ? 'text-gray-600' : ''}>
 {config.maxPlayers}
 </span>
 </div>
 <div>
 <span className="text-muted-foreground">Tee Time Interval: </span>
 <span className={isSuspended ? 'text-gray-600' : ''}>
 {config.teeTimeInterval}min
 </span>
 </div>
 <div>
 <span className="text-muted-foreground">Price: </span>
 <span className={isSuspended ? 'text-gray-600' : ''}>
 ${config.price} {config.pricingType === 'per-player' ? '/player' : '/round'}
 </span>
 </div>
 <div>
 <span className="text-muted-foreground">Days: </span>
 <span className={isSuspended ? 'text-gray-600' : ''}>
 {config.daysOfWeek.join(', ')}
 </span>
 </div>
 </div>
 </div>
 <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
 <Button
 variant="outline"
 size="sm"
 onClick={() => {
 setEditingConfig(config);
 setConfigErrors([]);
 setShowConfigDialog(true);
 }}
 >
 <Edit className="h-4 w-4" />
 </Button>
 <Button
 variant="outline"
 size="sm"
 onClick={() => deleteTeeTimeConfig(config)}
 >
 <Trash2 className="h-4 w-4" />
 </Button>
 </div>
 </div>
 </CardContent>
 </Card>
 );
 })}

 {teeTimeConfigs.length === 0 && (
 <Card className="border-dashed">
 <CardContent className="flex items-center justify-center py-12">
 <div className="text-center">
 <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
 <h4 className="font-medium mb-2">No tee time configurations</h4>
 <p className="text-sm text-muted-foreground mb-4">
 Add your first tee time configuration to start accepting bookings
 </p>
 <Button 
 className={brandStyles.primary}
 onClick={() => {
 setEditingConfig(null);
 setConfigErrors([]);
 setShowConfigDialog(true);
 }}
 >
 <Plus className="h-4 w-4 mr-2" />
 Add Configuration
 </Button>
 </div>
 </CardContent>
 </Card>
 )}
 </div>
 </CardContent>
 </Card>

 {/* Configuration Preview Calendar */}
 {teeTimeConfigs.length > 0 && (
 <>
 <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <div>
 <CardTitle className="flex items-center gap-2">
 Ad-Hoc Availability Management
 {hasUnsavedChanges && (
 <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
 Unsaved Changes
 </Badge>
 )}
 </CardTitle>
 <CardDescription>
 Block specific time slots, entire days, or whole weeks for configurations. First click blocks the time slot, second click blocks the entire day, third click blocks the week for that configuration. Blue dots indicate existing bookings that may be cancelled if blocked.
 </CardDescription>
 </div>
 <div className="flex gap-2">
 {hasUnsavedChanges && (
 <>
 <Button
 variant="outline"
 size="sm"
 onClick={discardAdHocChanges}
 >
 Discard Changes
 </Button>
 <Button
 className={brandStyles.primary}
 size="sm"
 onClick={saveAdHocBlocks}
 >
 Save & Publish
 </Button>
 </>
 )}
 </div>
 </div>
 </CardHeader>
 <CardContent>
 {/* Preview Week Navigation */}
 <div className="flex items-center justify-center gap-4 mb-6">
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => navigatePreviewWeek('prev')}
 >
 <ChevronRight className="h-4 w-4 rotate-180" />
 </Button>
 
 <div className="text-center min-w-[200px]">
 <div className="font-medium">
 {formatDate(getPreviewWeekDates()[0])} - {formatDate(getPreviewWeekDates()[6])}
 </div>
 </div>
 
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => navigatePreviewWeek('next')}
 >
 <ChevronRight className="h-4 w-4" />
 </Button>
 </div>

 {/* Legend for booking indicators */}
 <div className="mb-4 p-3 bg-gray-50 rounded-lg">
 <div className="flex items-center gap-6 text-sm">
 <div className="flex items-center gap-2">
 <div className="relative w-4 h-4 bg-[#f5f5dc] border-[#2B8B3B] border rounded">
 </div>
 <span>Open</span>
 </div>
 <div className="flex items-center gap-2">
 <div className="relative w-4 h-4 bg-red-100 border-red-400 border rounded">
 </div>
 <span>Blocked</span>
 </div>
 <div className="flex items-center gap-2">
 <div className="relative w-4 h-4 bg-[#f5f5dc] border-[#2B8B3B] border rounded">
 <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
 </div>
 <span>Has Booking</span>
 </div>
 <div className="flex items-center gap-2">
 <div className="relative w-4 h-4 bg-[#f5f5dc] border-[#2B8B3B] border rounded">
 <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
 </div>
 <span>Unsaved Changes</span>
 </div>
 </div>
 </div>
 <div className="grid gap-1" style={{ gridTemplateColumns: `100px repeat(7, 1fr)` }}>
 {/* Header row with actual dates */}
 <div className="p-2 text-sm font-medium">Time</div>
 {getPreviewWeekDates().map((date, index) => (
 <div key={index} className="p-2 text-center">
 <div className="text-sm font-medium">{weekDays[index]}</div>
 <div className="text-xs text-muted-foreground">{formatDate(date)}</div>
 </div>
 ))}

 {/* Time slots */}
 {getAllTimeSlots().map((time, timeIndex) => (
 <div key={`row-${timeIndex}`} className="contents">
 <div className="p-2 text-xs font-medium">{time}</div>
 {getPreviewWeekDates().map((date, dayIndex) => {
 const day = weekDays[dayIndex];
 const dateKey = formatDateKey(date); // YYYY-MM-DD format without timezone shift
 const slotData = getConfigSlotData(day, time, dateKey);
 const isSlotBlocked = (pendingBlockedTimeSlots[dateKey] || []).includes(time);
 const isDayBlocked = pendingBlockedDays.includes(dateKey);
 
 // Check if week is blocked (applies to entire week, all configs)
 // Parse dateKey to create UTC date to avoid timezone shifts
 const [year, month, dayNum] = dateKey.split('-').map(Number);
 const parsedDateForWeek = new Date(Date.UTC(year, month - 1, dayNum));
 const weekStart = getWeekStart(parsedDateForWeek);
 const weekKey = formatUTCDateKey(weekStart);
 const isWeekBlocked = pendingBlockedWeeks.includes(weekKey);
 
 const isToday = date.toDateString() === new Date().toDateString();
 
 // Check if there's a booking at this slot
 const hasBooking = hasBookingAtSlot(dateKey, time);
 
 // If week-blocked, we should always render the slot even if no regular config applies
 const shouldRender = slotData.available || slotData.blocked || isSlotBlocked || isDayBlocked || isWeekBlocked;
 
 if (!shouldRender) {
 return (
 <div
 key={`${dayIndex}-${timeIndex}`}
 className="p-1 text-center border rounded bg-gray-100 border-gray-300 text-gray-500 min-h-[40px] flex items-center justify-center"
 >
 <span className="text-xs">—</span>
 </div>
 );
 }

 let bgColor = brandStyles.accent;
 let borderColor = 'border-[#2B8B3B]';
 let textColor = 'text-[#2B8B3B]';
 let displayText = slotData.configAbbrev || slotData.configName;
 let configForTooltip = slotData.configName || '';
 
 if (isWeekBlocked) {
 bgColor = 'bg-red-100';
 borderColor = 'border-red-400';
 textColor = 'text-red-700';
 displayText = 'WEEK';
 } else if (isDayBlocked) {
 bgColor = 'bg-red-100';
 borderColor = 'border-red-400';
 textColor = 'text-red-700';
 displayText = 'DAY';
 // Use the regular slot config or find an applicable one for the tooltip
 if (!configForTooltip) {
 const dayConfig = teeTimeConfigs.find(config => 
 config.daysOfWeek.includes(day) &&
 generateTimeSlots(config.startTime, config.endTime, config.teeTimeInterval).includes(time) &&
 config.status !== 'suspended'
 );
 configForTooltip = dayConfig ? dayConfig.name : 'Configuration';
 }
 } else if (isSlotBlocked) {
 bgColor = 'bg-red-100';
 borderColor = 'border-red-400';
 textColor = 'text-red-700';
 displayText = 'SLOT';
 }
 
 return (
 <div
 key={`${dayIndex}-${timeIndex}`}
 className={`
 p-1 text-center cursor-pointer border rounded transition-all duration-200 min-h-[40px] flex items-center justify-center relative
 ${bgColor} ${borderColor} ${textColor}
 hover:shadow-md hover:scale-105
 ${isToday ? 'ring-1 ring-[#2B8B3B] ring-opacity-50' : ''}
 ${hasUnsavedChanges && (isSlotBlocked || isDayBlocked || isWeekBlocked) ? 'ring-2 ring-orange-400 ring-opacity-50' : ''}
 `}
 onClick={() => handleTimeSlotClick(dateKey, time)}
 title={
 isWeekBlocked
 ? `Entire week blocked - Click to unblock${hasUnsavedChanges ? ' (Pending)' : ''}${hasBooking ? ' ⚠️ HAS BOOKING - May cancel existing reservation' : ''}`
 : isDayBlocked 
 ? `Entire day blocked for ${date.toLocaleDateString()} - Click to block entire week${hasUnsavedChanges ? ' (Pending)' : ''}${hasBooking ? ' ⚠️ HAS BOOKING - May cancel existing reservation' : ''}`
 : isSlotBlocked 
 ? `Time slot blocked for ${date.toLocaleDateString()} ${time} - Click to block entire day${hasUnsavedChanges ? ' (Pending)' : ''}${hasBooking ? ' ⚠️ HAS BOOKING - May cancel existing reservation' : ''}`
 : `${configForTooltip} available at ${time} - Click to block this time slot${hasBooking ? ' ⚠️ HAS BOOKING - May cancel existing reservation' : ''}`
 }
 >
 {hasUnsavedChanges && (isSlotBlocked || isDayBlocked || isWeekBlocked) && (
 <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-orange-500 rounded-full" title="Unsaved changes" />
 )}
 {hasBooking && (
 <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full" title="Has booking" />
 )}
 <div className="text-xs font-bold truncate">
 {displayText}
 </div>
 </div>
 );
 })}
 </div>
 ))}
 </div>
 
 {hasUnsavedChanges && (
 <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
 <div className="flex items-center gap-2 text-sm text-orange-800">
 <AlertCircle className="h-4 w-4" />
 <span className="font-medium">You have unsaved changes.</span>
 <span>Click "Save & Publish" to make these blocks visible to players, or "Discard Changes" to cancel.</span>
 </div>
 </div>
 )}
 </CardContent>
 </Card>

 </>
 )}

 {/* Tee Time Configuration Dialog */}
 <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
 <DialogContent className="max-w-2xl">
 <DialogHeader>
 <DialogTitle>
 {editingConfig ? 'Edit Tee Time Configuration' : 'Add Tee Time Configuration'}
 </DialogTitle>
 <DialogDescription>
 Configure availability and capacity for specific times and days
 </DialogDescription>
 </DialogHeader>
 
 {configErrors.length > 0 && (
 <div className="bg-red-50 border border-red-200 rounded-md p-3">
 <div className="flex items-center">
 <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
 <span className="text-sm font-medium text-red-700">Configuration Conflicts:</span>
 </div>
 <ul className="mt-2 text-sm text-red-600">
 {configErrors.map((error, index) => (
 <li key={index}>• {error}</li>
 ))}
 </ul>
 </div>
 )}

 <TeeTimeConfigForm
 config={editingConfig}
 onSubmit={addOrUpdateTeeTimeConfig}
 onCancel={() => {
 setShowConfigDialog(false);
 setEditingConfig(null);
 setConfigErrors([]);
 }}
 />
 </DialogContent>
 </Dialog>
 
 {/* Delete Confirmation Dialog */}
 <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>Delete Tee Time Configuration?</AlertDialogTitle>
 <AlertDialogDescription>
 Are you sure you want to delete "{configToDelete?.name}"? This action cannot be undone.
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel onClick={() => {
 setDeleteConfirmOpen(false);
 setConfigToDelete(null);
 }}>
 Cancel
 </AlertDialogCancel>
 <AlertDialogAction
 onClick={confirmDeleteConfig}
 className={brandStyles.primary}
 >
 Delete
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>

 {/* Confirm blocking slots that have bookings */}
 <AlertDialog open={blockConfirmOpen} onOpenChange={setBlockConfirmOpen}>
 <AlertDialogContent>
 <AlertDialogHeader>
 <AlertDialogTitle>Block time slots with active bookings?</AlertDialogTitle>
 <AlertDialogDescription asChild>
 <div>
 <p className="mb-3">
 The following time slots have existing bookings. Blocking them will automatically cancel those bookings and notify the players.
 </p>
 <ul className="space-y-1 text-sm border rounded-md p-3 bg-amber-50 border-amber-200">
 {blockBookingConflicts.map((c, i) => {
 const [y, m, d] = c.date.split('-').map(Number);
 const dateLabel = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
 return (
 <li key={i} className="flex items-center gap-2 text-amber-800">
 <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
 <span><strong>{dateLabel}</strong> at {c.time} — {c.playerName}</span>
 </li>
 );
 })}
 </ul>
 <p className="mt-3 text-sm font-medium text-red-600">
 Are you sure you want to proceed? This cannot be undone.
 </p>
 </div>
 </AlertDialogDescription>
 </AlertDialogHeader>
 <AlertDialogFooter>
 <AlertDialogCancel onClick={() => setBlockConfirmOpen(false)}>
 Go Back
 </AlertDialogCancel>
 <AlertDialogAction
 onClick={confirmSaveAdHocBlocks}
 className="bg-red-600 hover:bg-red-700 text-white"
 >
 Block & Cancel Bookings
 </AlertDialogAction>
 </AlertDialogFooter>
 </AlertDialogContent>
 </AlertDialog>
 </div>
 );
 };

 const renderBookingsManagement = () => (
 <>
 <div className="space-y-6">
 <Tabs value={bookingsActiveTab} onValueChange={(value) => setBookingsActiveTab(value as 'table' | 'calendar')}>
 <div className="flex justify-between items-center">
 <TabsList>
 <TabsTrigger value="table">Table View</TabsTrigger>
 <TabsTrigger value="calendar">Calendar View</TabsTrigger>
 </TabsList>
 <Button className={brandStyles.primary} onClick={() => { resetManualBookingForm(); setManualBookingOpen(true); }}>
 <Plus className="h-4 w-4 mr-2" />
 Manual Booking
 </Button>
 </div>

 <TabsContent value="table">
 <Card>
 <CardHeader>
 <CardTitle>All Bookings</CardTitle>
 <CardDescription>Manage player reservations and check-ins</CardDescription>
 </CardHeader>
 <CardContent>
 {/* Search and Filter Controls */}
 <div className="mb-6 space-y-4">
 <div className="flex gap-4 flex-wrap">
 {/* Search */}
 <div className="flex-1 min-w-[200px]">
 <Input
 placeholder="Search by name or email..."
 value={bookingsSearchTerm}
 onChange={(e) => setBookingsSearchTerm(e.target.value)}
 className="max-w-sm"
 />
 </div>
 
 {/* Date Filter */}
 <Select value={bookingsDateFilter} onValueChange={setBookingsDateFilter}>
 <SelectTrigger className="w-[150px]">
 <SelectValue placeholder="Filter by date" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All dates</SelectItem>
 <SelectItem value="range">Date range</SelectItem>
 <SelectItem value="today">Today</SelectItem>
 <SelectItem value="tomorrow">Tomorrow</SelectItem>
 <SelectItem value="this-week">This week</SelectItem>
 <SelectItem value="next-week">Next week</SelectItem>
 </SelectContent>
 </Select>

 {/* Date Range Inputs */}
 {bookingsDateFilter === 'range' && (
 <>
 <Input
 type="date"
 placeholder="Start date"
 value={bookingsDateRangeStart}
 onChange={(e) => setBookingsDateRangeStart(e.target.value)}
 className="w-[140px]"
 />
 <Input
 type="date"
 placeholder="End date"
 value={bookingsDateRangeEnd}
 onChange={(e) => setBookingsDateRangeEnd(e.target.value)}
 className="w-[140px]"
 />
 </>
 )}

 {/* Status Filter */}
 <Select value={bookingsStatusFilter} onValueChange={setBookingsStatusFilter}>
 <SelectTrigger className="w-[130px]">
 <SelectValue placeholder="Status" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All statuses</SelectItem>
 <SelectItem value="confirmed">Confirmed</SelectItem>
 <SelectItem value="requested">Pending Request</SelectItem>
 <SelectItem value="cancelled">Cancelled</SelectItem>
 <SelectItem value="refunded">Refunded</SelectItem>
 </SelectContent>
 </Select>

 {/* Clear Filters */}
 {(bookingsSearchTerm || (bookingsDateFilter && bookingsDateFilter !== 'all') || bookingsDateRangeStart || bookingsDateRangeEnd || (bookingsStatusFilter && bookingsStatusFilter !== 'all')) && (
 <Button 
 variant="outline" 
 onClick={() => {
 setBookingsSearchTerm('');
 setBookingsDateFilter('all');
 setBookingsDateRangeStart('');
 setBookingsDateRangeEnd('');
 setBookingsStatusFilter('all');
 }}
 >
 Clear Filters
 </Button>
 )}
 </div>

 {/* Results Count */}
 <div className="text-sm text-muted-foreground">
 Showing {getFilteredBookings().length} of {bookings.length} bookings
 {(bookingsSearchTerm || (bookingsDateFilter && bookingsDateFilter !== 'all') || bookingsDateRangeStart || bookingsDateRangeEnd || (bookingsStatusFilter && bookingsStatusFilter !== 'all')) && (
 <span className="ml-2 text-[#2B8B3B]">• Filters applied</span>
 )}
 </div>
 </div>
 {getFilteredBookings().length === 0 ? (
 <div className="text-center py-12">
 <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
 <h3 className="font-medium mb-2">No bookings found</h3>
 <p className="text-sm text-muted-foreground mb-4">
 {bookingsSearchTerm || (bookingsDateFilter && bookingsDateFilter !== 'all') || bookingsDateRangeStart || bookingsDateRangeEnd || (bookingsStatusFilter && bookingsStatusFilter !== 'all')
 ? 'Try adjusting your search criteria or filters'
 : 'No bookings have been made yet'
 }
 </p>
 {(bookingsSearchTerm || (bookingsDateFilter && bookingsDateFilter !== 'all') || bookingsDateRangeStart || bookingsDateRangeEnd || (bookingsStatusFilter && bookingsStatusFilter !== 'all')) && (
 <Button 
 variant="outline"
 onClick={() => {
 setBookingsSearchTerm('');
 setBookingsDateFilter('all');
 setBookingsDateRangeStart('');
 setBookingsDateRangeEnd('');
 setBookingsStatusFilter('all');
 }}
 >
 Clear All Filters
 </Button>
 )}
 </div>
 ) : (
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Confirmation #</TableHead>
 <TableHead
 className="cursor-pointer select-none hover:text-foreground"
 onClick={() => handleBookingsSort('playerName')}
 >
 Player {bookingsSortKey === 'playerName' ? (bookingsSortDir === 'asc' ? '↑' : '↓') : <span className="text-muted-foreground/40">↕</span>}
 </TableHead>
 <TableHead
 className="cursor-pointer select-none hover:text-foreground"
 onClick={() => handleBookingsSort('slotStart')}
 >
 Tee Time {bookingsSortKey === 'slotStart' ? (bookingsSortDir === 'asc' ? '↑' : '↓') : <span className="text-muted-foreground/40">↕</span>}
 </TableHead>
 <TableHead
 className="cursor-pointer select-none hover:text-foreground"
 onClick={() => handleBookingsSort('createdAt')}
 >
 Created {bookingsSortKey === 'createdAt' ? (bookingsSortDir === 'asc' ? '↑' : '↓') : <span className="text-muted-foreground/40">↕</span>}
 </TableHead>
 <TableHead>Players</TableHead>
 <TableHead
 className="cursor-pointer select-none hover:text-foreground"
 onClick={() => handleBookingsSort('amount')}
 >
 Amount {bookingsSortKey === 'amount' ? (bookingsSortDir === 'asc' ? '↑' : '↓') : <span className="text-muted-foreground/40">↕</span>}
 </TableHead>
 <TableHead
 className="cursor-pointer select-none hover:text-foreground"
 onClick={() => handleBookingsSort('status')}
 >
 Status {bookingsSortKey === 'status' ? (bookingsSortDir === 'asc' ? '↑' : '↓') : <span className="text-muted-foreground/40">↕</span>}
 </TableHead>
 <TableHead className="w-[80px]">Actions</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {getFilteredBookings().map((booking) => (
 <TableRow key={booking.id}>
 <TableCell>
 <p className="font-mono text-sm font-medium">{(booking as any).confirmationNumber || '—'}</p>
 </TableCell>
 <TableCell>
 <div>
 <p className="font-medium">{booking.playerName}</p>
 <p className="text-sm text-muted-foreground">{booking.email}</p>
 </div>
 </TableCell>
 <TableCell>
 <div>
 <p>{getBookingDateTime(booking).date}</p>
 <p className="text-sm text-muted-foreground">{getBookingDateTime(booking).time}</p>
 </div>
 </TableCell>
 <TableCell>
 <div>
 <p>{booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : '—'}</p>
 <p className="text-sm text-muted-foreground">{booking.createdAt ? new Date(booking.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</p>
 </div>
 </TableCell>
 <TableCell>{booking.players}</TableCell>
 <TableCell>${booking.amount}</TableCell>
 <TableCell>
 <Badge
 variant="outline"
 className={
   booking.status === 'confirmed' ? 'bg-green-100 text-green-800 border-green-300' :
   booking.status === 'requested' ? 'bg-orange-50 text-orange-700 border-orange-300' :
   booking.status === 'cancelled' ? 'bg-red-100 text-red-700 border-red-300' :
   booking.status === 'refunded' ? 'bg-gray-100 text-gray-600 border-gray-300' : ''
 }
 >
 {booking.status === 'confirmed' && <CheckCircle className="h-3 w-3 mr-1" />}
 {booking.status === 'requested' && <Clock className="h-3 w-3 mr-1" />}
 {booking.status === 'cancelled' && <X className="h-3 w-3 mr-1" />}
 {booking.status === 'refunded' && <RotateCcw className="h-3 w-3 mr-1" />}
 {booking.status === 'confirmed' ? 'Confirmed' : booking.status === 'requested' ? 'Pending Request' : booking.status === 'refunded' ? 'Refunded' : booking.status === 'cancelled' ? 'Cancelled' : booking.status}
 </Badge>
 </TableCell>
 <TableCell>
 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <Button variant="outline" size="sm" className="w-8 h-8 p-0">
 <MoreVertical className="h-4 w-4" />
 </Button>
 </DropdownMenuTrigger>
 <DropdownMenuContent align="end" className="w-48">
 {/* Request-specific actions - ONLY for request status */}
 {booking.status === 'requested' ? (
 <>
 <DropdownMenuItem 
 onClick={() => handleBookingRequest(booking.id, 'accept')}
 className="text-green-600 focus:text-green-600"
 >
 <CheckCircle className="h-4 w-4 mr-2" />
 Accept Request
 </DropdownMenuItem>
 <DropdownMenuItem 
 onClick={() => handleBookingRequest(booking.id, 'reject')}
 className="text-red-600 focus:text-red-600"
 >
 <X className="h-4 w-4 mr-2" />
 Reject Request
 </DropdownMenuItem>
 </>
 ) : (
 <>
 {/* Edit Booking - for non-request bookings */}
 <DropdownMenuItem 
 onClick={() => {
 setSelectedBookingForEdit(booking);
 setEditDialogOpen(true);
 }}
 >
 <Edit className="h-4 w-4 mr-2" />
 Edit Booking
 </DropdownMenuItem>
 
 {/* Cancel booking option for active bookings - for non-request bookings */}
 {booking.status !== 'cancelled' && booking.status !== 'refunded' && (
 <>
 <DropdownMenuSeparator />
 <DropdownMenuItem 
 onClick={() => {
 const { date, time } = getBookingDateTime(booking);
 if (confirm(`Are you sure you want to cancel ${booking.playerName}'s booking for ${date} at ${time}?`)) {
 cancelBooking(booking.id);
 }
 }}
 className="text-red-600 focus:text-red-600"
 >
 <X className="h-4 w-4 mr-2" />
 Cancel Booking
 </DropdownMenuItem>
 </>
 )}
 </>
 )}
 </DropdownMenuContent>
 </DropdownMenu>
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 )}
 </CardContent>
 </Card>

 {/* Edit Booking Dialog */}
 <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Edit Booking</DialogTitle>
 <DialogDescription>
 {selectedBookingForEdit && `Modify booking details for ${selectedBookingForEdit.playerName}`}
 </DialogDescription>
 </DialogHeader>
 {selectedBookingForEdit && (
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div>
 <span className="font-medium">Confirmation #:</span> <span className="font-mono">{(selectedBookingForEdit as any).confirmationNumber || '—'}</span>
 </div>
 <div>
 <span className="font-medium">Player:</span> {selectedBookingForEdit.playerName}
 </div>
 <div>
 <span className="font-medium">Email:</span> {selectedBookingForEdit.email}
 </div>
 <div>
 <span className="font-medium">Date & Time:</span> {getBookingDateTime(selectedBookingForEdit).date} at {getBookingDateTime(selectedBookingForEdit).time}
 </div>
 <div>
 <span className="font-medium">Players:</span> {selectedBookingForEdit.players}
 </div>
 <div>
 <span className="font-medium">Amount:</span> ${selectedBookingForEdit.amount}
 </div>
 </div>
 <div className="space-y-2">
 <Label>Status</Label>
 <Select defaultValue={selectedBookingForEdit.status} disabled={['cancelled', 'refunded'].includes(selectedBookingForEdit.status)}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="confirmed">Confirmed</SelectItem>
 <SelectItem value="requested">Pending Request</SelectItem>
 <SelectItem value="cancelled">Cancelled</SelectItem>
 <SelectItem value="refunded">Refunded</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="flex gap-2">
 <Button className={brandStyles.primary}>Save Changes</Button>
 <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>
 </TabsContent>

 <TabsContent value="calendar">
 <Card>
 <CardHeader>
 <CardTitle className="mb-4">Weekly Schedule Overview</CardTitle>
 
 {/* Simplified Week Navigation */}
 <div className="flex items-center justify-center gap-4 mb-4">
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => navigateWeek('prev')}
 >
 <ChevronRight className="h-4 w-4 rotate-180" />
 </Button>
 
 <div className="text-center min-w-[200px]">
 <div className="font-medium text-lg">
 {formatDate(getCurrentWeekDates()[0])} - {formatDate(getCurrentWeekDates()[6])}
 </div>
 </div>
 
 <Button 
 variant="outline" 
 size="sm"
 onClick={() => navigateWeek('next')}
 >
 <ChevronRight className="h-4 w-4" />
 </Button>
 </div>
 </CardHeader>
 <CardContent>
 {/* Bookings Calendar Legend */}
 <div className="mb-6 p-4 bg-gray-50 rounded-lg">
 <div className="flex items-center gap-6 text-sm">
 <div className="flex items-center gap-2">
 <div className="w-4 h-4 bg-[#f5f5dc] border-[#2B8B3B] border rounded"></div>
 <span>Available</span>
 </div>
 <div className="flex items-center gap-2">
 <div className="w-4 h-4 bg-red-100 border-red-400 border rounded"></div>
 <span>Booked</span>
 </div>
 <div className="flex items-center gap-2">
 <div className="w-4 h-4 bg-gray-100 border-gray-300 border rounded"></div>
 <span>Not Available</span>
 </div>
 </div>
 </div>

 {getAllTimeSlots().length === 0 ? (
 <div className="text-center py-12 text-muted-foreground">
 <Calendar className="h-12 w-12 mx-auto mb-4" />
 <p>No tee time configurations set up yet.</p>
 <p>Go to Tee Time Setup to add your first configuration.</p>
 </div>
 ) : (
 <div className="grid gap-2" style={{ gridTemplateColumns: `120px repeat(7, 1fr)` }}>
 {/* Header row with dates */}
 <div className="p-3 font-medium">Time</div>
 {getCurrentWeekDates().map((date, index) => (
 <div key={index} className="p-3 text-center">
 <div className="font-medium">{weekDays[index]}</div>
 <div className="text-sm text-muted-foreground">{formatDate(date)}</div>
 </div>
 ))}

 {/* Time slots */}
 {getAllTimeSlots().map((time, timeIndex) => (
 <div key={`row-${timeIndex}`} className="contents">
 <div className="p-3 text-sm font-medium">{time}</div>
 {getCurrentWeekDates().map((date, dayIndex) => {
 const day = weekDays[dayIndex];
 const slotId = `${day}-${time}`;
 const slotData = getBookingSlotData(day, time, date);

 if (!slotData.available) {
 return (
 <div
 key={slotId}
 className="p-3 text-center border rounded bg-gray-100 border-gray-300 text-gray-500"
 >
 —
 </div>
 );
 }

 const bgColor = slotData.isBooked ? 'bg-red-100' : brandStyles.accent;
 const borderColor = slotData.isBooked ? 'border-red-400' : 'border-[#2B8B3B]';
 const textColor = slotData.isBooked ? 'text-red-700' : 'text-[#2B8B3B]';

 const isToday = date.toDateString() === new Date().toDateString();

 return (
 <div
 key={slotId}
 className={`p-2 text-center border rounded ${bgColor} ${borderColor} ${textColor} ${isToday ? 'ring-2 ring-[#2B8B3B] ring-opacity-50' : ''}`}
 title={slotData.isBooked ? `${slotData.playerName || 'Booked'} · ${slotData.players} player${slotData.players !== 1 ? 's' : ''}` : 'Available'}
 >
 {slotData.isBooked ? (
 <>
 <div className="text-xs font-semibold truncate">{slotData.playerName || 'Booked'}</div>
 <div className="text-xs opacity-75">{slotData.players}p</div>
 </>
 ) : (
 <div className="text-xs opacity-50">Open</div>
 )}
 </div>
 );
 })}
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 </TabsContent>
 </Tabs>
 </div>

 {/* Manual Booking Modal */}
 <Dialog open={manualBookingOpen} onOpenChange={(open) => { if (!manualBookingSubmitting) { setManualBookingOpen(open); if (!open) resetManualBookingForm(); } }}>
 <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Add Manual Booking</DialogTitle>
 <DialogDescription>Create a booking on behalf of a player. If the email doesn't exist in the system, a new player account will be created.</DialogDescription>
 </DialogHeader>
 <form onSubmit={handleManualBookingSubmit} className="space-y-5 pt-1">
 {/* Player Info */}
 <div>
 <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Player Information</p>
 <div className="grid grid-cols-2 gap-3 mb-3">
 <div className="space-y-1">
 <Label htmlFor="mb-first">First Name <span className="text-red-500">*</span></Label>
 <Input id="mb-first" value={mbFirstName} onChange={e => setMbFirstName(e.target.value)} placeholder="Jane" disabled={manualBookingSubmitting} />
 </div>
 <div className="space-y-1">
 <Label htmlFor="mb-last">Last Name <span className="text-red-500">*</span></Label>
 <Input id="mb-last" value={mbLastName} onChange={e => setMbLastName(e.target.value)} placeholder="Smith" disabled={manualBookingSubmitting} />
 </div>
 </div>
 <div className="space-y-1 mb-3">
 <Label htmlFor="mb-email">Email <span className="text-red-500">*</span></Label>
 <Input id="mb-email" type="email" value={mbEmail} onChange={e => setMbEmail(e.target.value)} placeholder="jane@example.com" disabled={manualBookingSubmitting} />
 </div>
 <div className="space-y-1">
 <Label htmlFor="mb-phone">Phone</Label>
 <Input id="mb-phone" type="tel" value={mbPhone} onChange={e => setMbPhone(e.target.value)} placeholder="(503) 555-0100" disabled={manualBookingSubmitting} />
 </div>
 </div>

 {/* Booking Details */}
 <div className="border-t pt-4">
 <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Booking Details</p>
 <div className="grid grid-cols-2 gap-3 mb-3">
 <div className="space-y-1">
 <Label htmlFor="mb-date">Date <span className="text-red-500">*</span></Label>
 <Input id="mb-date" type="date" value={mbDate} onChange={e => { setMbDate(e.target.value); setMbTime(''); setMbConfigId(''); }} disabled={manualBookingSubmitting} />
 </div>
 <div className="space-y-1">
 <Label htmlFor="mb-qty">Number of Players <span className="text-red-500">*</span></Label>
 <Input id="mb-qty" type="number" min="1" max="20" value={mbQty} onChange={e => setMbQty(e.target.value)} disabled={manualBookingSubmitting} />
 </div>
 </div>
 <div className="space-y-1 mb-3">
 <Label htmlFor="mb-time">Time Slot <span className="text-red-500">*</span></Label>
 {mbDate ? (() => {
 const slots = getTimeSlotsForDate(mbDate);
 return slots.length > 0 ? (
 <Select value={mbTime} onValueChange={val => { setMbTime(val); const s = slots.find(sl => sl.time === val); if (s) setMbConfigId(s.configId); else setMbConfigId(''); }} disabled={manualBookingSubmitting}>
 <SelectTrigger><SelectValue placeholder="Select a time slot" /></SelectTrigger>
 <SelectContent style={{ zIndex: 9999 }}>
 {slots.map((s, i) => (
 <SelectItem key={i} value={s.time}>{s.time} — {s.configName} {s.price > 0 ? `($${s.price}${s.pricingType === 'per-player' ? '/player' : '/round'})` : ''}</SelectItem>
 ))}
 <SelectItem value="__custom__">Other (enter manually)</SelectItem>
 </SelectContent>
 </Select>
 ) : (
 <div className="space-y-1">
 <p className="text-xs text-amber-600">No tee time configs for this day. Enter time manually.</p>
 <Input value={mbCustomTime} onChange={e => setMbCustomTime(e.target.value)} placeholder="e.g. 10:00 AM" disabled={manualBookingSubmitting} />
 </div>
 );
 })() : <Input value="" placeholder="Select a date first" disabled />}
 {mbTime === '__custom__' && (
 <Input className="mt-2" value={mbCustomTime} onChange={e => setMbCustomTime(e.target.value)} placeholder="e.g. 10:00 AM" disabled={manualBookingSubmitting} />
 )}
 </div>
 </div>

 {/* Pricing — read-only, derived from selected tee time slot */}
 {(() => {
 const slots = getTimeSlotsForDate(mbDate);
 const matched = slots.find(s => s.time === mbTime);
 if (!matched || matched.price === 0) return null;
 const qty = parseInt(mbQty, 10) || 1;
 const total = matched.pricingType === 'per-player' ? matched.price * qty : matched.price;
 return (
 <div className="border-t pt-4 flex items-center justify-between">
 <span className="text-sm text-gray-600">
 Total ({matched.pricingType === 'per-player' ? `$${matched.price} × ${qty} player${qty !== 1 ? 's' : ''}` : `flat rate`})
 </span>
 <span className="text-lg font-semibold text-gray-900">${total.toFixed(2)}</span>
 </div>
 );
 })()}

 {manualBookingError && <p className="text-sm text-red-600">{manualBookingError}</p>}

 <DialogFooter>
 <Button type="button" variant="outline" onClick={() => setManualBookingOpen(false)} disabled={manualBookingSubmitting}>Cancel</Button>
 <Button type="submit" className={brandStyles.primary} disabled={manualBookingSubmitting}>
 {manualBookingSubmitting ? 'Creating…' : 'Create Booking'}
 </Button>
 </DialogFooter>
 </form>
 </DialogContent>
 </Dialog>
 </>
 );

 const renderInsights = () => {
  const chartData = insightsData?.chartData || [];
  const kpis = insightsData?.kpis;
  // Shadow module-scope mocks with real data when available
  const reviewData = insightsData?.reviewData ?? {
   totalReviews: 0,
   averageRating: 0,
   ratingTrend: { direction: 'stable' as const, percentage: 0, previousRating: 0 },
   ratingDistribution: [5, 4, 3, 2, 1].map(r => ({ rating: r, count: 0, percentage: 0 })),
  };
  const structuredFeedback = insightsData?.structuredFeedback ?? {
   whatWentWell: [] as { text: string; count: number; percentage: number }[],
   areasForImprovement: [] as { text: string; count: number; percentage: number }[],
  };

  const trendPct = (current: number, prev: number) =>
   prev === 0 ? null : Math.round((current - prev) / prev * 100);

  const bookingTrend = kpis ? trendPct(kpis.currentMonthBookings, kpis.prevMonthBookings) : null;
  const revenueTrend = kpis ? trendPct(kpis.currentMonthRevenue, kpis.prevMonthRevenue) : null;
  const playersTrend = kpis ? trendPct(kpis.totalPlayers, kpis.prevMonthPlayers) : null;

  const fmtCurrency = (v: number) =>
   new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

  const TrendBadge = ({ pct }: { pct: number | null }) => {
   if (pct === null) return <span className="text-sm text-gray-400 ml-1">No prior data</span>;
   const up = pct >= 0;
   return (
    <div className={`flex items-center flex-shrink-0 ${up ? 'text-green-600' : 'text-red-600'}`}>
     {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
     <span className="text-sm ml-1">{up ? '+' : ''}{pct}%</span>
    </div>
   );
  };

  return (
 <div className="space-y-6">
 {/* Key Performance Metrics */}
 <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
 <Card>
 <CardContent className="p-6">
 <div className="flex items-center justify-between">
 <div className="w-full">
 <p className="text-sm text-muted-foreground">Average Rating</p>
 <div className="flex items-baseline gap-3 mt-1 flex-wrap">
 <div className="flex items-center gap-2 flex-shrink-0">
 <p className="text-3xl font-bold text-[#2B8B3B]">{reviewData.averageRating}</p>
 <div className="flex">
 {[1, 2, 3, 4, 5].map((star) => (
 <Star 
 key={star} 
 className={`h-4 w-4 ${star <= Math.round(reviewData.averageRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
 />
 ))}
 </div>
 </div>
 <div className={`flex items-center flex-shrink-0 ${reviewData.ratingTrend.direction === 'up' ? 'text-green-600' : reviewData.ratingTrend.direction === 'down' ? 'text-red-600' : 'text-gray-600'}`}>
 {reviewData.ratingTrend.direction === 'up' && <TrendingUp className="h-4 w-4" />}
 {reviewData.ratingTrend.direction === 'down' && <TrendingDown className="h-4 w-4" />}
 {reviewData.ratingTrend.direction === 'stable' && <div className="h-4 w-4 border-t-2 border-current" />}
 <span className="text-sm ml-1">
 {reviewData.ratingTrend.direction === 'stable' ? '0.0%' : `${reviewData.ratingTrend.direction === 'up' ? '+' : ''}${reviewData.ratingTrend.percentage}%`}
 </span>
 </div>
 </div>
 <p className="text-xs text-muted-foreground mt-1">
 L4W trend • {reviewData.totalReviews} total reviews
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardContent className="p-6">
 <div className="flex items-center justify-between">
 <div className="w-full">
 <p className="text-sm text-muted-foreground">Monthly Bookings</p>
 <div className="flex items-baseline gap-3 mt-1 flex-wrap">
 <p className="text-3xl font-bold flex-shrink-0">{kpis?.currentMonthBookings ?? 0}</p>
 <TrendBadge pct={bookingTrend} />
 </div>
 <p className="text-xs text-muted-foreground mt-1">vs. last month</p>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardContent className="p-6">
 <div className="flex items-center justify-between">
 <div className="w-full">
 <p className="text-sm text-muted-foreground">Monthly Revenue</p>
 <div className="flex items-baseline gap-3 mt-1 flex-wrap">
 <p className="text-3xl font-bold flex-shrink-0">{fmtCurrency(kpis?.currentMonthRevenue ?? 0)}</p>
 <TrendBadge pct={revenueTrend} />
 </div>
 <p className="text-xs text-muted-foreground mt-1">vs. last month</p>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardContent className="p-6">
 <div className="flex items-center justify-between">
 <div className="w-full">
 <p className="text-sm text-muted-foreground">Total Players</p>
 <div className="flex items-baseline gap-3 mt-1 flex-wrap">
 <p className="text-3xl font-bold flex-shrink-0">{kpis?.totalPlayers ?? 0}</p>
 <TrendBadge pct={playersTrend} />
 </div>
 <p className="text-xs text-muted-foreground mt-1">vs. last month</p>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Performance Trends */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <Card>
 <CardHeader>
 <CardTitle>Booking Trends</CardTitle>
 <CardDescription>Monthly booking volume over the past 6 months</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="h-80">
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={chartData}>
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="month" stroke="#666" />
 <YAxis stroke="#666" />
 <Tooltip 
 contentStyle={{ 
 backgroundColor: '#F5F5DC', 
 border: '1px solid #2B8B3B',
 borderRadius: '8px'
 }} 
 />
 <Line 
 type="monotone" 
 dataKey="bookings" 
 stroke="#2B8B3B" 
 strokeWidth={3}
 dot={{ fill: '#2B8B3B', strokeWidth: 2, r: 4 }}
 />
 </LineChart>
 </ResponsiveContainer>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle>Revenue Growth</CardTitle>
 <CardDescription>Monthly revenue trends and performance</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="h-80">
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={chartData}>
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="month" stroke="#666" />
 <YAxis stroke="#666" />
 <Tooltip 
 contentStyle={{ 
 backgroundColor: '#F5F5DC', 
 border: '1px solid #2B8B3B',
 borderRadius: '8px'
 }} 
 formatter={(value) => [`${value}`, 'Revenue']}
 />
 <Bar dataKey="revenue" fill="#2B8B3B" radius={[4, 4, 0, 0]} />
 </BarChart>
 </ResponsiveContainer>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Rating Analysis */}
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
 <Card>
 <CardHeader>
 <CardTitle>Rating Distribution</CardTitle>
 <CardDescription>Breakdown of all player ratings</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {reviewData.ratingDistribution.map((rating) => (
 <div key={rating.rating} className="flex items-center gap-3">
 <div className="flex items-center gap-1 w-12">
 <span className="text-sm font-medium">{rating.rating}</span>
 <Star className="h-3 w-3 text-yellow-400 fill-current" />
 </div>
 <div className="flex-1">
 <div className="w-full bg-gray-200 rounded-full h-2">
 <div 
 className="bg-[#2B8B3B] h-2 rounded-full transition-all duration-300"
 style={{ width: `${rating.percentage}%` }}
 />
 </div>
 </div>
 <div className="text-sm text-muted-foreground w-16 text-right">
 {rating.count} ({rating.percentage}%)
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle>Average Rating Trend</CardTitle>
 <CardDescription>Rating performance over time</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="h-48">
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={chartData}>
 <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
 <XAxis dataKey="month" stroke="#666" />
 <YAxis domain={[3.8, 4.6]} stroke="#666" />
 <Tooltip 
 contentStyle={{ 
 backgroundColor: '#F5F5DC', 
 border: '1px solid #2B8B3B',
 borderRadius: '8px'
 }}
 formatter={(value) => [`${value} stars`, 'Average Rating']}
 />
 <Line 
 type="monotone" 
 dataKey="avgRating" 
 stroke="#2B8B3B" 
 strokeWidth={3}
 dot={{ fill: '#2B8B3B', strokeWidth: 2, r: 4 }}
 />
 </LineChart>
 </ResponsiveContainer>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle>Rating Summary</CardTitle>
 <CardDescription>Quick overview of your ratings</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div className="text-center">
 <div className="text-4xl font-bold text-[#2B8B3B] mb-2">
 {reviewData.averageRating}
 </div>
 <div className="flex justify-center mb-2">
 {[1, 2, 3, 4, 5].map((star) => (
 <Star 
 key={star} 
 className={`h-6 w-6 ${star <= Math.round(reviewData.averageRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
 />
 ))}
 </div>
 <p className="text-sm text-muted-foreground">
 Based on {reviewData.totalReviews} reviews
 </p>
 </div>
 
 <Separator />
 
 <div className="space-y-2">
 <div className="flex justify-between text-sm">
 <span>5 & 4 star reviews:</span>
 <span className="font-medium text-green-600">
 {((reviewData.ratingDistribution[0].percentage + reviewData.ratingDistribution[1].percentage)).toFixed(1)}%
 </span>
 </div>
 <div className="flex justify-between text-sm">
 <span>3 star reviews:</span>
 <span className="font-medium text-yellow-600">
 {reviewData.ratingDistribution[2].percentage}%
 </span>
 </div>
 <div className="flex justify-between text-sm">
 <span>1 & 2 star reviews:</span>
 <span className="font-medium text-red-600">
 {(reviewData.ratingDistribution[3].percentage + reviewData.ratingDistribution[4].percentage).toFixed(1)}%
 </span>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Structured Feedback Analysis */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <Card>
 <CardHeader>
 <CardTitle className="text-green-700">What Went Well</CardTitle>
 <CardDescription>Positive feedback from your players</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {structuredFeedback.whatWentWell
 .sort((a, b) => b.count - a.count)
 .slice(0, 6)
 .map((item, index) => (
 <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
 <div className="flex items-center gap-3">
 <div className="w-2 h-2 bg-green-500 rounded-full" />
 <span className="text-sm font-medium text-green-800">{item.text}</span>
 </div>
 <div className="flex items-center gap-2">
 <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
 {item.count} mentions
 </Badge>
 <span className="text-sm text-green-600 font-medium">
 {item.percentage}%
 </span>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-orange-700">Areas for Improvement</CardTitle>
 <CardDescription>Feedback to help you enhance the experience</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {structuredFeedback.areasForImprovement
 .sort((a, b) => b.count - a.count)
 .slice(0, 6)
 .map((item, index) => (
 <div key={index} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
 <div className="flex items-center gap-3">
 <div className="w-2 h-2 bg-orange-500 rounded-full" />
 <span className="text-sm font-medium text-orange-800">{item.text}</span>
 </div>
 <div className="flex items-center gap-2">
 <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
 {item.count} mentions
 </Badge>
 <span className="text-sm text-orange-600 font-medium">
 {item.percentage}%
 </span>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Actionable Insights */}
 <Card>
 <CardHeader>
 <CardTitle>Actionable Insights</CardTitle>
 <CardDescription>Recommendations based on your feedback data</CardDescription>
 </CardHeader>
 <CardContent>
 {reviewData.totalReviews === 0 ? (
 <p className="text-sm text-muted-foreground text-center py-6">No feedback yet — insights will appear once players submit reviews.</p>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {structuredFeedback.areasForImprovement[0] && (
 <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <div className="w-3 h-3 bg-blue-500 rounded-full" />
 <span className="font-medium text-blue-800">Priority Action</span>
 </div>
 <p className="text-sm text-blue-700">
 <strong>{structuredFeedback.areasForImprovement[0].text}</strong> — mentioned by {structuredFeedback.areasForImprovement[0].count} {structuredFeedback.areasForImprovement[0].count === 1 ? 'player' : 'players'} ({structuredFeedback.areasForImprovement[0].percentage}%).
 </p>
 </div>
 )}
 {structuredFeedback.whatWentWell[0] && (
 <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <div className="w-3 h-3 bg-yellow-500 rounded-full" />
 <span className="font-medium text-yellow-800">Maintain Strengths</span>
 </div>
 <p className="text-sm text-yellow-700">
 <strong>{structuredFeedback.whatWentWell[0].text}</strong> — your most-praised aspect ({structuredFeedback.whatWentWell[0].count} {structuredFeedback.whatWentWell[0].count === 1 ? 'mention' : 'mentions'}, {structuredFeedback.whatWentWell[0].percentage}%). Keep it up.
 </p>
 </div>
 )}
 {structuredFeedback.areasForImprovement[1] && (
 <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <div className="w-3 h-3 bg-green-500 rounded-full" />
 <span className="font-medium text-green-800">Growth Opportunity</span>
 </div>
 <p className="text-sm text-green-700">
 <strong>{structuredFeedback.areasForImprovement[1].text}</strong> — {structuredFeedback.areasForImprovement[1].count} {structuredFeedback.areasForImprovement[1].count === 1 ? 'player' : 'players'} flagged this ({structuredFeedback.areasForImprovement[1].percentage}%).
 </p>
 </div>
 )}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Detailed Analytics */}
 <Card>
 <CardHeader>
 <CardTitle>Monthly Performance Comparison</CardTitle>
 <CardDescription>Comprehensive view of key metrics over time</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="overflow-x-auto">
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Month</TableHead>
 <TableHead>Bookings</TableHead>
 <TableHead>Revenue</TableHead>
 <TableHead>Players</TableHead>
 <TableHead>Growth</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {chartData.length === 0 ? (
 <TableRow>
 <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No data yet</TableCell>
 </TableRow>
 ) : chartData.map((month, index) => {
 const prevMonth = index > 0 ? chartData[index - 1] : null;
 const bookingGrowth = prevMonth && prevMonth.bookings > 0
 ? ((month.bookings - prevMonth.bookings) / prevMonth.bookings * 100).toFixed(1)
 : null;
 const isPositiveGrowth = Number(bookingGrowth) > 0;

 return (
 <TableRow key={month.month}>
 <TableCell className="font-medium">{month.month}</TableCell>
 <TableCell>{month.bookings}</TableCell>
 <TableCell>{fmtCurrency(month.revenue)}</TableCell>
 <TableCell>{month.players}</TableCell>
 <TableCell>
 {bookingGrowth !== null && (
 <div className={`flex items-center gap-1 ${isPositiveGrowth ? 'text-green-600' : 'text-red-600'}`}>
 {isPositiveGrowth ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
 <span className="text-sm">{Math.abs(Number(bookingGrowth))}%</span>
 </div>
 )}
 </TableCell>
 </TableRow>
 );
 })}
 </TableBody>
 </Table>
 </div>
 </CardContent>
 </Card>
 </div>
 );
 };

 const handleRefundPolicyChange = async (policyId) => {
 if (!activeCourseId) {
 toast.error('No course selected');
 return;
 }
 
 try {
 await api.post(`/api/owner/courses/${activeCourseId}`, {
 refund_policy_id: policyId
 });
 
 setCourse(prev => ({ ...prev, refund_policy_id: policyId }));
 toast.success('Refund policy updated successfully');
 } catch (error) {
 console.error('Failed to update refund policy:', error);
 toast.error('Failed to update refund policy');
 }
 };

 const handleBookingFeePayerChange = async (newPayer) => {
 if (!activeCourseId) {
 toast.error('No course selected');
 return;
 }
 
 setBookingFeePayer(newPayer);
 
 try {
 await api.post(`/api/owner/courses/${activeCourseId}`, {
 booking_fee_payer: newPayer
 });
 
 setCourse(prev => ({ ...prev, booking_fee_payer: newPayer }));
 toast.success('Booking fee payer updated successfully');
 } catch (error) {
 console.error('Failed to update booking fee payer:', error);
 toast.error('Failed to update booking fee payer');
 setBookingFeePayer(course?.booking_fee_payer || 'player');
 }
 };

 const handleSaveBasicInfo = async () => {
 if (!activeCourseId) {
 toast.error('No course selected');
 return;
 }

 try {
 await api.post(`/api/owner/courses/${activeCourseId}`, {
 name: courseName,
 description: courseDescription,
 holes: parseInt(selectedHoles, 10)
 });

 setCourse(prev => ({
 ...prev,
 name: courseName,
 description: courseDescription,
 holes: parseInt(selectedHoles, 10)
 }));

 setOriginalCourseData(prev => prev ? {
 ...prev,
 name: courseName,
 description: courseDescription,
 holes: selectedHoles,
 } : prev);

 setIsEditingBasicInfo(false);
 toast.success('Course information saved successfully');
 } catch (error) {
 console.error('Failed to save course info:', error);
 toast.error('Failed to save course information');
 }
 };

 const handleCancelBasicInfo = () => {
 setCourseName(course?.name || '');
 setCourseDescription(course?.description || '');
 setSelectedHoles(course?.holes?.toString() || '18');
 setIsEditingBasicInfo(false);
 };

 const handleSaveLocation = async () => {
 if (!activeCourseId) {
 toast.error('No course selected');
 return;
 }

 try {
 await api.post(`/api/owner/courses/${activeCourseId}`, {
 address: courseAddress,
 latitude: courseLatitude,
 longitude: courseLongitude,
 timezone: courseTimezone
 });

 setCourse(prev => ({
 ...prev,
 address: courseAddress,
 latitude: courseLatitude,
 longitude: courseLongitude,
 timezone: courseTimezone
 }));

 setOriginalCourseData(prev => prev ? {
 ...prev,
 address: courseAddress,
 latitude: courseLatitude,
 longitude: courseLongitude,
 timezone: courseTimezone,
 } : prev);

 setIsEditingLocation(false);
 toast.success('Location information saved successfully');
 } catch (error) {
 console.error('Failed to save location info:', error);
 toast.error('Failed to save location information');
 }
 };

 const handleCancelLocation = () => {
 setCourseAddress(course?.address || '');
 setCourseLatitude(course?.latitude || null);
 setCourseLongitude(course?.longitude || null);
 setCourseTimezone(course?.timezone || 'America/New_York');
 setIsEditingLocation(false);
 };

 const handleSavePaymentPrefs = async () => {
 try {
 // In the future, this will save payment preferences to the database
 setOriginalPaymentPrefs({...paymentPrefs});
 setIsEditingPaymentPrefs(false);
 toast.success('Payment preferences saved successfully');
 } catch (error) {
 console.error('Failed to save payment preferences:', error);
 toast.error('Failed to save payment preferences');
 }
 };

 const handleCancelPaymentPrefs = () => {
 // Revert to original payment preferences
 if (originalPaymentPrefs) {
 setPaymentPrefs({...originalPaymentPrefs});
 }
 setIsEditingPaymentPrefs(false);
 };

 const getPolicyColor = (policyName) => {
 const nameLower = policyName.toLowerCase();
 if (nameLower.includes('flexible')) return 'green';
 if (nameLower.includes('standard')) return 'yellow';
 if (nameLower.includes('strict')) return 'red';
 return 'gray';
 };

 const renderPaymentsSettings = () => {
 const courseIdForLink = course?.id || COURSE_ID;
 const bookingLink = courseIdForLink ? `${DISCGO_SITE_URL}/book/${courseIdForLink}` : null;

 const handleCopyLink = () => {
   if (!bookingLink) return;
   navigator.clipboard.writeText(bookingLink).then(() => {
     setBookingLinkCopied(true);
     setTimeout(() => setBookingLinkCopied(false), 2000);
   });
 };

 return (
 <>
 <div className="space-y-6">
 {/* Earnings & Payouts — full width */}
 <Card>
 <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
 <div>
 <CardTitle>Earnings &amp; Payouts</CardTitle>
 <CardDescription>Your confirmed bookings and transfer history</CardDescription>
 </div>
 {connectStatus === 'complete' && (
 <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-medium flex-shrink-0 border border-green-700">
 <CheckCircle className="h-3.5 w-3.5" />
 Payout account connected
 </div>
 )}
 </CardHeader>
 <CardContent className="pt-0 space-y-5">
 {/* STATE 1 — not started */}
 {connectStatus === 'not_started' && (
 <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 mb-2">
 <div className="flex items-start gap-3">
 <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
 <div className="flex-1 min-w-0">
 <p className="text-sm font-semibold text-yellow-900">Set up your payouts</p>
 <p className="text-sm text-yellow-800 mt-1">Connect your bank account to receive earnings from your bookings. You won't receive any transfers until this is complete.</p>
 </div>
 <Button size="sm" className={brandStyles.primary} disabled={connectLoading} onClick={startConnectFlow}>
 {connectLoading ? 'Redirecting…' : 'Connect bank account'}
 </Button>
 </div>
 </div>
 )}
 {/* STATE 2 — incomplete */}
 {connectStatus === 'incomplete' && (
 <div className="rounded-lg border border-orange-300 bg-orange-50 p-4 mb-2">
 <div className="flex items-start gap-3">
 <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
 <div className="flex-1 min-w-0">
 <p className="text-sm font-semibold text-orange-900">Finish setting up payouts</p>
 <p className="text-sm text-orange-800 mt-1">Your payout setup isn't complete. Finish connecting your bank account to start receiving transfers.</p>
 </div>
 <Button size="sm" className={brandStyles.primary} disabled={connectLoading} onClick={startConnectFlow}>
 {connectLoading ? 'Redirecting…' : 'Continue setup'}
 </Button>
 </div>
 </div>
 )}
 {/* STATE 3 — payout to bank failed */}
 {earningsData?.payoutFailed && connectStatus === 'complete' && (
 <div className="rounded-lg border border-red-300 bg-red-50 p-4 mb-2">
 <div className="flex items-start gap-3">
 <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
 <div className="flex-1 min-w-0">
 <p className="text-sm font-semibold text-red-900">Your last payout failed</p>
 <p className="text-sm text-red-800 mt-1">Please update your bank details in Stripe to resume payouts.</p>
 </div>
 <Button size="sm" className={brandStyles.primary} disabled={connectLoading} onClick={startConnectFlow}>
 {connectLoading ? 'Redirecting…' : 'Update bank details'}
 </Button>
 </div>
 </div>
 )}
 {/* Summary figures */}
 <div className="flex gap-3">
 <div className="flex-1 p-3 rounded-lg border bg-muted/30">
 <p className="text-xs text-muted-foreground mb-1">Pending Earnings</p>
 <p className="text-xl font-semibold text-[#2B8B3B]">
 {earningsLoading ? '—' : `$${(earningsData?.pendingEarnings ?? 0).toFixed(2)}`}
 </p>
 </div>
 <div className="flex-1 p-3 rounded-lg border bg-muted/30">
 <p className="text-xs text-muted-foreground mb-1">Transferred This Month</p>
 <p className="text-xl font-semibold text-gray-800">
 {earningsLoading ? '—' : `$${(earningsData?.transferredThisMonth ?? 0).toFixed(2)}`}
 </p>
 </div>
 <div className="flex-1 p-3 rounded-lg border bg-muted/30">
 <p className="text-xs text-muted-foreground mb-1">Last Transfer</p>
 <p className="text-xl font-semibold text-gray-800">
 {earningsLoading ? '—' : earningsData?.lastTransferDate
   ? new Date(earningsData.lastTransferDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
   : 'None yet'}
 </p>
 </div>
 <div className="flex-1 p-3 rounded-lg border bg-muted/30">
 <p className="text-xs text-muted-foreground mb-1"><span className="font-bold text-[#2B8B3B]">discgo</span> Distro Fund</p>
 <div className="flex items-center justify-between">
  <p className="text-xl font-semibold text-[#2B8B3B]">
  {earningsLoading ? '—' : `$${(distroSummary?.anticipated_dividend_per_course ?? 0).toFixed(2)}`}
  </p>
  <img src={basketIcon} alt="Basket" className="w-8 h-8" style={{ filter: 'brightness(0) saturate(100%) invert(37%) sepia(32%) saturate(1288%) hue-rotate(90deg) brightness(95%) contrast(85%)' }} />
 </div>
 <p className="text-xs text-muted-foreground mt-0.5">Est. year-end dividend</p>
 </div>
 </div>

 {/* History table */}
 <div className="mt-2">
 {earningsLoading ? (
 <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
 ) : !earningsData || earningsData.history.length === 0 ? (
 <p className="text-sm text-muted-foreground text-center py-4">No confirmed bookings yet.</p>
 ) : (
 <div>
 <div className="overflow-x-auto rounded-md border">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b bg-muted/40">
 <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
 <th className="text-left px-3 py-2 font-medium text-muted-foreground">Booking</th>
 <th className="text-left px-3 py-2 font-medium text-muted-foreground">Tee Time</th>
 <th className="text-right px-3 py-2 font-medium text-muted-foreground">Amount</th>
 <th className="text-center px-3 py-2 font-medium text-muted-foreground">Status</th>
 </tr>
 </thead>
 <tbody>
 {earningsData.history
   .slice(earningsPage * EARNINGS_PAGE_SIZE, (earningsPage + 1) * EARNINGS_PAGE_SIZE)
   .map((row) => {
     const d = new Date(row.slotStart);
     const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
     const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
     const wasRefunded = row.bookingStatus === 'refunded' || (row.bookingStatus === 'cancelled' && row.refundedAt);
     const wasCancelledNoRefund = row.bookingStatus === 'cancelled' && !row.refundedAt;
     const badgeClass = wasRefunded ? 'bg-gray-100 text-gray-600 border-gray-300'
       : wasCancelledNoRefund ? 'bg-red-100 text-red-700 border-red-300'
       : row.transferStatus === 'transferred' ? 'bg-green-100 text-green-800 border-green-300'
       : row.transferStatus === 'failed' ? 'bg-red-100 text-red-700 border-red-300'
       : 'bg-orange-50 text-orange-700 border-orange-300';
     const badgeLabel = wasRefunded ? 'Refunded'
       : wasCancelledNoRefund ? 'Cancelled'
       : row.transferStatus === 'transferred' ? 'Transferred'
       : row.transferStatus === 'failed' ? 'Failed'
       : 'Pending';
     return (
       <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
         <td className="px-3 py-2 text-muted-foreground">{dateStr}</td>
         <td className="px-3 py-2 font-medium">{row.playerName}</td>
         <td className="px-3 py-2 text-muted-foreground">{timeStr}</td>
         <td className={`px-3 py-2 text-right font-medium ${wasRefunded ? 'text-muted-foreground line-through' : ''}`}>${row.ownerAmount.toFixed(2)}</td>
         <td className="px-3 py-2 text-center">
           <Badge variant="outline" className={badgeClass}>
             {wasRefunded && <RotateCcw className="h-3 w-3 mr-1" />}
             {wasCancelledNoRefund && <X className="h-3 w-3 mr-1" />}
             {!wasRefunded && !wasCancelledNoRefund && row.transferStatus === 'transferred' && <CheckCircle className="h-3 w-3 mr-1" />}
             {badgeLabel}
           </Badge>
         </td>
       </tr>
     );
   })}
 </tbody>
 </table>
 </div>
 {earningsData.history.length > EARNINGS_PAGE_SIZE && (
 <div className="flex items-center justify-between mt-3">
 <p className="text-xs text-muted-foreground">
 Showing {earningsPage * EARNINGS_PAGE_SIZE + 1}–{Math.min((earningsPage + 1) * EARNINGS_PAGE_SIZE, earningsData.history.length)} of {earningsData.history.length}
 </p>
 <div className="flex gap-2">
 <Button variant="outline" size="sm" disabled={earningsPage === 0} onClick={() => setEarningsPage(p => p - 1)}>
 <ChevronLeft className="h-4 w-4" />
 </Button>
 <Button variant="outline" size="sm" disabled={(earningsPage + 1) * EARNINGS_PAGE_SIZE >= earningsData.history.length} onClick={() => setEarningsPage(p => p + 1)}>
 <ChevronRight className="h-4 w-4" />
 </Button>
 </div>
 </div>
 )}
 </div>
 )}
 </div>

 {/* Payout History */}
 {earningsData && earningsData.payoutHistory.length > 0 && (
 <div className="mt-4">
 <h4 className="text-sm font-medium text-gray-800 mb-2">Bank Payout History</h4>
 <div className="overflow-x-auto rounded-md border">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b bg-muted/40">
 <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
 <th className="text-right px-3 py-2 font-medium text-muted-foreground">Amount</th>
 <th className="text-center px-3 py-2 font-medium text-muted-foreground">Status</th>
 </tr>
 </thead>
 <tbody>
 {earningsData.payoutHistory.map((p) => (
 <tr key={p.id} className="border-b last:border-0">
 <td className="px-3 py-2 text-muted-foreground">
 {p.arrivalDate
   ? new Date(p.arrivalDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
   : '—'}
 </td>
 <td className="px-3 py-2 text-right font-medium">${(p.amount / 100).toFixed(2)}</td>
 <td className="px-3 py-2 text-center">
 {p.status === 'paid' ? (
 <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">Paid</span>
 ) : p.status === 'failed' ? (
 <div>
 <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-300">Failed</span>
 {p.failureMessage && <p className="text-xs text-red-600 mt-0.5">{p.failureMessage}</p>}
 </div>
 ) : (
 <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-300">Pending</span>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 )}

 {/* How Payouts Work */}
 <div className="mt-6 p-3 bg-gray-50 border border-gray-200 rounded-md">
 <h4 className="text-sm font-medium text-gray-800 mb-2">How Payouts Work</h4>
 <ul className="text-xs text-gray-600 space-y-1">
 <li>• Once a tee time passes, your earnings are automatically transferred to your connected Stripe account</li>
 <li>• Transfers typically arrive in your bank account within 2 business days</li>
 <li>• Cancellations are paid out or refunded based on your refund policy</li>
 </ul>
 </div>
 </CardContent>
 </Card>

 {/* Refund Policy Configuration */}
 <Card>
 <CardHeader>
 <CardTitle>Automated Refund Policy</CardTitle>
 <CardDescription>
 Choose your refund policy template. This will be clearly displayed to players during booking and automatically enforced by our system.
 </CardDescription>
 </CardHeader>
 <CardContent className="space-y-6">
 <div className="space-y-4">
 <Label>Select Your Refund Policy</Label>
 <div className="space-y-3">
 {refundPolicies.map((policy) => {
 const color = getPolicyColor(policy.name);
 const isSelected = course?.refund_policy_id === policy.id;
 
 const colorClasses = {
 green: {
 bg: 'bg-green-50',
 border: 'border-green-200',
 hoverBorder: 'hover:border-green-400',
 checkedBorder: 'has-[:checked]:border-green-500',
 checkedRing: 'has-[:checked]:ring-green-200',
 inputText: 'text-green-600',
 inputBorder: 'border-green-400',
 inputFocus: 'focus:ring-green-500',
 titleText: 'text-green-800',
 descText: 'text-green-700'
 },
 yellow: {
 bg: 'bg-yellow-50',
 border: 'border-yellow-200',
 hoverBorder: 'hover:border-yellow-400',
 checkedBorder: 'has-[:checked]:border-yellow-500',
 checkedRing: 'has-[:checked]:ring-yellow-200',
 inputText: 'text-yellow-600',
 inputBorder: 'border-yellow-400',
 inputFocus: 'focus:ring-yellow-500',
 titleText: 'text-yellow-800',
 descText: 'text-yellow-700'
 },
 red: {
 bg: 'bg-red-50',
 border: 'border-red-200',
 hoverBorder: 'hover:border-red-400',
 checkedBorder: 'has-[:checked]:border-red-500',
 checkedRing: 'has-[:checked]:ring-red-200',
 inputText: 'text-red-600',
 inputBorder: 'border-red-400',
 inputFocus: 'focus:ring-red-500',
 titleText: 'text-red-800',
 descText: 'text-red-700'
 },
 gray: {
 bg: 'bg-gray-50',
 border: 'border-gray-200',
 hoverBorder: 'hover:border-gray-400',
 checkedBorder: 'has-[:checked]:border-gray-500',
 checkedRing: 'has-[:checked]:ring-gray-200',
 inputText: 'text-gray-600',
 inputBorder: 'border-gray-400',
 inputFocus: 'focus:ring-gray-500',
 titleText: 'text-gray-800',
 descText: 'text-gray-700'
 }
 };
 
 const classes = colorClasses[color];
 
 return (
 <label key={policy.id} htmlFor={policy.id} className="block">
 <div className={`border-2 rounded-lg p-4 cursor-pointer transition-colors has-[:checked]:ring-2 ${classes.bg} ${classes.border} ${classes.hoverBorder} ${classes.checkedBorder} ${classes.checkedRing}`}>
 <div className="flex items-start space-x-4">
 <input 
 type="radio" 
 name="refundPolicy" 
 value={policy.id} 
 checked={isSelected}
 onChange={() => handleRefundPolicyChange(policy.id)}
 className={`w-5 h-5 mt-0.5 border-2 ${classes.inputText} ${classes.inputBorder} ${classes.inputFocus}`}
 id={policy.id}
 />
 <div className="flex-1">
 <div className={`font-medium ${classes.titleText}`}>
 {policy.name}
 </div>
 <p className={`text-sm mt-1 ${classes.descText}`}>
 {policy.window_hours === 0
 ? 'Full refund for any cancellation made before the tee time.'
 : `Full refund if cancelled ${policy.window_hours}+ hours before the tee time. No refund within ${policy.window_hours} hours.`
 }
 </p>
 </div>
 </div>
 </div>
 </label>
 );
 })}
 </div>
 </div>

 <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
 <strong>Note:</strong> Changing your policy only affects <em>future</em> bookings. Existing bookings are locked to the policy that was in effect at the time of booking.
 </div>

 <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
 <h4 className="text-sm font-medium text-gray-800 mb-2">How Automated Refunds Work</h4>
 <ul className="text-xs text-gray-600 space-y-1">
 <li>• All cancellations are either a <strong>full refund or no refund</strong> — no partial refunds</li>
 <li>• The cutoff is calculated from the tee time, not the booking date</li>
 <li>• No-shows (player doesn't cancel and doesn't show up) receive no refund</li>
 <li>• Policy is clearly displayed and agreed to during player checkout</li>
 <li>• Eligible refunds are processed automatically through Stripe</li>
 </ul>
 </div>
 </CardContent>
 </Card>

 {/* Payment Preferences — REMOVED, replaced by Stripe */}
 {false && <Card>
 <CardHeader>
 <div className="flex items-start justify-between">
 <div>
 <CardTitle>Payment Preferences</CardTitle>
 <CardDescription>Configure how you want to receive payments from bookings</CardDescription>
 </div>
 {!isEditingPaymentPrefs ? (
 <Button 
 onClick={() => {
 setOriginalPaymentPrefs({...paymentPrefs});
 setIsEditingPaymentPrefs(true);
 }} 
 size="sm"
 variant="outline"
 >
 <Edit className="h-4 w-4 mr-1" />
 Edit
 </Button>
 ) : (
 <div className="flex gap-2">
 <Button 
 onClick={handleCancelPaymentPrefs} 
 size="sm"
 variant="outline"
 >
 Cancel
 </Button>
 <Button 
 onClick={handleSavePaymentPrefs} 
 size="sm"
 className={brandStyles.primary}
 >
 <Save className="h-4 w-4 mr-1" />
 Save
 </Button>
 </div>
 )}
 </div>
 </CardHeader>
 <CardContent className="space-y-6">
 <div className="space-y-4">
 <div className="space-y-2">
 <Label>Payment Method</Label>
 <Select 
 value={paymentPrefs.paymentMethod} 
 onValueChange={(value) => setPaymentPrefs({...paymentPrefs, paymentMethod: value})}
 disabled={!isEditingPaymentPrefs}
 >
 <SelectTrigger className={!isEditingPaymentPrefs ? 'bg-gray-50 cursor-not-allowed' : ''}>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="bank">Bank Account (ACH Transfer)</SelectItem>
 <SelectItem value="paypal">PayPal</SelectItem>
 <SelectItem value="stripe">Stripe Express</SelectItem>
 </SelectContent>
 </Select>
 </div>

 {/* Bank Account Fields */}
 {paymentPrefs.paymentMethod === 'bank' && (
 <>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label htmlFor="accountHolder">Account Holder Name</Label>
 <Input 
 id="accountHolder" 
 placeholder="Pine Valley Disc Golf LLC" 
 value={paymentPrefs.accountHolder}
 onChange={(e) => setPaymentPrefs({...paymentPrefs, accountHolder: e.target.value})}
 readOnly={!isEditingPaymentPrefs}
 className={!isEditingPaymentPrefs ? 'bg-gray-50 cursor-not-allowed' : ''}
 />
 </div>
 <div className="space-y-2">
 <Label htmlFor="bankName">Bank Name</Label>
 <Input 
 id="bankName" 
 placeholder="First National Bank" 
 value={paymentPrefs.bankName}
 onChange={(e) => setPaymentPrefs({...paymentPrefs, bankName: e.target.value})}
 readOnly={!isEditingPaymentPrefs}
 className={!isEditingPaymentPrefs ? 'bg-gray-50 cursor-not-allowed' : ''}
 />
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label htmlFor="routingNumber">Routing Number</Label>
 <Input 
 id="routingNumber" 
 placeholder="123456789" 
 value={paymentPrefs.routingNumber}
 onChange={(e) => setPaymentPrefs({...paymentPrefs, routingNumber: e.target.value})}
 readOnly={!isEditingPaymentPrefs}
 className={!isEditingPaymentPrefs ? 'bg-gray-50 cursor-not-allowed' : ''}
 />
 </div>
 <div className="space-y-2">
 <Label htmlFor="accountNumber">Account Number</Label>
 <Input 
 id="accountNumber" 
 placeholder="987654321" 
 value={paymentPrefs.accountNumber}
 onChange={(e) => setPaymentPrefs({...paymentPrefs, accountNumber: e.target.value})}
 type="password" 
 readOnly={!isEditingPaymentPrefs}
 className={!isEditingPaymentPrefs ? 'bg-gray-50 cursor-not-allowed' : ''}
 />
 </div>
 </div>

 <div className="space-y-2">
 <Label>Account Type</Label>
 <Select 
 value={paymentPrefs.accountType}
 onValueChange={(value) => setPaymentPrefs({...paymentPrefs, accountType: value})}
 disabled={!isEditingPaymentPrefs}
 >
 <SelectTrigger className={!isEditingPaymentPrefs ? 'bg-gray-50 cursor-not-allowed' : ''}>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="checking">Checking Account</SelectItem>
 <SelectItem value="savings">Savings Account</SelectItem>
 <SelectItem value="business">Business Account</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </>
 )}

 {/* PayPal Fields */}
 {paymentPrefs.paymentMethod === 'paypal' && (
 <div className="space-y-4">
 <div className="space-y-2">
 <Label htmlFor="paypalEmail">PayPal Email Address</Label>
 <Input 
 id="paypalEmail" 
 type="email" 
 placeholder="owner@pinevalleydg.com" 
 value={paymentPrefs.paypalEmail}
 onChange={(e) => setPaymentPrefs({...paymentPrefs, paypalEmail: e.target.value})}
 readOnly={!isEditingPaymentPrefs}
 className={!isEditingPaymentPrefs ? 'bg-gray-50 cursor-not-allowed' : ''}
 />
 <p className="text-xs text-muted-foreground">
 This email must be associated with your PayPal business account
 </p>
 </div>

 {isEditingPaymentPrefs && (
 <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
 <h4 className="font-medium text-blue-800 mb-2">Connect PayPal Account</h4>
 <p className="text-sm text-blue-700 mb-3">
 Connect your PayPal business account to receive payments directly
 </p>
 <Button variant="outline" className="bg-white hover:bg-blue-50 border-blue-300">
 Connect PayPal Account
 </Button>
 </div>
 )}
 </div>
 )}

 {/* Stripe Express Fields */}
 {paymentPrefs.paymentMethod === 'stripe' && (
 <div className="space-y-4">
 <div className="space-y-2">
 <Label htmlFor="businessName">Business Name</Label>
 <Input 
 id="businessName" 
 placeholder="Pine Valley Disc Golf LLC" 
 value={paymentPrefs.businessName}
 onChange={(e) => setPaymentPrefs({...paymentPrefs, businessName: e.target.value})}
 readOnly={!isEditingPaymentPrefs}
 className={!isEditingPaymentPrefs ? 'bg-gray-50 cursor-not-allowed' : ''}
 />
 </div>

 {isEditingPaymentPrefs && (
 <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
 <h4 className="font-medium text-purple-800 mb-2">Connect Stripe Express Account</h4>
 <p className="text-sm text-purple-700 mb-3">
 Stripe Express provides the fastest payouts with competitive rates. Complete your onboarding to start receiving payments.
 </p>
 <Button variant="outline" className="bg-white hover:bg-purple-50 border-purple-300">
 Setup Stripe Express
 </Button>
 </div>
 )}
 </div>
 )}


 </div>

 <Separator />

 <div className="space-y-4">
 <h4 className="font-medium">Booking Fee Payer</h4>
 <p className="text-sm text-muted-foreground">Choose who pays the 10% booking fee</p>
 
 <div className="space-y-3">
 <label className="block">
 <div className={`border-2 rounded-lg p-4 cursor-pointer transition-colors has-[:checked]:ring-2 ${bookingFeePayer === 'player' ? 'border-[#2B8B3B] bg-green-50 has-[:checked]:ring-green-200' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
 <div className="flex items-start space-x-4">
 <input 
 type="radio" 
 name="bookingFeePayer" 
 value="player" 
 checked={bookingFeePayer === 'player'}
 onChange={(e) => handleBookingFeePayerChange(e.target.value)}
 className="w-5 h-5 mt-0.5 text-[#2B8B3B] border-2 border-gray-400 focus:ring-[#2B8B3B]"
 />
 <div className="flex-1">
 <div className="font-medium text-gray-900">
 Player Pays
 </div>
 <p className="text-sm text-gray-600 mt-1">
 A 10% booking fee is added to the player's total at checkout. Your listed prices remain as shown, and you receive the full amount.
 </p>
 <p className="text-xs text-gray-500 mt-2">
 Example: $20 tee time → Player pays $22 → You receive $20
 </p>
 </div>
 </div>
 </div>
 </label>

 <label className="block">
 <div className={`border-2 rounded-lg p-4 cursor-pointer transition-colors has-[:checked]:ring-2 ${bookingFeePayer === 'owner' ? 'border-[#2B8B3B] bg-green-50 has-[:checked]:ring-green-200' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
 <div className="flex items-start space-x-4">
 <input 
 type="radio" 
 name="bookingFeePayer" 
 value="owner" 
 checked={bookingFeePayer === 'owner'}
 onChange={(e) => handleBookingFeePayerChange(e.target.value)}
 className="w-5 h-5 mt-0.5 text-[#2B8B3B] border-2 border-gray-400 focus:ring-[#2B8B3B]"
 />
 <div className="flex-1">
 <div className="font-medium text-gray-900">
 Course Pays
 </div>
 <p className="text-sm text-gray-600 mt-1">
 You absorb the 10% booking fee. Players see and pay your listed price, and the booking fee is deducted from your payout.
 </p>
 <p className="text-xs text-gray-500 mt-2">
 Example: $20 tee time → Player pays $20 → You receive $18
 </p>
 </div>
 </div>
 </div>
 </label>
 </div>
 </div>

 <Separator />

 <div className="space-y-4">
 <h4 className="font-medium">Processing Fees</h4>
 <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5F5DC' }}>
 <div className="space-y-2 text-sm">
 <div className="flex justify-between">
 <span>Credit Card Processing:</span>
 <span className="font-medium">2.9% + $0.30</span>
 </div>
 <div className="flex justify-between">
 <span>ACH Bank Transfer:</span>
 <span className="font-medium">0.8% (max $5.00)</span>
 </div>
 <div className="flex justify-between">
 <span>Payout Transfer Fee:</span>
 <span className="font-medium">Free</span>
 </div>
 <div className="flex justify-between">
 <span>Automated Refund Processing:</span>
 <span className="font-medium">Free</span>
 </div>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>}

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* Left column: Booking Fee Payer + Booking Link */}
 <div className="space-y-4">

 {/* Booking Fee Payer */}
 <Card>
 <CardHeader className="pb-3">
 <CardTitle className="text-base">Booking Fee Payer</CardTitle>
 <CardDescription>Choose who pays the 10% booking fee</CardDescription>
 </CardHeader>
 <CardContent className="pt-0 space-y-3">
 <label className="block">
 <div className={`border-2 rounded-lg p-4 cursor-pointer transition-colors has-[:checked]:ring-2 ${bookingFeePayer === 'player' ? 'border-[#2B8B3B] bg-green-50 has-[:checked]:ring-green-200' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
 <div className="flex items-start space-x-4">
 <input type="radio" name="bookingFeePayer" value="player" checked={bookingFeePayer === 'player'} onChange={(e) => handleBookingFeePayerChange(e.target.value)} className="w-5 h-5 mt-0.5 text-[#2B8B3B] border-2 border-gray-400 focus:ring-[#2B8B3B]" />
 <div className="flex-1">
 <div className="font-medium text-gray-900">Player Pays</div>
 <p className="text-sm text-gray-600 mt-1">Player absorbs the 10% booking fee. You receive your full listed price.</p>
 <p className="text-xs text-gray-500 mt-2">Example: $20 tee time → Player pays $22 → You receive $20</p>
 </div>
 </div>
 </div>
 </label>
 <label className="block">
 <div className={`border-2 rounded-lg p-4 cursor-pointer transition-colors has-[:checked]:ring-2 ${bookingFeePayer === 'owner' ? 'border-[#2B8B3B] bg-green-50 has-[:checked]:ring-green-200' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
 <div className="flex items-start space-x-4">
 <input type="radio" name="bookingFeePayer" value="owner" checked={bookingFeePayer === 'owner'} onChange={(e) => handleBookingFeePayerChange(e.target.value)} className="w-5 h-5 mt-0.5 text-[#2B8B3B] border-2 border-gray-400 focus:ring-[#2B8B3B]" />
 <div className="flex-1">
 <div className="font-medium text-gray-900">Course Pays</div>
 <p className="text-sm text-gray-600 mt-1">You absorb the 10% booking fee. Players see and pay your listed price.</p>
 <p className="text-xs text-gray-500 mt-2">Example: $20 tee time → Player pays $20 → You receive $18</p>
 </div>
 </div>
 </div>
 </label>
 </CardContent>
 </Card>

 {/* Booking Link */}
 <Card>
 <CardHeader className="pb-2">
 <CardTitle className="flex items-center gap-2 text-base">
   <Link className="h-4 w-4 text-[#2B8B3B]" />
   Booking Link
 </CardTitle>
 <CardDescription>
   Share this link so players can find and book your course directly.
 </CardDescription>
 </CardHeader>
 <CardContent className="pt-0 pb-4">
 {bookingLink ? (
   <div className="flex items-start gap-4">
     <div className="flex-shrink-0">
       <img
         src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(bookingLink)}`}
         alt="Booking link QR code"
         className="rounded border"
         width={80}
         height={80}
       />
       <p className="text-xs text-muted-foreground mt-1 text-center">right-click to save</p>
     </div>
     <div className="flex-1 flex flex-col gap-2 justify-center">
       <div className="px-3 py-2 bg-muted rounded-md text-xs font-mono text-muted-foreground break-all border leading-relaxed">
         {bookingLink}
       </div>
       <button
         onClick={handleCopyLink}
         className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${bookingLinkCopied ? 'bg-green-100 text-green-700' : 'bg-[#2B8B3B] text-white hover:bg-[#236B2F]'}`}
       >
         {bookingLinkCopied ? (
           <><CheckCircle className="h-4 w-4" /> Copied!</>
         ) : (
           <><Copy className="h-4 w-4" /> Copy Link</>
         )}
       </button>
     </div>
   </div>
 ) : (
   <p className="text-sm text-muted-foreground">Booking link unavailable — course not yet loaded.</p>
 )}
 </CardContent>
 </Card>
 </div>

 {/* Settings */}
 <Card>
 <CardHeader>
 <CardTitle>Course Settings</CardTitle>
 <CardDescription>Manage your contact information</CardDescription>
 </CardHeader>
 <CardContent className="space-y-6">
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="font-medium">New Booking Emails</p>
 <p className="text-sm text-muted-foreground">Receive an email when a player books a tee time</p>
 </div>
 <Switch
   checked={notifEmailBookings}
   onCheckedChange={async (val) => {
     setNotifEmailBookings(val);
     if (activeCourseId) {
       try {
         await api.post(`/api/owner/courses/${activeCourseId}`, { notif_email_bookings: val });
         toast.success('Preference saved');
       } catch { toast.error('Failed to save preference'); }
     }
   }}
   disabled={!activeCourseId}
 />
 </div>

 <div className="flex items-center justify-between opacity-50 select-none">
 <div>
 <p className="font-medium text-muted-foreground">SMS Notifications</p>
 <p className="text-sm text-muted-foreground">Get text alerts for bookings — coming soon</p>
 </div>
 <Switch disabled />
 </div>

 <div className="flex items-center justify-between">
 <div>
 <p className="font-medium">Cancellation Emails</p>
 <p className="text-sm text-muted-foreground">Receive an email when a player cancels their booking</p>
 </div>
 <Switch
   checked={notifEmailCancellations}
   onCheckedChange={async (val) => {
     setNotifEmailCancellations(val);
     if (activeCourseId) {
       try {
         await api.post(`/api/owner/courses/${activeCourseId}`, { notif_email_cancellations: val });
         toast.success('Preference saved');
       } catch { toast.error('Failed to save preference'); }
     }
   }}
   disabled={!activeCourseId}
 />
 </div>
 </div>

 <Separator />

 <div className="space-y-2">
 <Label htmlFor="contactEmail">Contact Email</Label>
 <Input
   id="contactEmail"
   type="email"
   value={contactEmail}
   onChange={e => setContactEmail(e.target.value)}
   onBlur={async () => {
     if (!activeCourseId || contactEmail === savedContactEmail.current) return;
     try {
       await api.post(`/api/owner/courses/${activeCourseId}`, { contact_email: contactEmail || null });
       savedContactEmail.current = contactEmail;
       toast.success('Contact email saved');
     } catch { toast.error('Failed to save contact email'); }
   }}
   placeholder="contact@yourcourse.com"
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="contactPhone">Contact Phone</Label>
 <Input
   id="contactPhone"
   type="tel"
   value={contactPhone}
   onChange={e => setContactPhone(e.target.value)}
   onBlur={async () => {
     if (!activeCourseId || contactPhone === savedContactPhone.current) return;
     try {
       await api.post(`/api/owner/courses/${activeCourseId}`, { contact_phone: contactPhone || null });
       savedContactPhone.current = contactPhone;
       toast.success('Contact phone saved');
     } catch { toast.error('Failed to save contact phone'); }
   }}
   placeholder="(555) 555-5555"
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="courseWebsite">Course Website</Label>
 <Input
   id="courseWebsite"
   type="url"
   value={courseWebsite}
   onChange={e => setCourseWebsite(e.target.value)}
   onBlur={async () => {
     if (!activeCourseId || courseWebsite === savedCourseWebsite.current) return;
     try {
       await api.post(`/api/owner/courses/${activeCourseId}`, { website_url: courseWebsite || null });
       savedCourseWebsite.current = courseWebsite;
       toast.success('Website saved');
     } catch { toast.error('Failed to save website'); }
   }}
   placeholder="https://your-course.com"
 />
 <p className="text-xs text-muted-foreground">Optional link to your course website</p>
 </div>
 </CardContent>
 </Card>
 </div>
 </div>

 </>
 );
 };

 // Tee Time Configuration Form Component
 const TeeTimeConfigForm = ({ 
 config, 
 onSubmit, 
 onCancel 
 }: { 
 config: TeeTimeConfig | null; 
 onSubmit: (config: TeeTimeConfig) => boolean; 
 onCancel: () => void; 
 }) => {
 const [formData, setFormData] = useState<TeeTimeConfig>({
 id: config?.id || '',
 name: config?.name || '',
 maxPlayers: config?.maxPlayers || 6,
 teeTimeInterval: config?.teeTimeInterval || 30,
 bookingType: config?.bookingType || 'auto',
 startTime: config?.startTime || '09:00',
 endTime: config?.endTime || '17:00',
 daysOfWeek: config?.daysOfWeek || [],
 pricingType: config?.pricingType || 'per-player',
 price: config?.price || 20
 });

 const handleDayToggle = (day: string) => {
 setFormData(prev => ({
 ...prev,
 daysOfWeek: prev.daysOfWeek.includes(day)
 ? prev.daysOfWeek.filter(d => d !== day)
 : [...prev.daysOfWeek, day]
 }));
 };

 const handleSubmit = (e: React.FormEvent) => {
 e.preventDefault();
 if (formData.daysOfWeek.length === 0) {
 return;
 }
 const success = onSubmit(formData);
 if (!success) {
 // Form will show errors, don't close
 }
 };

 return (
 <form onSubmit={handleSubmit} className="space-y-6">
 <div className="space-y-2">
 <Label htmlFor="configName">Configuration Name</Label>
 <Input
 id="configName"
 value={formData.name}
 onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
 placeholder="e.g., Morning Sessions, Weekend Premium"
 required
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label htmlFor="startTime">Start Time</Label>
 <Input
 id="startTime"
 type="time"
 value={formData.startTime}
 onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
 required
 />
 </div>
 <div className="space-y-2">
 <Label htmlFor="endTime">End Time</Label>
 <Input
 id="endTime"
 type="time"
 value={formData.endTime}
 onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
 required
 />
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div className="space-y-2">
 <Label htmlFor="maxPlayersConfig">Max Players per Slot</Label>
 <Select
 value={formData.maxPlayers.toString()}
 onValueChange={(value) => setFormData(prev => ({ ...prev, maxPlayers: parseInt(value) }))}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="2">2 Players</SelectItem>
 <SelectItem value="4">4 Players</SelectItem>
 <SelectItem value="6">6 Players</SelectItem>
 <SelectItem value="8">8 Players</SelectItem>
 <SelectItem value="12">12 Players</SelectItem>
 <SelectItem value="16">16 Players</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-2">
 <Label htmlFor="teeTimeIntervalConfig">Tee Time Interval</Label>
 <Select
 value={formData.teeTimeInterval.toString()}
 onValueChange={(value) => setFormData(prev => ({ ...prev, teeTimeInterval: parseInt(value) }))}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="15">15 Minutes</SelectItem>
 <SelectItem value="30">30 Minutes</SelectItem>
 <SelectItem value="60">60 Minutes</SelectItem>
 <SelectItem value="120">120 Minutes</SelectItem>
 </SelectContent>
 </Select>
 <p className="text-xs text-muted-foreground">Time between each group's tee time</p>
 </div>
 <div className="space-y-2">
 <Label>Booking Type</Label>
 <Select
 value={formData.bookingType}
 onValueChange={(value: 'auto' | 'request') => setFormData(prev => ({ ...prev, bookingType: value }))}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="auto">Auto Booking</SelectItem>
 <SelectItem value="request">Request to Book</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label>Pricing Type</Label>
 <Select
 value={formData.pricingType}
 onValueChange={(value: 'per-player' | 'per-round') => setFormData(prev => ({ ...prev, pricingType: value }))}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="per-player">Per Player</SelectItem>
 <SelectItem value="per-round">Per Round</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-2">
 <Label htmlFor="price">Price ($)</Label>
 <Input
 id="price"
 type="number"
 min="0"
 step="0.01"
 value={formData.price}
 onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
 placeholder="20.00"
 required
 />
 </div>
 </div>

 <div className="space-y-3">
 <Label>Days of the Week</Label>
 <div className="flex flex-wrap gap-2">
 {weekDays.map((day) => (
 <button
 key={day}
 type="button"
 onClick={() => handleDayToggle(day)}
 className={`
 px-3 py-2 rounded-md text-sm font-medium transition-colors
 ${formData.daysOfWeek.includes(day)
 ? 'bg-[#2B8B3B] text-white'
 : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
 }
 `}
 >
 {day}
 </button>
 ))}
 </div>
 {formData.daysOfWeek.length === 0 && (
 <p className="text-sm text-red-600">Please select at least one day</p>
 )}
 </div>

 <div className="flex justify-end gap-3">
 <Button type="button" variant="outline" onClick={onCancel}>
 Cancel
 </Button>
 <Button 
 type="submit" 
 className={brandStyles.primary}
 disabled={formData.daysOfWeek.length === 0 || !formData.name.trim()}
 >
 {config ? 'Update Configuration' : 'Add Configuration'}
 </Button>
 </div>
 </form>
 );
 };

 const renderContent = () => {
 switch (activeSection) {
 case 'dashboard':
 return renderDashboard();
 case 'course':
 return renderCourseProfile();
 case 'availability':
 return renderTeeTimeSetup();
 case 'bookings':
 return renderBookingsManagement();
 case 'insights':
 return renderInsights();
 case 'payments':
 return renderPaymentsSettings();
 default:
 return renderDashboard();
 }
 };

 return (
 <AuthProvider>
 <CourseProvider>
 <AppContent
 sidebarCollapsed={sidebarCollapsed}
 setSidebarCollapsed={setSidebarCollapsed}
 activeSection={activeSection}
 handleNavigation={handleNavigation}
 navigationItems={navigationItems}
 renderContent={renderContent}
 loadCourseData={loadCourseData}
 setActiveCourseId={setActiveCourseId}
 getOwnerInitials={getOwnerInitials}
 />
 </CourseProvider>
 </AuthProvider>
 );
}

function AppContent({ sidebarCollapsed, setSidebarCollapsed, activeSection, handleNavigation, navigationItems, renderContent, loadCourseData, setActiveCourseId, getOwnerInitials }: any) {
 const { loading: authLoading, signOut, user, profileFirstName, profileLastName } = useAuth();
 const { courses, selectedCourseId, selectedCourse, loading: courseLoading, error: courseError, refreshCourses } = useCourse();
 const { theme } = useTheme();
 const currentTheme = themes.find((t) => t.id === theme) ?? themes[0];

 // Course status toggle
 const [statusDialogOpen, setStatusDialogOpen] = useState(false);
 const [themeModalOpen, setThemeModalOpen] = useState(false);
 const [statusLoading, setStatusLoading] = useState(false);
 const [statusError, setStatusError] = useState('');

 const handleToggleCourseStatus = async () => {
 if (!selectedCourse) return;
 const newStatus = selectedCourse.status === 'active' ? 'inactive' : 'active';
 setStatusLoading(true);
 setStatusError('');
 try {
 await api.post(`/api/owner/courses/${selectedCourse.id}`, { status: newStatus });
 await refreshCourses();
 setStatusDialogOpen(false);
 toast.success(newStatus === 'active' ? 'Course activated.' : 'Course deactivated.');
 } catch (err: any) {
 setStatusError(err.message || 'Failed to update course status. Please try again.');
 } finally {
 setStatusLoading(false);
 }
 };

 // Load course data and sync activeCourseId when selectedCourseId changes.
 // loadCourseData is intentionally omitted from deps — it's recreated every render
 // but its behavior only depends on stable state setters, so we only want to
 // re-run when selectedCourseId actually changes.
 const loadCourseDataRef = useRef(loadCourseData);
 loadCourseDataRef.current = loadCourseData;

 useEffect(() => {
 if (selectedCourseId) {
 loadCourseDataRef.current(selectedCourseId);
 setActiveCourseId(selectedCourseId);
 }
 }, [selectedCourseId, setActiveCourseId]);

 // Show branded loading screen while auth or courses are initializing
 if (authLoading || courseLoading) {
 return (
 <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5DC' }}>
 <div className="text-center">
 <DiscgoLogo style={{ height: '70px', width: '70px', display: 'block', margin: '0 auto 1rem' }} />
 <p className="text-gray-600">Loading...</p>
 </div>
 </div>
 );
 }

 // Redirect to login if unauthenticated (session expired or signed out)
 if (!user) {
 window.location.href = `${DISCGO_SITE_URL}/login`;
 return null;
 }

 // Courses failed to load — if session expired, redirect to login; otherwise show error
 if (user && !courseLoading && courseError) {
 if (courseError.includes('SESSION_EXPIRED') || courseError.includes('401')) {
   window.location.href = `${DISCGO_SITE_URL}/login`;
   return null;
 }
 return (
   <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5DC' }}>
     <div className="text-center max-w-sm">
       <DiscgoLogo style={{ height: '70px', width: '70px', display: 'block', margin: '0 auto 1rem' }} />
       <p className="text-gray-700 mb-4">Unable to load your course. Please refresh the page.</p>
       <button
         onClick={() => window.location.reload()}
         className="px-4 py-2 bg-[#2B8B3B] text-white rounded-md text-sm font-medium hover:bg-[#236b2e]"
       >
         Refresh
       </button>
     </div>
   </div>
 );
 }

 // Show course creation form only when authenticated owner genuinely has no courses
 if (user && !courseLoading && courses.length === 0 && !courseError) {
 return <CreateCourseForm onCreated={refreshCourses} />;
 }

 // Show course selector if multiple courses but none selected
 if (courses.length > 1 && !selectedCourseId) {
 return (
 <div className="flex h-screen items-center justify-center">
 <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-lg">
 <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
 <h2 className="text-xl font-semibold text-gray-900 mb-4">Select a Course</h2>
 <p className="text-gray-600 mb-6">
 You manage multiple courses. Please select one to continue.
 </p>
 <CourseSelector />
 </div>
 </div>
 );
 }

 return (
 <div className="flex bg-white" style={{ height: '100vh', overflow: 'hidden' }}>
 {/* Sidebar Navigation */}
 <aside
 className={`${sidebarCollapsed ? 'w-20' : 'w-72'} border-r border-gray-300 flex flex-col transition-all duration-300 shrink-0 relative`}
 style={{ backgroundColor: '#F5F5DC' }}
 >
 {/* Logo + Course Manager */}
 <div className="px-4 flex items-center border-b border-gray-300" style={{ height: '65px', flexShrink: 0 }}>
 <div className="h-[50px] w-[50px] flex-shrink-0 flex items-center justify-center">
 <DiscgoLogo style={{ height: '50px', width: '50px', flexShrink: 0 }} />
 </div>
 {!sidebarCollapsed && (
 <span className="ml-3 text-lg text-gray-900 whitespace-nowrap overflow-hidden transition-opacity duration-300">Course Manager</span>
 )}
 </div>
 {/* Collapse button — straddling the sidebar's right border, centered in header height */}
 <button
 onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
 className="w-6 h-6 bg-[#2B8B3B] hover:bg-[#236e2f] rounded-full flex items-center justify-center text-white shadow-md transition-colors"
 style={{ position: 'absolute', top: '32.5px', right: '-12px', transform: 'translateY(-50%)', zIndex: 20 }}
 aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
 >
 {sidebarCollapsed ? (
 <ChevronRight className="w-4 h-4" />
 ) : (
 <ChevronLeft className="w-4 h-4" />
 )}
 </button>

 {/* Navigation Items */}
 <nav className="flex-1 min-h-0 px-4 pt-3 pb-4 space-y-1 mt-3 overflow-y-auto">
 {navigationItems.map((item) => {
 const Icon = item.icon;
 const isActive = activeSection === item.id;
 
 return (
 <button
 key={item.id}
 onClick={() => handleNavigation(item.id)}
 className={`
 w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-colors
 ${isActive 
 ? 'bg-[#2B8B3B] text-white shadow-sm' 
 : 'text-[oklch(0.145_0_0)] hover:bg-[#e5dfc8]'
 }
 `}
 title={sidebarCollapsed ? item.name : undefined}
 >
 <Icon className="w-5 h-5 flex-shrink-0" />
 {!sidebarCollapsed && (
 <span className="truncate">{item.name}</span>
 )}
 </button>
 );
 })}
 </nav>

 </aside>

 {/* Main Content Area */}
 <main className="flex flex-col flex-1" style={{ minHeight: 0, overflow: 'hidden' }}>
 {/* Header */}
 <header className="bg-white border-b border-gray-300 flex items-center flex-shrink-0" style={{ height: '65px', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
 <div className="flex items-center justify-between w-full">
 <h1 className="text-2xl text-[#2B8B3B]">
 {activeSection === 'dashboard' && 'Dashboard Overview'}
 {activeSection === 'course' && 'Course Profile'}
 {activeSection === 'availability' && 'Tee Time Setup'}
 {activeSection === 'bookings' && 'Bookings Management'}
 {activeSection === 'insights' && 'Insights'}
 {activeSection === 'payments' && 'Payments & Settings'}
 </h1>
 <div className="flex items-center space-x-4">
 <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
 <span className="text-sm text-gray-600">{selectedCourse?.name || 'Loading...'}</span>
 {selectedCourse?.status && (
 <span style={{
 display: 'inline-flex', alignItems: 'center', gap: '5px',
 background: selectedCourse.status === 'active' ? '#dcfce7' : selectedCourse.status === 'draft' ? '#fef9c3' : '#f3f4f6',
 border: `1px solid ${selectedCourse.status === 'active' ? '#16a34a' : selectedCourse.status === 'draft' ? '#ca8a04' : '#d1d5db'}`,
 color: selectedCourse.status === 'active' ? '#15803d' : selectedCourse.status === 'draft' ? '#854d0e' : '#6b7280',
 fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: '9999px',
 whiteSpace: 'nowrap',
 }}>
 <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: selectedCourse.status === 'active' ? '#16a34a' : selectedCourse.status === 'draft' ? '#ca8a04' : '#9ca3af', display: 'inline-block', flexShrink: 0 }} />
 {selectedCourse.status === 'active' ? 'Active' : selectedCourse.status === 'draft' ? 'Pending Review' : 'Inactive'}
 </span>
 )}
 </div>
 <DropdownMenu>
 <DropdownMenuTrigger asChild>
 <div className="h-8 w-8 bg-[#2B8B3B] rounded-full flex items-center justify-center text-white text-sm font-medium cursor-pointer hover:bg-[#236B2F] transition-colors">
 {getOwnerInitials(user, profileFirstName, profileLastName)}
 </div>
 </DropdownMenuTrigger>
 <DropdownMenuContent align="end" className="w-52">
 {selectedCourse && selectedCourse.status !== 'draft' && (
 <>
 <DropdownMenuItem
 className="cursor-pointer"
 style={{ color: selectedCourse.status === 'active' ? '#d97706' : '#2B8B3B' }}
 onClick={() => { setStatusError(''); setStatusDialogOpen(true); }}
 >
 {selectedCourse.status === 'active' ? 'Deactivate Course' : 'Activate Course'}
 </DropdownMenuItem>
 <DropdownMenuSeparator />
 </>
 )}
 <DropdownMenuItem className="cursor-pointer" onClick={() => signOut()}>
 Logout
 </DropdownMenuItem>
 </DropdownMenuContent>
 </DropdownMenu>
 </div>
 </div>
 </header>

 {/* ThemeModal hidden pre-launch — revisit post-launch */}

 {/* Main Content */}
 <div className="flex-1 bg-white pt-6 pb-6" style={{ paddingLeft: '2rem', paddingRight: '2rem', overflowY: 'auto', minHeight: 0 }}>
 {renderContent()}
 </div>
 </main>
 {/* Course status toggle dialog */}
 <Dialog
 open={statusDialogOpen}
 onOpenChange={(open) => { if (!statusLoading) { setStatusDialogOpen(open); setStatusError(''); } }}
 >
 <DialogContent className="max-w-sm">
 <DialogHeader>
 <DialogTitle>
 {selectedCourse?.status === 'active' ? 'Deactivate Course' : 'Activate Course'}
 </DialogTitle>
 <DialogDescription>
 {selectedCourse?.status === 'active'
 ? `Deactivate ${selectedCourse?.name}? Your course will no longer be visible or accept new bookings.`
 : `Activate ${selectedCourse?.name}? Your course will become visible and open for bookings.`
 }
 </DialogDescription>
 </DialogHeader>

 {statusError && (
 <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
 <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
 <span>{statusError}</span>
 </div>
 )}

 <div className="flex justify-end gap-3 mt-2">
 <Button
 variant="outline"
 onClick={() => { setStatusDialogOpen(false); setStatusError(''); }}
 disabled={statusLoading}
 >
 Cancel
 </Button>
 <Button
 onClick={handleToggleCourseStatus}
 disabled={statusLoading}
 style={selectedCourse?.status === 'active'
 ? { backgroundColor: '#d97706', color: '#fff', border: 'none' }
 : undefined
 }
 className={selectedCourse?.status === 'active' ? undefined : brandStyles.primary}
 >
 {statusLoading
 ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2" />Updating…</>
 : selectedCourse?.status === 'active' ? 'Deactivate' : 'Activate'
 }
 </Button>
 </div>
 </DialogContent>
 </Dialog>

 <Toaster />
 </div>
 );
}

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois',
  'Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts',
  'Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota',
  'Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina',
  'South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington',
  'West Virginia','Wisconsin','Wyoming',
];
const CA_PROVINCES = [
  'Alberta','British Columbia','Manitoba','New Brunswick',
  'Newfoundland and Labrador','Nova Scotia','Ontario','Prince Edward Island',
  'Quebec','Saskatchewan','Northwest Territories','Nunavut','Yukon',
];
const COUNTRIES = [
  'United States','Canada','Australia','Austria','Belgium','Brazil','Chile',
  'Croatia','Czech Republic','Denmark','Estonia','Finland','France','Germany',
  'Hungary','Iceland','Ireland','Italy','Japan','Latvia','Lithuania','Mexico',
  'Netherlands','New Zealand','Norway','Poland','Portugal','Slovakia','Slovenia',
  'Spain','Sweden','Switzerland','United Kingdom','Other',
];

function CreateCourseForm({ onCreated }: { onCreated: () => Promise<void> }) {
 const [name, setName] = useState('');
 const [holes, setHoles] = useState('18');
 const [address, setAddress] = useState('');
 const [city, setCity] = useState('');
 const [state, setState] = useState('');
 const [country, setCountry] = useState('United States');
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');

 const handleSubmit = async (e: React.FormEvent) => {
   e.preventDefault();
   setLoading(true);
   setError('');
   try {
     const { data: { session } } = await supabase.auth.getSession();
     const accessToken = session?.access_token ?? null;
     if (!accessToken) {
       throw new Error('Not authenticated. Please sign in again.');
     }
     const res = await fetch('/api/owner/courses', {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
       },
       body: JSON.stringify({ name, holes, address, city, state, country }),
     });
     const body = await res.json();
     if (!res.ok) throw new Error(body.message || 'Failed to create course.');
     await onCreated();
   } catch (err: any) {
     setError(err.message || 'Something went wrong.');
   } finally {
     setLoading(false);
   }
 };

 const inputStyle: React.CSSProperties = {
   width: '100%', padding: '10px 16px', borderRadius: '8px',
   border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none',
   boxSizing: 'border-box',
 };

 const labelStyle: React.CSSProperties = {
   display: 'block', fontSize: '0.85rem', fontWeight: 600,
   color: '#374151', marginBottom: '4px',
 };

 return (
   <div style={{ minHeight: '100vh', background: '#F5F5DC', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
     <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '480px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
       <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
         <DiscgoLogo style={{ height: '72px', width: '72px', display: 'block', margin: '0 auto 0.75rem' }} />
         <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: 0 }}>Set up your course</h2>
         <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '4px' }}>Tell us about your course to get started.</p>
       </div>

       <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
         <div>
           <label style={labelStyle}>Course Name *</label>
           <input style={inputStyle} placeholder="Pineview Disc Golf" value={name} onChange={e => setName(e.target.value)} required />
         </div>
         <div>
           <label style={labelStyle}>Number of Holes *</label>
           <select style={inputStyle} value={holes} onChange={e => setHoles(e.target.value)} required>
             {[9,12,15,18,21,24,27,30,33,36].map(n => (
               <option key={n} value={n}>{n} Holes</option>
             ))}
           </select>
         </div>
         <div>
           <label style={labelStyle}>Street Address</label>
           <input style={inputStyle} placeholder="123 Disc Golf Lane" value={address} onChange={e => setAddress(e.target.value)} />
         </div>
         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
           <div>
             <label style={labelStyle}>City *</label>
             <input style={inputStyle} placeholder="Portland" value={city} onChange={e => setCity(e.target.value)} required />
           </div>
           <div>
             <label style={labelStyle}>State / Province *</label>
             <select style={inputStyle} value={state} onChange={e => setState(e.target.value)} required>
               <option value="">Select…</option>
               <optgroup label="US States">
                 {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
               </optgroup>
               <optgroup label="Canadian Provinces">
                 {CA_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
               </optgroup>
               <option value="Other">Other</option>
             </select>
           </div>
         </div>
         <div>
           <label style={labelStyle}>Country *</label>
           <select style={inputStyle} value={country} onChange={e => setCountry(e.target.value)} required>
             <option value="">Select…</option>
             {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
           </select>
         </div>

         {error && (
           <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: 0 }}>{error}</p>
         )}

         <button
           type="submit"
           disabled={loading}
           style={{ width: '100%', padding: '12px', background: '#2B8B3B', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, marginTop: '0.25rem' }}
         >
           {loading ? 'Creating…' : 'Create Course'}
         </button>
       </form>
     </div>
   </div>
 );
}